import { AbiCoder, Contract, MaxUint256, formatUnits, keccak256, parseUnits, toUtf8Bytes } from 'ethers';
import type { Address } from 'viem';

import {
  adapterAbi,
  adapterAbiEthers,
  courtOracleAbi,
  courtOracleAbiEthers,
  erc20Abi,
  erc20AbiEthers,
  events,
  factoryAbi,
  marketAbi,
  marketAbiEthers,
  reputationAbi,
} from '../shared/abi';
import { getMarketHistory, type MarketHistoryPoint } from '../shared/analytics';
import { readClient, readContractsBatch } from '../shared/client';
import { getCache, setCache } from '../shared/cache';
import {
  DEPLOYMENT,
  NETWORK_USDC,
  OUTCOME_LABEL,
  getActiveChainConfig,
  getActiveNetworkKey,
  setActiveNetwork,
  type NetworkKey,
} from '../shared/config';
import { fmtDuration, fmtPct, fmtToken, shortAddr } from '../shared/format';
import { logLine } from '../shared/logger';
import { escapeHtml, sanitizeTokenSymbol } from '../shared/sanitize';
import { openAccountModal } from '../shared/accountModal';
import {
  type CollateralMeta,
  defaultCollateralMeta,
  fmtCollateral,
  fmtCollateralUsd,
  resolveCollateralMeta,
  resolveTokenLogoUrl,
  resolveTokenLogoUrlSync,
} from '../shared/collateral';
import { normalizeReason, extractCustomErrorSelector, friendlyErrorMessage } from './errorHandling';
import { normalizeOutcomeLabel, outcomeSymbol, isZeroAddress, displayAddr, reputationLabel, fmtTs } from './helpers';
import { createAnalyticsBucketsService } from './analyticsBuckets';
import { renderBarHistorySvg, renderFlowSplitSvg, renderStackedFlowSvg } from './analyticsRenderers';
import {
  getBuyUsdcPulseXUrl as buildBuyUsdcPulseXUrl,
  showCopyFeedback,
  tokenLabelHtml as buildTokenLabelHtml,
} from './uiHelpers';
import {
  bindWalletEvents,
  connectWallet,
  contractWithSigner,
  ensurePulseChainConnected,
  rehydrateWalletSession,
  walletState,
} from '../shared/wallet';

const REFRESH_ICON_SVG = `<img src="${import.meta.env.BASE_URL}images/refreshing.svg" alt="" aria-hidden="true"/>`;
const MARKET_REFRESH_VISIBLE_MS = 20_000;
const MARKET_REFRESH_HIDDEN_MS = 90_000;
const ANALYTICS_REFRESH_VISIBLE_MS = 45_000;
const POST_TX_DELAYED_REFRESH_MS = 6_000;
const CREATOR_MARKETS_REFRESH_MS = 120_000;

function logTxDebug(action: string, error: any, extra: Record<string, unknown> = {}): void {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.shortMessage || error?.message || '').toLowerCase();
  const userRejected = code === 'ACTION_REJECTED' || message.includes('user rejected');
  const selector = extractCustomErrorSelector(error);
  const log = userRejected ? console.warn : console.error;
  log(`[BoxBet] ${action} ${userRejected ? 'cancelled by user' : 'failed'}`, {
    action,
    userRejected,
    selector,
    shortMessage: error?.shortMessage,
    message: error?.message,
    cause: error?.cause,
    data: error?.data,
    info: error?.info,
    details: error?.details,
    extra,
    rawError: error,
  });
}

export async function mountMarketView(appRoot: HTMLElement, marketAddress: Address): Promise<void> {
  type AnalyticsRange = '12h' | '24h' | '7d';
  const activeChain = getActiveChainConfig();
  const activeNetwork = getActiveNetworkKey();

  appRoot.innerHTML = `
    <main class="app" style="padding-bottom:180px; max-width:1180px;">
      <header class="topbar" style="margin-bottom:24px;">
        <div class="brand">
          <div class="brand-mark" aria-hidden="true"></div>
          <div>
            <div class="brand-text" style="font-size:1.1rem;">BoxBet</div>
            <div class="brand-sub">${activeChain.chainName}</div>
          </div>
        </div>
        <div class="header-actions">
          <a class="btn header-back-btn" href="./" aria-label="Back to markets" title="Back to markets">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
          <details class="header-dropdown" id="networkDropdown">
            <summary class="header-dropdown-btn" aria-label="Network selector">
              <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="12" cy="12" r="9" fill="none" stroke-width="2"/>
                <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" fill="none" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <span class="header-dropdown-caret" aria-hidden="true">▾</span>
            </summary>
            <div class="header-dropdown-menu" role="menu" aria-label="Network selector">
              <button id="networkMainnetBtn" class="header-dropdown-item ${activeNetwork === 'mainnet' ? 'active' : ''}" type="button" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2v20M2 12h20" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                  Mainnet
                </span>
              </button>
              <button id="networkTestnetBtn" class="header-dropdown-item ${activeNetwork === 'testnet' ? 'active' : ''}" type="button" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 6h16v12H4zM8 10h8" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Testnet
                </span>
              </button>
            </div>
          </details>
          <details class="header-dropdown" id="linksDropdown">
            <summary class="header-dropdown-btn" aria-label="Header links">
              <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" fill="none" stroke-width="2" stroke-linejoin="round"/>
                <path d="M4 7.5l8 4.5 8-4.5M12 12v9" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="header-dropdown-caret" aria-hidden="true">▾</span>
            </summary>
            <div class="header-dropdown-menu" role="menu" aria-label="Navigation links">
              <a href="/boxbet/help" class="header-dropdown-item" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 18h.01M9.1 9a3 3 0 1 1 5.8 1c-.57.78-1.55 1.23-2.18 2" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Help
                </span>
              </a>
              <a href="/boxbet/create/" class="header-dropdown-item" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                  Create Market
                </span>
              </a>
              <a href="/boxbet/adversary" class="header-dropdown-item" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l8 4v5c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V7l8-4z" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Adversary
                </span>
              </a>
            </div>
          </details>
          <button id="walletBtn" class="wallet-pill">Connect Wallet</button>
        </div>
      </header>

      <section class="card" style="padding:22px; margin-bottom:16px; display:grid; gap:12px;">
        <div style="display:flex; justify-content:space-between; gap:10px; align-items:flex-start; flex-wrap:wrap;">
          <div class="market-addr-actions-row">
            <div class="mono tiny" id="marketAddr" style="word-break:break-all;"></div>
            <button id="marketAddrCopyBtn" type="button" class="token-copy-btn market-addr-action-btn" aria-label="Copy market address" title="Copy market address">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="9" y="9" width="10" height="10" rx="1" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <rect x="5" y="5" width="10" height="10" rx="1" fill="none" stroke="currentColor" stroke-width="1.8"/>
              </svg>
            </button>
            <button id="marketLinkCopyBtn" type="button" class="token-copy-btn market-addr-action-btn" aria-label="Copy market link" title="Copy market link">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v17H6.5A2.5 2.5 0 0 1 4 17.5z" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v17h4.5a2.5 2.5 0 0 0 2.5-2.5z" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <path d="M11 6.5h2M11 10h2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <button id="finalizeBtn" class="btn btn-green finalize-cta" style="display:none;">Finalize</button>
        </div>
        <div id="marketQuestion" style="font-size:1.2rem; font-weight:700; line-height:1.35"></div>
        <div style="display:flex; flex-wrap:wrap; gap:8px; align-items:center;">
          <span class="meta-chip" id="marketStatus"></span>
          <span class="meta-chip" id="marketPhase">LIVE</span>
        </div>
        <div class="odds">
          <div class="odds-outcome">
            <div id="yesLabelOdds" class="market-token-pill">-</div>
            <div class="row"><div class="track"><div id="yesBar" class="fill yes" style="width:50%"></div></div><span id="yesPct">50.0%</span></div>
          </div>
          <div class="odds-outcome">
            <div id="noLabelOdds" class="market-token-pill">-</div>
            <div class="row"><div class="track"><div id="noBar" class="fill no" style="width:50%"></div></div><span id="noPct">50.0%</span></div>
          </div>
        </div>
        <div id="reserveLine" class="mono" style="font-size:.68rem; color:#ffffff"></div>
        <div id="collateralLine" class="mono" style="font-size:.68rem; color:var(--muted); display:flex; align-items:center; gap:6px;"></div>

      </section>

      <section style="display:grid; gap:12px;">
        <div class="market-view-switcher">
          <div id="primaryTabsContainer" class="market-tabs-container">
            <div class="tabs tabs-primary">
              <button type="button" id="claimsTabBtn" class="tab" style="display:none" data-tab="claimsPanel">Claim</button>
              <button type="button" class="tab analytics-icon-tab" data-tab="analyticsPanel" aria-label="Analytics">
                <span class="analytics-icon-stack" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false" style="width:28px; height:32px; display:block;">
                    <rect x="1" y="14" width="1" height="8" rx="0.5" fill="#ef4444"/>
                    <rect x="2" y="16" width="2" height="5" rx="0.7" fill="#ef4444"/>

                    <rect x="5" y="12" width="1" height="10" rx="0.5" fill="#ef4444"/>
                    <rect x="6" y="14" width="2" height="6" rx="0.7" fill="#ef4444"/>

                    <rect x="9" y="10" width="1" height="12" rx="0.5" fill="#22c55e"/>
                    <rect x="10" y="12" width="2" height="7" rx="0.7" fill="#22c55e"/>

                    <rect x="13" y="8" width="1" height="14" rx="0.5" fill="#22c55e"/>
                    <rect x="14" y="10" width="2" height="8" rx="0.7" fill="#22c55e"/>

                    <rect x="17" y="5" width="1" height="17" rx="0.5" fill="#22c55e"/>
                    <rect x="18" y="7" width="2" height="10" rx="0.7" fill="#22c55e"/>

                    <rect x="21" y="2" width="1" height="20" rx="0.5" fill="#22c55e"/>
                    <rect x="22" y="4" width="2" height="12" rx="0.7" fill="#22c55e"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>
          <div id="advancedTabsContainer" class="market-tabs-container" style="display:none;">
            <div class="tabs tabs-advanced">
              <button type="button" class="tab" data-tab="resolutionPanel">Resolution</button>
              <button type="button" id="creatorTabBtn" class="tab" data-tab="creatorPanel">Judge</button>
            </div>
          </div>
          <button id="extraControlsToggle" type="button" class="extra-controls-toggle" title="Show extra controls" aria-label="Show extra controls" aria-pressed="false">
            <img src="/boxbet/images/extra.svg" alt="Extra controls" class="extra-controls-icon" />
          </button>
        </div>

        <div id="tradePanel" class="panel active">
          <div class="trade-unified-card">
            <div id="tradePriceRefreshIndicator" class="tuc-price-refresh-indicator" aria-hidden="true" hidden></div>

            <div class="tuc-topbar">
              <button id="tradeRefreshBtn" type="button" class="btn icon-btn tuc-settings-btn" aria-label="Refresh market prices" title="Refresh market prices">
                ${REFRESH_ICON_SVG}
              </button>
              <button id="tradeSettingsBtn" type="button" class="token-copy-btn tuc-settings-btn" aria-label="Open trade settings" title="Trade settings">
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M19.14 12.94a7.89 7.89 0 0 0 .05-.94 7.89 7.89 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.23 7.23 0 0 0-1.63-.94L14.4 2.8a.5.5 0 0 0-.5-.4h-3.8a.5.5 0 0 0-.5.4L9.25 5.32c-.57.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.71 8.84a.5.5 0 0 0 .12.64l2.03 1.58c-.03.31-.05.62-.05.94 0 .32.02.63.05.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96c.51.4 1.06.71 1.63.94l.35 2.52a.5.5 0 0 0 .5.4h3.8a.5.5 0 0 0 .5-.4l.35-2.52c.57-.23 1.12-.54 1.63-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64zm-7.14 2.06a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" fill="currentColor"/></svg>
              </button>
            </div>

            <div class="tuc-balances">
              <div class="tuc-balance-item"><span id="yesBalanceLabel" class="mono tuc-bal-key">-</span><span id="yesBalanceTrade" class="tuc-bal-val">-</span></div>
              <div class="tuc-balance-item"><span id="noBalanceLabel" class="mono tuc-bal-key">-</span><span id="noBalanceTrade" class="tuc-bal-val">-</span></div>
            </div>

            <div class="tuc-divider"></div>

            <div class="tuc-field">
              <div class="tuc-field-label-row tuc-pay-header">
                <span id="payPositionLabel" class="mono tuc-field-label">From</span>

              </div>
              <div class="tuc-pay-selector-row">
                <div id="paySideDropdown" class="tuc-inline-asset-select">
                  <button id="paySideDropdownBtn" type="button" class="tuc-inline-asset-btn is-usdc-static" aria-haspopup="listbox" aria-expanded="false">
                    <span class="tuc-asset-logo"><img id="payAssetIcon" src="/boxbet/images/usdc.png" alt="$" class="tuc-asset-logo-img" /></span>
                    <span id="paySideDropdownValue" class="tuc-inline-asset-value"></span>
                    <span class="tuc-custom-select-caret" aria-hidden="true">▾</span>
                  </button>
                  <div id="paySideDropdownMenu" class="tuc-custom-select-menu" role="listbox" aria-label="Sell Position options" hidden>
                    <button id="paySideOptionYesBtn" type="button" class="tuc-custom-option is-yes" role="option" data-value="yes">-</button>
                    <button id="paySideOptionNoBtn" type="button" class="tuc-custom-option is-no" role="option" data-value="no">-</button>
                  </div>
                </div>
                <div id="payPercentDropdown" class="tuc-custom-select tuc-percent-dropdown">
                  <button id="payPercentDropdownBtn" type="button" class="tuc-custom-select-btn tuc-balance-dropdown tuc-percent-dropdown-btn" aria-haspopup="listbox" aria-expanded="false" aria-label="Use percent of balance">
                    <span id="payPercentDropdownValue" class="tuc-custom-select-value">Balance</span>
                    <span class="tuc-custom-select-caret" aria-hidden="true">▾</span>
                  </button>
                  <div id="payPercentDropdownMenu" class="tuc-custom-select-menu" role="listbox" aria-label="Use percent of balance" hidden>
                    <button id="payPercent25Btn" type="button" class="tuc-custom-option tuc-percent-option" role="option" data-value="25">25%</button>
                    <button id="payPercent50Btn" type="button" class="tuc-custom-option tuc-percent-option" role="option" data-value="50">50%</button>
                    <button id="payPercent75Btn" type="button" class="tuc-custom-option tuc-percent-option" role="option" data-value="75">75%</button>
                    <button id="payPercent100Btn" type="button" class="tuc-custom-option tuc-percent-option" role="option" data-value="100">MAX</button>
                  </div>
                </div>
                <select id="payPercentSelect" class="tuc-select-native" tabindex="-1" aria-hidden="true">
                  <option id="payBalanceOption" value="" hidden>Balance</option>
                  <option value="25">25%</option>
                  <option value="50">50%</option>
                  <option value="75">75%</option>
                  <option value="100">MAX</option>
                </select>
                <div id="buyUsdcActions" class="tuc-buy-actions" hidden>
                  <button id="buyUsdcCopyBtn" type="button" class="token-copy-btn" aria-label="Copy USDC address" title="Copy USDC address">⧉</button>
                  <button id="buyUsdcLinkBtn" type="button" class="token-copy-btn token-link-btn" aria-label="Open USDC on PulseX" title="Open USDC on PulseX">
                    <img src="/boxbet/images/link.svg" class="tuc-link-icon" alt="" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div class="tuc-input-row">
                <span id="amountInputPrefix" class="tuc-input-prefix" hidden>$</span>
                <input id="amountInput" class="tuc-input" placeholder="100" inputmode="decimal" autocomplete="off" spellcheck="false" />
              </div>
            </div>

            <div class="tuc-field">
              <div class="tuc-field-label-row tuc-receive-header-row">
                <span id="receivePositionLabel" class="mono tuc-field-label">TO WIN</span>
                <button id="tradeModeToggle" class="trade-mode-toggle is-buy" type="button" title="Switch buy and sell mode" aria-label="Switch buy and sell mode" aria-pressed="false">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M16 3l4 4-4 4M20 7H8a4 4 0 0 0-4 4M8 21l-4-4 4-4M4 17h12a4 4 0 0 0 4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
              <div class="tuc-receive-selector-row">
                <div id="sideDropdown" class="tuc-custom-select">
                  <button id="sideDropdownBtn" type="button" class="tuc-custom-select-btn" aria-haspopup="listbox" aria-expanded="false">
                    <span class="tuc-receive-select-main">
                      <span class="tuc-asset-logo tuc-receive-logo"><img id="receiveAssetIcon" src="/boxbet/images/win.svg" alt="Receive position" class="tuc-asset-logo-img is-out-asset-icon is-outcome-yes is-trend-leading" /></span>
                      <span id="sideDropdownValue" class="tuc-custom-select-value" style="display:none;"></span>
                      <span id="receiveSymbol" class="tuc-custom-select-value" style="display:none;"></span>
                    </span>
                    <span class="tuc-custom-select-caret" aria-hidden="true">▾</span>
                  </button>
                  <div id="sideDropdownMenu" class="tuc-custom-select-menu" role="listbox" aria-label="Receive Position options" hidden>
                    <button id="sideOptionYesBtn" type="button" class="tuc-custom-option is-yes" role="option" data-value="yes">-</button>
                    <button id="sideOptionNoBtn" type="button" class="tuc-custom-option is-no" role="option" data-value="no">-</button>
                  </div>
                </div>
                <div id="tradeImpactTrigger" class="trade-impact-trigger-wrap"></div>
              </div>
              <select id="sideSelect" class="tuc-select-native" tabindex="-1" aria-hidden="true">
                <option id="yesSideOption" class="yes-outcome-option" value="yes">-</option>
                <option id="noSideOption" class="no-outcome-option" value="no">-</option>
              </select>
              <div id="tradeExecutionInsights" class="tuc-insights">
                <div class="trade-preview-shell">
                  <div class="trade-preview-output">
                    <div class="trade-preview-output-value">
                      <span class="tuc-asset-logo"><img src="/boxbet/images/win.svg" alt="-" class="tuc-asset-logo-img is-out-asset-icon is-outcome-yes is-trend-leading" /></span>
                      <span>-</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="tuc-divider"></div>

            <div class="tuc-action-row">
              <button id="tradeActionBtn" class="btn btn-green trade-action-btn">Buy</button>
            </div>

            <div id="tradeImpactPanel" class="tuc-impact-bottom"></div>

            <div class="tuc-footer">
              <label class="soft-close-toggle" style="display:none;">
                <input id="tradeOverride" type="checkbox" />
                <span>Allow trading after resolve time (override soft-close)</span>
              </label>
              <div id="tradeGuard" class="mono tiny trade-guard-text" style="color:var(--muted);"></div>
            </div>

          </div>

          <div id="tradeSettingsModal" class="trade-settings-modal" hidden>
            <div class="trade-settings-sheet card" role="dialog" aria-modal="true" aria-labelledby="tradeSettingsTitle">
              <div class="trade-settings-header">
                <div id="tradeSettingsTitle" class="mono trade-settings-title">Trade Settings</div>
                <button id="tradeSettingsCloseBtn" type="button" class="btn icon-btn icon-btn-close" aria-label="Close" title="Close">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6l-12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
              <div class="trade-settings-body">
                <div class="mono trade-settings-label">Slippage Tolerance</div>
                <div class="trade-slippage-presets" id="tradeSlippagePresets">
                  <button type="button" class="btn trade-slippage-btn" data-bps="10">0.10%</button>
                  <button type="button" class="btn trade-slippage-btn" data-bps="50">0.50%</button>
                  <button type="button" class="btn trade-slippage-btn" data-bps="100">1.00%</button>
                  <button type="button" class="btn trade-slippage-btn" data-bps="300">3.00%</button>
                </div>
                <label class="trade-settings-custom" for="tradeSlippageCustomInput">
                  <span class="mono trade-settings-label">Custom %</span>
                  <input id="tradeSlippageCustomInput" class="btn trade-settings-input" type="number" min="0.01" max="50" step="0.01" placeholder="0.50" />
                </label>
                <label class="trade-expert-row" for="tradeExpertModeToggle">
                  <input id="tradeExpertModeToggle" type="checkbox" />
                  <span>Expert mode (allow trade when impact exceeds slippage)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div id="resolutionPanel" class="panel">
          <div class="card resolution-state-card" style="padding:10px; margin-bottom:10px;">
            <div class="mono" style="font-size:.66rem; color:var(--muted); margin-bottom:8px;">Resolution State</div>
            <div id="resolutionState" style="margin-bottom:8px;"></div>
            <div id="resolutionHelp" class="mono" style="font-size:.66rem; color:var(--muted);"></div>
          </div>
          <div id="resolutionDetails" class="card" style="padding:10px; margin-bottom:10px;"></div>

          <div class="card" style="padding:10px; margin-bottom:10px; display:grid; gap:8px;">
            <div class="mono" style="font-size:.66rem; color:var(--muted);">RESOLUTION BOND</div>
            <div class="resolution-metric-grid">
              <div class="resolution-metric-card">
                <div class="resolution-metric-label mono">Bond Asset</div>
                <div id="resolutionBondAsset" class="resolution-metric-value">-</div>
              </div>
              <div class="resolution-metric-card">
                <div class="resolution-metric-label mono">Bond Amount</div>
                <div id="resolutionBondAmount" class="resolution-metric-value">-</div>
              </div>
              <div class="mono" style="display:flex; flex-direction:column; gap:2px; padding-top:4px;">
                <div class="resolution-metric-label mono">Approval</div>
                <div id="resolutionBondAllowance" class="mono" style="font-size:.68rem; color:var(--text);">-</div>
              </div>
            </div>
            <div id="resolutionBondStatus" class="mono" style="font-size:.66rem; color:var(--muted);"></div>
          </div>

          <div id="resolutionRisk" class="card" style="padding:10px; margin-bottom:10px;"></div>

          <div class="resolution-action-grid">
            <div class="card resolution-action-card resolution-action-card-propose">
              <div class="resolution-action-head">
                <div class="mono resolution-action-kicker">PROPOSAL</div>
                <div class="resolution-action-title">Submit Outcome Proposal</div>
              </div>
              <div class="mono resolution-action-note">Choose the outcome slot to post as a proposal.</div>
              <div class="resolution-action-controls">
                <select id="outcomeSelect" class="btn resolution-action-select" disabled>
                  <option id="resolutionYesOption" value="1">-</option>
                  <option id="resolutionNoOption" value="2">-</option>
                  <option value="3">Invalid</option>
                </select>
                <button id="proposeBtn" class="btn btn-primary" disabled>Propose</button>
              </div>
            </div>

            <div class="card resolution-action-card resolution-action-card-dispute">
              <div class="resolution-action-head">
                <div class="mono resolution-action-kicker">DISPUTE</div>
                <div class="resolution-action-title">Challenge Accepted Outcome</div>
              </div>
              <div class="mono resolution-action-note">Choose the counter-outcome for the dispute transaction.</div>
              <div class="resolution-action-controls">
                <select id="disputeOutcomeSelect" class="btn resolution-action-select" disabled>
                  <option id="disputeYesOption" value="1">-</option>
                  <option id="disputeNoOption" value="2">-</option>
                  <option value="3">Invalid</option>
                </select>
                <button id="disputeBtn" class="btn btn-red" disabled>Dispute</button>
                <button id="emergencyBtn" class="btn btn-amber" style="display:none;" aria-hidden="true">Emergency</button>
              </div>
            </div>
          </div>
        </div>

        <div id="creatorPanel" class="panel">
          <div class="card" style="padding:10px; margin-bottom:10px; display:grid; gap:8px;">
            <div class="mono" style="font-size:.66rem; color:var(--muted);">MARKET JUDGE CONTROLS</div>
            <div id="creatorStatus" class="mono" style="font-size:.68rem; line-height:1.5;">Loading...</div>
            <div id="creatorActionsHelp" class="mono" style="font-size:.66rem; color:var(--muted);"></div>
          </div>

          <div class="card" style="padding:10px; margin-bottom:10px; display:grid; gap:8px;">
            <div class="mono" style="font-size:.66rem; color:var(--muted);">PENDING PROPOSAL</div>

            <div id="creatorProposalBlock" class="creator-proposal-block" hidden>
              <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:8px;">
                <span id="creatorPendingOutcomePill" class="meta-chip"></span>
                <span id="creatorPendingProposer" class="mono" style="font-size:.66rem; color:var(--muted);"></span>
              </div>
              <div id="creatorPendingReason" class="creator-proposal-reason mono" hidden></div>
              <div class="creator-pending-slot-grid" style="margin-bottom:8px;">
                <div id="creatorPendingSlotYes" class="creator-pending-slot creator-pending-slot-selectable" role="button" tabindex="0" data-outcome="1" aria-pressed="false">
                  <span class="creator-pending-slot-check" aria-hidden="true">✓</span>
                  <span id="creatorPendingSlotYesTitle" class="creator-pending-slot-title">-</span>
                  <span id="creatorPendingSlotYesProposer" class="mono" style="font-size:.67rem;"></span>
                  <span id="creatorPendingSlotYesReason" class="mono" style="font-size:.62rem; color:var(--muted);"></span>
                </div>
                <div id="creatorPendingSlotNo" class="creator-pending-slot creator-pending-slot-selectable" role="button" tabindex="0" data-outcome="2" aria-pressed="false">
                  <span class="creator-pending-slot-check" aria-hidden="true">✓</span>
                  <span id="creatorPendingSlotNoTitle" class="creator-pending-slot-title">-</span>
                  <span id="creatorPendingSlotNoProposer" class="mono" style="font-size:.67rem;"></span>
                  <span id="creatorPendingSlotNoReason" class="mono" style="font-size:.62rem; color:var(--muted);"></span>
                </div>
              </div>
              <div id="creatorPending" class="mono" style="font-size:.66rem; color:var(--muted); line-height:1.55; margin-bottom:10px;"></div>
              <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
                <button id="creatorAcceptBtn" class="creator-verdict-btn creator-verdict-accept">
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Accept
                </button>
                <button id="creatorRejectBtn" class="creator-verdict-btn creator-verdict-decline">
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M18 6L6 18M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Decline
                </button>
              </div>
            </div>

            <div id="creatorNoPending" class="mono" style="font-size:.68rem; color:var(--muted);">No pending proposal.</div>
          </div>

          <div id="creatorDisputeCard" class="card" style="padding:10px; margin-bottom:10px; display:none; gap:8px;">
            <div class="mono" style="font-size:.66rem; color:var(--muted);">ACTIVE DISPUTE</div>
            <div id="creatorDisputeBadges" style="display:flex; gap:6px; flex-wrap:wrap;"></div>
            <div id="creatorDisputeCountdown" class="mono" style="font-size:.66rem; color:var(--muted);"></div>
            <div style="display:grid; gap:8px;">
              <div class="card" style="padding:8px; display:flex; justify-content:space-between; align-items:center; gap:8px;">
                <div class="mono" style="font-size:.66rem; color:var(--muted);">Keep accepted proposal outcome</div>
                <button id="creatorResolveKeepBtn" class="creator-verdict-btn creator-verdict-accept" style="min-width:170px;" disabled>
                  Keep Accepted
                </button>
              </div>
              <div class="card" style="padding:8px; display:flex; justify-content:space-between; align-items:center; gap:8px;">
                <div class="mono" style="font-size:.66rem; color:var(--muted);">Override with disputed outcome</div>
                <button id="creatorResolveDisputedBtn" class="creator-verdict-btn creator-verdict-decline" style="min-width:170px;" disabled>
                  Accept Disputed
                </button>
              </div>
            </div>
          </div>

          <div class="card" style="padding:10px; display:grid; gap:8px;">
            <div class="mono" style="font-size:.66rem; color:var(--muted);">CREATED MARKETS</div>
            <div id="creatorMarketsHint" class="mono" style="font-size:.66rem; color:var(--muted);">Connect wallet to load your created markets.</div>
            <div id="creatorMarketsList" style="display:grid; gap:8px;"></div>
          </div>
        </div>

        <div id="courtPanel" class="panel">
          <div class="mono" style="font-size:.72rem; color:var(--muted); margin-bottom:8px;">Court / Juror Timing <span class="help-tip" title="Shows where the dispute is in the commit-reveal court lifecycle and what opens next.">?</span></div>
          <div id="courtDetails" class="card" style="padding:10px; margin-bottom:10px;"></div>
          <div class="card court-juror-shell" style="padding:10px; display:grid; gap:10px;">
            <div class="mono court-quick-row" style="font-size:.68rem; color:var(--muted); line-height:1.5;">
              Court quick start
              <span class="help-tip" title="Order: 1) Stake to be eligible, 2) If selected, commit before commit deadline, 3) reveal after commit closes and before reveal deadline, 4) proposer/disputer may appeal inside appeal window.">?</span>
              <span class="court-quick-note">Wait for the active phase/deadline if an action is unavailable.</span>
            </div>
            <div id="courtStatus" class="mono court-status-compact" style="font-size:.68rem; color:var(--muted);"></div>
            <div class="court-stats-grid" style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
              <div class="court-mini-card" style="border:1px solid var(--line); border-radius:10px; padding:8px; background:#071524;">
                <div class="mono" style="font-size:.64rem; color:var(--muted); margin-bottom:4px;">User Staked</div>
                <div id="jurorUserStakeStats" class="mono" style="font-size:.68rem; line-height:1.55;">Loading...</div>
              </div>
              <div class="court-mini-card" style="border:1px solid var(--line); border-radius:10px; padding:8px; background:#071524;">
                <div class="mono" style="font-size:.64rem; color:var(--muted); margin-bottom:4px;">Round Staked Stats <span class="help-tip" title="Only populates when a round is active.">?</span></div>
                <div id="jurorRoundStakeStats" class="mono" style="font-size:.68rem; line-height:1.55;">Loading...</div>
              </div>
            </div>
            <div class="card" style="padding:10px; display:grid; gap:8px;">
              <div class="mono" style="font-size:.66rem; color:var(--muted);">Staking</div>
              <div style="display:grid; gap:4px;">
                <div id="jurorStakeAmountLabel" class="mono" style="font-size:.66rem; color:var(--muted);">Stake Amount (Token) <span class="help-tip" title="Used for both Stake and Unstake. You must stake this token to be juror-eligible.">?</span></div>
                <input id="jurorStakeAmount" class="btn" style="text-align:left; background:#061320" placeholder="Stake/Unstake amount" />
              </div>
              <div style="display:flex; gap:8px; flex-wrap:wrap;">
                <button id="jurorStakeBtn" class="btn btn-primary" title="Lock stake so your address can be selected as a juror.">Stake</button>
                <button id="jurorUnstakeBtn" class="btn" title="Remove unlocked stake. Fails if you still have active juror assignments.">Unstake</button>
                <button id="jurorWithdrawBtn" class="btn btn-green" title="Claim accrued juror rewards.">Withdraw Rewards</button>
              </div>
            </div>
            <div class="card" style="padding:10px; display:grid; gap:8px;">
              <div class="mono" style="font-size:.66rem; color:var(--muted);">Voting</div>
              <div style="display:grid; gap:4px;">
                <div class="mono" style="font-size:.66rem; color:var(--muted);">Secret <span class="help-tip" title="Private value for commit/reveal. Use the same Secret when revealing.">?</span></div>
                <input id="jurorSalt" class="btn" style="text-align:left; background:#061320" placeholder="Secret (hex or text)" />
                <div style="display:flex; gap:6px; flex-wrap:wrap;">
                  <button id="jurorSaltGenerateBtn" type="button" class="btn" title="Generate a secure random Secret.">Generate Secret</button>
                  <button id="jurorSaltSaveBtn" type="button" class="btn" title="Save this Secret in session-only browser storage and download an encrypted backup file.">Save Secret</button>
                  <button id="jurorSaltRestoreBtn" type="button" class="btn" title="Restore Secret from an encrypted backup file.">Restore Secret</button>
                  <button id="jurorSaltCopyBtn" type="button" class="btn" title="Copy current Secret.">Copy Secret</button>
                </div>
                <div class="mono" style="font-size:.62rem; color:var(--muted); line-height:1.45;">
                  Secret handling
                  <span class="help-tip" title="Keep this Secret safe. Save stores it for this browser session only and downloads an encrypted backup file.">?</span>
                </div>
              </div>
              <div class="court-vote-grid" style="display:grid; grid-template-columns:1fr 1fr; gap:8px; align-items:center;">
                <div style="display:grid; gap:4px;">
                  <div class="mono" style="font-size:.66rem; color:var(--muted);">Vote Outcome <span class="help-tip" title="Outcome for commit/reveal.">?</span></div>
                  <select id="jurorOutcome" class="btn" style="text-align:left; background:#061320">
                    <option id="jurorYesOption" value="1">-</option>
                    <option id="jurorNoOption" value="2">-</option>
                    <option value="3">Invalid</option>
                  </select>
                </div>
                <div>
                  <div id="jurorCommitPreview" class="mono" style="font-size:.64rem; color:var(--muted); word-break:break-all;">Commitment preview: n/a</div>
                  <div class="mono" style="font-size:.62rem; color:var(--muted); margin-top:4px;">Preview only <span class="help-tip" title="Preview of the commit hash.">?</span></div>
                </div>
              </div>
              <div style="display:flex; gap:8px; flex-wrap:wrap;">
                <button id="jurorCommitBtn" class="btn btn-amber" title="Selected jurors only. Submit commitment before commit deadline.">Commit Vote</button>
                <button id="jurorRevealBtn" class="btn btn-primary" title="Selected jurors only. Reveal after commit phase and before reveal deadline using same outcome + Secret.">Reveal Vote</button>
              </div>
            </div>
            <div class="card" style="padding:10px; display:grid; gap:8px;">
              <div class="mono" style="font-size:.66rem; color:var(--muted);">Appeal</div>
              <div class="mono" style="font-size:.64rem; color:var(--muted); line-height:1.45;">
                Appeal use
                <span class="help-tip" title="Appeal is separate from voting. Use it only if you are proposer/disputer and the appeal window is open.">?</span>
              </div>
              <div id="appealTargetHint" class="mono" style="font-size:.66rem; color:var(--muted);"></div>
              <div id="appealCostHint" class="mono" style="font-size:.66rem; color:var(--muted);"></div>
              <div id="appealUnavailableHint" class="mono" style="font-size:.66rem; color:var(--muted);" hidden></div>
              <div id="appealReasonBlock" style="display:grid; gap:4px;">
                <div class="mono" style="font-size:.66rem; color:var(--muted);">Appeal Reason (Required, max 100 bytes)</div>
                <textarea id="appealReason" class="btn" style="text-align:left; background:#061320; min-height:56px; resize:vertical;" placeholder="Required rationale for appeal..."></textarea>
              </div>
              <div id="appealActionRow" style="display:flex; gap:8px; flex-wrap:wrap;">
                <button id="jurorAppealBtn" class="btn btn-red" title="Only proposer/disputer can appeal after a round resolves and before appeal window closes.">Appeal</button>
              </div>
            </div>
          </div>
        </div>

        <div id="claimsPanel" class="panel">
          <div style="display:flex; gap:8px; flex-wrap:wrap;">
            <button id="claimBtn" class="btn btn-green">Claim</button>
          </div>
          <div id="claimPreview" class="mono" style="font-size:.68rem; color:var(--muted); line-height:1.5; margin-top:8px;"></div>
        </div>

        <div id="analyticsPanel" class="panel">
            <div class="analytics-range-row">
            <button id="analyticsRangeShort" class="btn" type="button">12h</button>
            <button id="analyticsRangeMedium" class="btn btn-primary" type="button">24h</button>
            <button id="analyticsRangeFull" class="btn" type="button">7d</button>
            <button id="analyticsRefreshBtn" class="btn icon-btn" type="button" aria-label="Refresh analytics" title="Refresh analytics">${REFRESH_ICON_SVG}</button>
          </div>
            <div class="analytics-chart-card">
                <div class="mono analytics-chart-title">Market Split</div>
              <div id="analyticsProbabilityHistory"></div>
          </div>
            <div class="analytics-chart-card">
              <div class="mono analytics-chart-title">Window Net</div>
              <div id="analyticsFlowHistory"></div>
          </div>
            <div class="analytics-chart-card">
              <div class="mono analytics-chart-title">Buy / Sell</div>
              <div id="analyticsFlowStackedHistory"></div>
          </div>
        </div>
      </section>
    </main>
  `;

  const el = {
    walletBtn: document.getElementById('walletBtn') as HTMLButtonElement,
    marketAddr: document.getElementById('marketAddr') as HTMLDivElement,
    marketAddrCopyBtn: document.getElementById('marketAddrCopyBtn') as HTMLButtonElement,
    marketLinkCopyBtn: document.getElementById('marketLinkCopyBtn') as HTMLButtonElement,
    marketQuestion: document.getElementById('marketQuestion') as HTMLDivElement,
    marketStatus: document.getElementById('marketStatus') as HTMLDivElement,
    marketPhase: document.getElementById('marketPhase') as HTMLSpanElement,
    yesBar: document.getElementById('yesBar') as HTMLDivElement,
    noBar: document.getElementById('noBar') as HTMLDivElement,
    yesPct: document.getElementById('yesPct') as HTMLSpanElement,
    noPct: document.getElementById('noPct') as HTMLSpanElement,
    yesLabelOdds: document.getElementById('yesLabelOdds') as HTMLSpanElement,
    noLabelOdds: document.getElementById('noLabelOdds') as HTMLSpanElement,
    reserveLine: document.getElementById('reserveLine') as HTMLDivElement,
    collateralLine: document.getElementById('collateralLine') as HTMLDivElement,
    amount: document.getElementById('amountInput') as HTMLInputElement,
    amountInputPrefix: document.getElementById('amountInputPrefix') as HTMLSpanElement,
    side: document.getElementById('sideSelect') as HTMLSelectElement,
    sideDropdown: document.getElementById('sideDropdown') as HTMLDivElement,
    sideDropdownBtn: document.getElementById('sideDropdownBtn') as HTMLButtonElement,
    sideDropdownValue: document.getElementById('sideDropdownValue') as HTMLSpanElement,
    receiveSymbol: document.getElementById('receiveSymbol') as HTMLSpanElement,
    paySideDropdown: document.getElementById('paySideDropdown') as HTMLDivElement,
    paySideDropdownBtn: document.getElementById('paySideDropdownBtn') as HTMLButtonElement,
    paySideDropdownValue: document.getElementById('paySideDropdownValue') as HTMLSpanElement,
    receiveAssetIcon: document.getElementById('receiveAssetIcon') as HTMLImageElement,
    paySideDropdownMenu: document.getElementById('paySideDropdownMenu') as HTMLDivElement,
    sideDropdownMenu: document.getElementById('sideDropdownMenu') as HTMLDivElement,
    paySideOptionYesBtn: document.getElementById('paySideOptionYesBtn') as HTMLButtonElement,
    paySideOptionNoBtn: document.getElementById('paySideOptionNoBtn') as HTMLButtonElement,
    sideOptionYesBtn: document.getElementById('sideOptionYesBtn') as HTMLButtonElement,
    sideOptionNoBtn: document.getElementById('sideOptionNoBtn') as HTMLButtonElement,
    tradeOverride: document.getElementById('tradeOverride') as HTMLInputElement,
    tradeGuard: document.getElementById('tradeGuard') as HTMLDivElement,
    payPercentDropdown: document.getElementById('payPercentDropdown') as HTMLDivElement,
    tradePriceRefreshIndicator: document.getElementById('tradePriceRefreshIndicator') as HTMLSpanElement,
    payPercentDropdownBtn: document.getElementById('payPercentDropdownBtn') as HTMLButtonElement,
    payPercentDropdownValue: document.getElementById('payPercentDropdownValue') as HTMLSpanElement,
    payPercentDropdownMenu: document.getElementById('payPercentDropdownMenu') as HTMLDivElement,
    payPercent25Btn: document.getElementById('payPercent25Btn') as HTMLButtonElement,
    payPercent50Btn: document.getElementById('payPercent50Btn') as HTMLButtonElement,
    payPercent75Btn: document.getElementById('payPercent75Btn') as HTMLButtonElement,
    payPercent100Btn: document.getElementById('payPercent100Btn') as HTMLButtonElement,
    payBalanceOption: document.getElementById('payBalanceOption') as HTMLOptionElement,
    payPercentSelect: document.getElementById('payPercentSelect') as HTMLSelectElement,
    buyUsdcActions: document.getElementById('buyUsdcActions') as HTMLDivElement,
    buyUsdcCopyBtn: document.getElementById('buyUsdcCopyBtn') as HTMLButtonElement,
    buyUsdcLinkBtn: document.getElementById('buyUsdcLinkBtn') as HTMLButtonElement,
    payAssetIcon: document.getElementById('payAssetIcon') as HTMLImageElement,
    yesBalanceLabel: document.getElementById('yesBalanceLabel') as HTMLSpanElement,
    noBalanceLabel: document.getElementById('noBalanceLabel') as HTMLSpanElement,
    yesBalanceTrade: document.getElementById('yesBalanceTrade') as HTMLSpanElement,
    noBalanceTrade: document.getElementById('noBalanceTrade') as HTMLSpanElement,
    tradeExecutionInsights: document.getElementById('tradeExecutionInsights') as HTMLDivElement,
    tradeImpactTrigger: document.getElementById('tradeImpactTrigger') as HTMLDivElement,
    tradeImpactPanel: document.getElementById('tradeImpactPanel') as HTMLDivElement,
    yesSideOption: document.getElementById('yesSideOption') as HTMLOptionElement,
    noSideOption: document.getElementById('noSideOption') as HTMLOptionElement,
    tradeModeToggle: document.getElementById('tradeModeToggle') as HTMLButtonElement,
    tradeActionBtn: document.getElementById('tradeActionBtn') as HTMLButtonElement,
    tradeRefreshBtn: document.getElementById('tradeRefreshBtn') as HTMLButtonElement,
    tradeSettingsBtn: document.getElementById('tradeSettingsBtn') as HTMLButtonElement,
    tradeSettingsModal: document.getElementById('tradeSettingsModal') as HTMLDivElement,
    tradeSettingsCloseBtn: document.getElementById('tradeSettingsCloseBtn') as HTMLButtonElement,
    tradeSlippagePresets: document.getElementById('tradeSlippagePresets') as HTMLDivElement,
    tradeSlippageCustomInput: document.getElementById('tradeSlippageCustomInput') as HTMLInputElement,
    tradeExpertModeToggle: document.getElementById('tradeExpertModeToggle') as HTMLInputElement,
    receivePositionLabel: document.getElementById('receivePositionLabel') as HTMLSpanElement,
    resolutionYesOption: document.getElementById('resolutionYesOption') as HTMLOptionElement,
    resolutionNoOption: document.getElementById('resolutionNoOption') as HTMLOptionElement,
    jurorYesOption: document.getElementById('jurorYesOption') as HTMLOptionElement,
    jurorNoOption: document.getElementById('jurorNoOption') as HTMLOptionElement,
    claimBtn: document.getElementById('claimBtn') as HTMLButtonElement,
    claimPreview: document.getElementById('claimPreview') as HTMLDivElement,
    resolutionOutcome: document.getElementById('outcomeSelect') as HTMLSelectElement,
    disputeOutcome: document.getElementById('disputeOutcomeSelect') as HTMLSelectElement,
    disputeYesOption: document.getElementById('disputeYesOption') as HTMLOptionElement,
    disputeNoOption: document.getElementById('disputeNoOption') as HTMLOptionElement,
    proposeBtn: document.getElementById('proposeBtn') as HTMLButtonElement,
    disputeBtn: document.getElementById('disputeBtn') as HTMLButtonElement,
    finalizeBtn: document.getElementById('finalizeBtn') as HTMLButtonElement,
    emergencyBtn: document.getElementById('emergencyBtn') as HTMLButtonElement,
    creatorStatus: document.getElementById('creatorStatus') as HTMLDivElement,
    creatorProposalBlock: document.getElementById('creatorProposalBlock') as HTMLDivElement,
    creatorPendingOutcomePill: document.getElementById('creatorPendingOutcomePill') as HTMLSpanElement,
    creatorPendingProposer: document.getElementById('creatorPendingProposer') as HTMLSpanElement,
    creatorPendingSlotYes: document.getElementById('creatorPendingSlotYes') as HTMLDivElement,
    creatorPendingSlotNo: document.getElementById('creatorPendingSlotNo') as HTMLDivElement,
    creatorPendingSlotYesTitle: document.getElementById('creatorPendingSlotYesTitle') as HTMLSpanElement,
    creatorPendingSlotNoTitle: document.getElementById('creatorPendingSlotNoTitle') as HTMLSpanElement,
    creatorPendingSlotYesProposer: document.getElementById('creatorPendingSlotYesProposer') as HTMLSpanElement,
    creatorPendingSlotNoProposer: document.getElementById('creatorPendingSlotNoProposer') as HTMLSpanElement,
    creatorPendingSlotYesReason: document.getElementById('creatorPendingSlotYesReason') as HTMLSpanElement,
    creatorPendingSlotNoReason: document.getElementById('creatorPendingSlotNoReason') as HTMLSpanElement,
    creatorPendingReason: document.getElementById('creatorPendingReason') as HTMLDivElement,
    creatorPending: document.getElementById('creatorPending') as HTMLDivElement,
    creatorDisputeCard: document.getElementById('creatorDisputeCard') as HTMLDivElement,
    creatorDisputeBadges: document.getElementById('creatorDisputeBadges') as HTMLDivElement,
    creatorDisputeCountdown: document.getElementById('creatorDisputeCountdown') as HTMLDivElement,
    creatorNoPending: document.getElementById('creatorNoPending') as HTMLDivElement,
    creatorActionsHelp: document.getElementById('creatorActionsHelp') as HTMLDivElement,
    creatorAcceptBtn: document.getElementById('creatorAcceptBtn') as HTMLButtonElement,
    creatorRejectBtn: document.getElementById('creatorRejectBtn') as HTMLButtonElement,
    creatorResolveKeepBtn: document.getElementById('creatorResolveKeepBtn') as HTMLButtonElement,
    creatorResolveDisputedBtn: document.getElementById('creatorResolveDisputedBtn') as HTMLButtonElement,
    creatorTabBtn: document.getElementById('creatorTabBtn') as HTMLButtonElement,
    claimsTabBtn: document.getElementById('claimsTabBtn') as HTMLButtonElement,
    extraControlsToggle: document.getElementById('extraControlsToggle') as HTMLButtonElement,
    primaryTabsContainer: document.getElementById('primaryTabsContainer') as HTMLDivElement,
    advancedTabsContainer: document.getElementById('advancedTabsContainer') as HTMLDivElement,
    creatorMarketsHint: document.getElementById('creatorMarketsHint') as HTMLDivElement,
    creatorMarketsList: document.getElementById('creatorMarketsList') as HTMLDivElement,
    analyticsRangeShort: document.getElementById('analyticsRangeShort') as HTMLButtonElement,
    analyticsRangeMedium: document.getElementById('analyticsRangeMedium') as HTMLButtonElement,
    analyticsRangeFull: document.getElementById('analyticsRangeFull') as HTMLButtonElement,
    analyticsRefreshBtn: document.getElementById('analyticsRefreshBtn') as HTMLButtonElement,
    analyticsProbabilityHistory: document.getElementById('analyticsProbabilityHistory') as HTMLDivElement,
    analyticsFlowHistory: document.getElementById('analyticsFlowHistory') as HTMLDivElement,
    analyticsFlowStackedHistory: document.getElementById('analyticsFlowStackedHistory') as HTMLDivElement,
    resolutionState: document.getElementById('resolutionState') as HTMLDivElement,
    resolutionHelp: document.getElementById('resolutionHelp') as HTMLDivElement,
    resolutionBondAsset: document.getElementById('resolutionBondAsset') as HTMLSpanElement,
    resolutionBondAmount: document.getElementById('resolutionBondAmount') as HTMLSpanElement,
    resolutionBondAllowance: document.getElementById('resolutionBondAllowance') as HTMLSpanElement,
    resolutionBondStatus: document.getElementById('resolutionBondStatus') as HTMLDivElement,
    resolutionRisk: document.getElementById('resolutionRisk') as HTMLDivElement,
    resolutionDetails: document.getElementById('resolutionDetails') as HTMLDivElement,
    courtDetails: document.getElementById('courtDetails') as HTMLDivElement,
    courtStatus: document.getElementById('courtStatus') as HTMLDivElement,
    jurorStakeAmountLabel: document.getElementById('jurorStakeAmountLabel') as HTMLDivElement,
    jurorUserStakeStats: document.getElementById('jurorUserStakeStats') as HTMLDivElement,
    jurorRoundStakeStats: document.getElementById('jurorRoundStakeStats') as HTMLDivElement,
    jurorStakeAmount: document.getElementById('jurorStakeAmount') as HTMLInputElement,
    jurorSalt: document.getElementById('jurorSalt') as HTMLInputElement,
    jurorSaltGenerateBtn: document.getElementById('jurorSaltGenerateBtn') as HTMLButtonElement,
    jurorSaltSaveBtn: document.getElementById('jurorSaltSaveBtn') as HTMLButtonElement,
    jurorSaltRestoreBtn: document.getElementById('jurorSaltRestoreBtn') as HTMLButtonElement,
    jurorSaltCopyBtn: document.getElementById('jurorSaltCopyBtn') as HTMLButtonElement,
    jurorOutcome: document.getElementById('jurorOutcome') as HTMLSelectElement,
    jurorCommitPreview: document.getElementById('jurorCommitPreview') as HTMLDivElement,
    appealTargetHint: document.getElementById('appealTargetHint') as HTMLDivElement,
    appealCostHint: document.getElementById('appealCostHint') as HTMLDivElement,
    appealUnavailableHint: document.getElementById('appealUnavailableHint') as HTMLDivElement,
    appealReasonBlock: document.getElementById('appealReasonBlock') as HTMLDivElement,
    appealActionRow: document.getElementById('appealActionRow') as HTMLDivElement,
    appealReason: document.getElementById('appealReason') as HTMLTextAreaElement,
    jurorStakeBtn: document.getElementById('jurorStakeBtn') as HTMLButtonElement,
    jurorUnstakeBtn: document.getElementById('jurorUnstakeBtn') as HTMLButtonElement,
    jurorWithdrawBtn: document.getElementById('jurorWithdrawBtn') as HTMLButtonElement,
    jurorCommitBtn: document.getElementById('jurorCommitBtn') as HTMLButtonElement,
    jurorRevealBtn: document.getElementById('jurorRevealBtn') as HTMLButtonElement,
    jurorAppealBtn: document.getElementById('jurorAppealBtn') as HTMLButtonElement,
    logs: document.createElement('div'),
  };

  let currentMarketId = '';
  /** Per-market collateral address — may differ from DEPLOYMENT.collateral for custom markets. */
  let marketCollateralAddress: string = readCachedMarketCollateral();
  /** Collateral token metadata resolved from chain; starts neutral to avoid USDC flash on custom tokens. */
  let collateralMeta: CollateralMeta = marketCollateralAddress
    ? defaultCollateralMeta(marketCollateralAddress)
    : defaultCollateralMeta('0x0000000000000000000000000000000000000000');
  /** Resolved logo URL for the market collateral (used for pay-asset icon and CSS var). */
  let collateralLogoUrl: string = marketCollateralAddress
    ? (resolveTokenLogoUrlSync(marketCollateralAddress) ?? `${import.meta.env.BASE_URL}images/coin.svg`)
    : `${import.meta.env.BASE_URL}images/coin.svg`;
  const defaultUsdcLogoUrl = `${import.meta.env.BASE_URL}images/usdc.png`;
    let yesOutcomeLabel = '-';
  let noOutcomeLabel = '-';
  let yesTokenAddress: Address | null = null;
  let noTokenAddress: Address | null = null;
  let yesTokenDecimals = 18;
  let noTokenDecimals = 18;
  let currentYesReserve = 0n;
  let currentNoReserve = 0n;
  let cachedProposalReason = '';
  let cachedYesProposalReason = '';
  let cachedNoProposalReason = '';
  let cachedProposalReasonUnsupported = false;
  /** Cached resolution event logs — avoids redundant getLogs on every timer tick. Cleared on mutation. */
  let resolutionLogsCache: null | {
    marketId: string; ts: number;
    proposalLogs: any[]; proposalReasonLogs: any[];
    disputeLogs: any[]; proposalDisputedLogs: any[]; disputeResolvedByCreatorLogs: any[]; disputeReasonLogs: any[];
    appealReasonLogs: any[]; finalizedLogs: any[];
  } = null;
  const RESOLUTION_LOGS_CACHE_MS = 25_000;
  let cachedProposalOutcome: number | null = null;
  let cachedProposalProposer = '';
  let cachedYesProposalProposer = '';
  let cachedNoProposalProposer = '';
  let creatorDecisionOutcome: 1 | 2 | null = null;
  let creatorDisputeAcceptedOutcome: 1 | 2 | null = null;
  let creatorDisputeOutcome: 1 | 2 | null = null;
  let creatorDecisionDeadlineTs = 0;
  let creatorDisputeTimedOut = false;
  let creatorDisputeResolved = false;
  let creatorAddress: Address | null = null;
  let liquidityProviderAddress: Address | null = null;
  /** Cached immutable market fields — fetched once on first refresh. */
  let cachedQuestion = '';
  let cachedYesTokenAddress: Address | null = null;
  let cachedNoTokenAddress: Address | null = null;
  let walletHasCreatedMarket = false;
  let courtStakeAssetAddress: Address | null = null;
  let courtStakeAssetDecimals = 6;
  let courtStakeAssetSymbol = 'TOKEN';
  let courtAppealAssetAddress: Address | null = null;
  let courtAppealAssetDecimals = 6;
  let courtAppealAssetSymbol = 'TOKEN';
  let tradeMode: 'buy' | 'sell' = 'buy';
  let analyticsRange: AnalyticsRange = '24h';
  let usdcTradeBalanceRaw: bigint | null = null;
  let yesTradeBalanceRaw: bigint | null = null;
  let noTradeBalanceRaw: bigint | null = null;
  let yesTradeBalanceLabel = '';
  let noTradeBalanceLabel = '';
  let usdcTradeBalanceLabel = '';
  let tradeImpactExpanded = false;
  let tradeBlockedReason = '';
  let tradeSlippageBps = 50;
  let tradeExpertModeEnabled = false;
  let latestTradeImpactBps: bigint | null = null;
  let tradeImpactToleranceBreached = false;
  let suppressTradePreview = false;
  let analyticsLoaded = false;
  let analyticsDirty = true;
  let analyticsLoading: Promise<void> | null = null;
  let analyticsFetchEnabled = false;
  let analyticsUserActivated = false;
  let analyticsAutoRefreshStarted = false;
  let marketContextReady = false;
  let creatorMarketsLastLoadedAtMs = 0;
  let marketRefreshTimer: number | null = null;
  let analyticsRefreshTimer: number | null = null;
  let delayedMutationRefreshTimer: number | null = null;
  let refreshInFlight: Promise<void> | null = null;
  let tradePreviewDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let tradePreviewRequestVersion = 0;
  let tradePriceRefreshPending = 0;
  let cachedFeeConfig: { buyFeeBps: bigint; sellFeeBps: bigint; fetchedAtMs: number } | null = null;
  const FEE_CONFIG_CACHE_MS = 30_000;
  const TRADE_PREVIEW_DEBOUNCE_MS = 180;

  function onNetworkToggle(next: NetworkKey) {
    if (next === getActiveNetworkKey()) return;
    setActiveNetwork(next);
    window.location.reload();
  }

  const networkMainnetBtn = document.getElementById('networkMainnetBtn') as HTMLButtonElement | null;
  const networkTestnetBtn = document.getElementById('networkTestnetBtn') as HTMLButtonElement | null;
  const networkDropdown = document.getElementById('networkDropdown') as HTMLDetailsElement | null;
  const linksDropdown = document.getElementById('linksDropdown') as HTMLDetailsElement | null;
  networkMainnetBtn?.addEventListener('click', () => onNetworkToggle('mainnet'));
  networkTestnetBtn?.addEventListener('click', () => onNetworkToggle('testnet'));

  function positionHeaderDropdown(dropdown: HTMLDetailsElement | null): void {
    if (!dropdown || !dropdown.open) return;
    const summary = dropdown.querySelector('summary') as HTMLElement | null;
    const menu = dropdown.querySelector('.header-dropdown-menu') as HTMLElement | null;
    if (!summary || !menu) return;
    const viewportPadding = 8;
    const summaryRect = summary.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const centeredLeft = summaryRect.left + (summaryRect.width / 2) - (menuRect.width / 2);
    const maxLeft = Math.max(viewportPadding, window.innerWidth - viewportPadding - menuRect.width);
    const clampedLeft = Math.min(Math.max(viewportPadding, centeredLeft), maxLeft);
    menu.style.left = `${clampedLeft - summaryRect.left}px`;
    menu.style.right = 'auto';
    menu.style.transform = 'none';
  }

  function refreshHeaderDropdownPositions(): void {
    positionHeaderDropdown(networkDropdown);
    positionHeaderDropdown(linksDropdown);
  }

  const scheduleHeaderDropdownRefresh = (): void => {
    window.requestAnimationFrame(refreshHeaderDropdownPositions);
  };

  networkDropdown?.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  linksDropdown?.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  window.addEventListener('resize', refreshHeaderDropdownPositions);
  document.addEventListener('click', (event) => {
    const target = event.target as Node;
    if (networkDropdown && !networkDropdown.contains(target)) networkDropdown.open = false;
    if (linksDropdown && !linksDropdown.contains(target)) linksDropdown.open = false;
  });

  function getBuyUsdcPulseXUrl(): string {
    return buildBuyUsdcPulseXUrl(getActiveNetworkKey(), marketCollateralAddress);
  }

  function getMarketCollateralCacheKey(): string {
    return `boxbet:market-collateral:${getActiveNetworkKey()}:${marketAddress.toLowerCase()}`;
  }

  function readCachedMarketCollateral(): string {
    try {
      const value = String(localStorage.getItem(getMarketCollateralCacheKey()) || '').trim();
      if (!/^0x[0-9a-fA-F]{40}$/.test(value)) return '';
      if (isZeroAddress(value)) return '';
      return value;
    } catch {
      return '';
    }
  }

  function writeCachedMarketCollateral(address: string): void {
    try {
      if (!/^0x[0-9a-fA-F]{40}$/.test(address)) return;
      if (isZeroAddress(address)) return;
      localStorage.setItem(getMarketCollateralCacheKey(), address);
    } catch {
      // no-op
    }
  }

  function isDefaultUsdcCollateral(address: string): boolean {
    const value = (address || '').toLowerCase();
    if (!value) return false;
    const deploymentCollateral = (DEPLOYMENT.collateral || '').toLowerCase();
    const canonicalUsdc = (NETWORK_USDC[getActiveNetworkKey()] || '').toLowerCase();
    return value === deploymentCollateral || value === canonicalUsdc;
  }

  function applyCustomCollateralDecimalPolicy(meta: CollateralMeta, address: string): CollateralMeta {
    if (isDefaultUsdcCollateral(address)) return meta;
    const parsedDecimals = Number(meta.decimals);
    const safeDecimals = Number.isFinite(parsedDecimals) && parsedDecimals >= 0 && parsedDecimals <= 36
      ? parsedDecimals
      : 18;
    return { ...meta, decimals: safeDecimals };
  }

  function normalizeTokenDecimals(value: unknown, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    if (parsed < 0 || parsed > 36) return fallback;
    return parsed;
  }

  function tokenLabelHtml(symbol: string): string {
    return buildTokenLabelHtml(import.meta.env.BASE_URL, symbol);
  }

  const analyticsBuckets = createAnalyticsBucketsService({
    resolveBlockTimeLabel,
    getViewportWidth: () => window.innerWidth || 1280,
  });

  const helpTipPopover = document.createElement('div');
  helpTipPopover.className = 'help-tip-popover';
  helpTipPopover.hidden = true;
  helpTipPopover.setAttribute('role', 'tooltip');
  let activeHelpTip: HTMLElement | null = null;
  let helpTipPinned = false;
  let helpTipHideTimer: ReturnType<typeof setTimeout> | null = null;

  function clearHelpTipHideTimer(): void {
    if (!helpTipHideTimer) return;
    clearTimeout(helpTipHideTimer);
    helpTipHideTimer = null;
  }

  function prepHelpTipNode(node: HTMLElement): void {
    if (node.dataset.helpTipReady === '1') return;
    node.dataset.helpTipReady = '1';
    node.setAttribute('tabindex', '0');
    node.setAttribute('role', 'button');
    node.setAttribute('aria-label', 'Show help');
    node.setAttribute('aria-expanded', 'false');
  }

  function prepHelpTips(root: ParentNode = document): void {
    root.querySelectorAll('.help-tip').forEach((node) => prepHelpTipNode(node as HTMLElement));
  }

  function getHelpTipText(node: HTMLElement): string {
    prepHelpTipNode(node);
    const dataText = (node.dataset.tooltip || '').trim();
    if (dataText) return dataText;
    const titleText = (node.getAttribute('title') || '').trim();
    if (titleText) {
      node.dataset.tooltip = titleText;
      node.removeAttribute('title');
    }
    return titleText;
  }

  function positionHelpTip(node: HTMLElement): void {
    const rect = node.getBoundingClientRect();
    helpTipPopover.style.left = '0px';
    helpTipPopover.style.top = '0px';
    const popRect = helpTipPopover.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const preferredTop = rect.top - popRect.height - 10;
    const fallbackTop = rect.bottom + 10;
    const top = preferredTop >= 6 ? preferredTop : Math.min(fallbackTop, vh - popRect.height - 6);
    const centeredLeft = rect.left + (rect.width - popRect.width) / 2;
    const left = Math.max(6, Math.min(centeredLeft, vw - popRect.width - 6));

    helpTipPopover.style.left = `${Math.round(left)}px`;
    helpTipPopover.style.top = `${Math.round(top)}px`;
  }

  function hideHelpTip(): void {
    clearHelpTipHideTimer();
    if (activeHelpTip) {
      activeHelpTip.setAttribute('aria-expanded', 'false');
    }
    activeHelpTip = null;
    helpTipPinned = false;
    helpTipPopover.hidden = true;
    helpTipPopover.textContent = '';
  }

  function scheduleHelpTipHide(): void {
    if (helpTipPinned) return;
    clearHelpTipHideTimer();
    helpTipHideTimer = setTimeout(() => {
      hideHelpTip();
    }, 120);
  }

  function showHelpTip(node: HTMLElement, pinned: boolean): void {
    const text = getHelpTipText(node);
    if (!text) return;
    clearHelpTipHideTimer();
    activeHelpTip?.setAttribute('aria-expanded', 'false');
    activeHelpTip = node;
    helpTipPinned = pinned;
    helpTipPopover.textContent = text;
    helpTipPopover.hidden = false;
    node.setAttribute('aria-expanded', 'true');
    positionHelpTip(node);
  }

  function setupHelpTipBehavior(): void {
    document.body.appendChild(helpTipPopover);
    prepHelpTips(appRoot);

    appRoot.addEventListener('mouseover', (event) => {
      if (helpTipPinned) return;
      const tip = (event.target as HTMLElement).closest('.help-tip') as HTMLElement | null;
      if (!tip) return;
      showHelpTip(tip, false);
    });

    appRoot.addEventListener('mouseout', (event) => {
      if (helpTipPinned) return;
      const fromTip = (event.target as HTMLElement).closest('.help-tip');
      if (!fromTip) return;
      const related = event.relatedTarget as HTMLElement | null;
      if (related && ((related.closest('.help-tip') === fromTip) || helpTipPopover.contains(related))) {
        return;
      }
      scheduleHelpTipHide();
    });

    appRoot.addEventListener('focusin', (event) => {
      const tip = (event.target as HTMLElement).closest('.help-tip') as HTMLElement | null;
      if (!tip) return;
      showHelpTip(tip, false);
    });

    appRoot.addEventListener('focusout', (event) => {
      if (helpTipPinned) return;
      const fromTip = (event.target as HTMLElement).closest('.help-tip');
      if (!fromTip) return;
      const next = event.relatedTarget as HTMLElement | null;
      if (next && ((next.closest('.help-tip') === fromTip) || helpTipPopover.contains(next))) {
        return;
      }
      scheduleHelpTipHide();
    });

    appRoot.addEventListener('click', (event) => {
      const tip = (event.target as HTMLElement).closest('.help-tip') as HTMLElement | null;
      if (!tip) return;
      event.preventDefault();
      event.stopPropagation();
      if (activeHelpTip === tip && helpTipPinned) {
        hideHelpTip();
        return;
      }
      showHelpTip(tip, true);
    });

    appRoot.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const tip = (event.target as HTMLElement).closest('.help-tip') as HTMLElement | null;
      if (!tip) return;
      event.preventDefault();
      if (activeHelpTip === tip && helpTipPinned) {
        hideHelpTip();
        return;
      }
      showHelpTip(tip, true);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        hideHelpTip();
      }
    });

    document.addEventListener('click', (event) => {
      const target = event.target as Node;
      if (!activeHelpTip) return;
      if (activeHelpTip.contains(target)) return;
      if (helpTipPopover.contains(target)) return;
      hideHelpTip();
    });

    window.addEventListener('resize', () => {
      if (!activeHelpTip || helpTipPopover.hidden) return;
      positionHelpTip(activeHelpTip);
    });

    window.addEventListener('scroll', () => {
      if (!activeHelpTip || helpTipPopover.hidden) return;
      positionHelpTip(activeHelpTip);
    }, true);

    helpTipPopover.addEventListener('mouseenter', () => {
      clearHelpTipHideTimer();
    });

    helpTipPopover.addEventListener('mouseleave', () => {
      if (helpTipPinned) return;
      scheduleHelpTipHide();
    });
  }


  function applyOutcomeLabels(): void {
    el.yesLabelOdds.textContent = yesOutcomeLabel;
    el.noLabelOdds.textContent = noOutcomeLabel;
    el.yesBalanceLabel.textContent = yesOutcomeLabel;
    el.noBalanceLabel.textContent = noOutcomeLabel;
    syncSideOptionLabels();
    el.resolutionYesOption.textContent = yesOutcomeLabel;
    el.resolutionNoOption.textContent = noOutcomeLabel;
    el.disputeYesOption.textContent = yesOutcomeLabel;
    el.disputeNoOption.textContent = noOutcomeLabel;
    el.jurorYesOption.textContent = yesOutcomeLabel;
    el.jurorNoOption.textContent = noOutcomeLabel;
    el.creatorPendingSlotYesTitle.textContent = yesOutcomeLabel;
    el.creatorPendingSlotNoTitle.textContent = noOutcomeLabel;
  }

  function isYesTrending(): boolean {
    return currentYesReserve >= currentNoReserve;
  }

  function syncSideOptionLabels(): void {
    const yesIsTrending = isYesTrending();
    const noIsTrending = !yesIsTrending;
    const yesSuffix = yesTradeBalanceLabel ? ` - ${yesTradeBalanceLabel}` : '';
    const noSuffix = noTradeBalanceLabel ? ` - ${noTradeBalanceLabel}` : '';
    const yesText = `${yesOutcomeLabel}${yesSuffix}`;
    const noText = `${noOutcomeLabel}${noSuffix}`;
    el.yesSideOption.textContent = yesText;
    el.noSideOption.textContent = noText;
    el.paySideOptionYesBtn.textContent = yesText;
    el.paySideOptionNoBtn.textContent = noText;
    el.sideOptionYesBtn.textContent = yesText;
    el.sideOptionNoBtn.textContent = noText;
    el.yesSideOption.style.color = yesIsTrending ? '#2ec989' : '#e8637a';
    el.noSideOption.style.color = noIsTrending ? '#2ec989' : '#e8637a';
    el.yesSideOption.style.backgroundColor = yesIsTrending ? '#081f17' : '#2a1117';
    el.noSideOption.style.backgroundColor = noIsTrending ? '#081f17' : '#2a1117';
    el.yesSideOption.style.borderBottom = '1px solid rgba(255, 255, 255, 0.2)';
    el.yesSideOption.style.fontWeight = '700';
    el.noSideOption.style.fontWeight = '700';
    el.paySideOptionYesBtn.classList.toggle('trend-leading', yesIsTrending);
    el.paySideOptionYesBtn.classList.toggle('trend-trailing', !yesIsTrending);
    el.paySideOptionNoBtn.classList.toggle('trend-leading', noIsTrending);
    el.paySideOptionNoBtn.classList.toggle('trend-trailing', !noIsTrending);
    el.sideOptionYesBtn.classList.toggle('trend-leading', yesIsTrending);
    el.sideOptionYesBtn.classList.toggle('trend-trailing', !yesIsTrending);
    el.sideOptionNoBtn.classList.toggle('trend-leading', noIsTrending);
    el.sideOptionNoBtn.classList.toggle('trend-trailing', !noIsTrending);
    el.side.classList.toggle('is-yes', el.side.value === 'yes');
    el.side.classList.toggle('is-no', el.side.value === 'no');
    el.paySideOptionYesBtn.classList.toggle('is-active', el.side.value === 'yes');
    el.paySideOptionNoBtn.classList.toggle('is-active', el.side.value === 'no');
    el.sideOptionYesBtn.classList.toggle('is-active', el.side.value === 'yes');
    el.sideOptionNoBtn.classList.toggle('is-active', el.side.value === 'no');
    updateTradeActionButtonLabel();
    setPayAssetUi();
  }

  function setSideDropdownOpen(open: boolean): void {
    el.sideDropdown.classList.toggle('is-open', open);
    el.sideDropdownMenu.hidden = !open;
    el.sideDropdownBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function setPaySideDropdownOpen(open: boolean): void {
    const allowOpen = tradeMode === 'sell';
    const nextOpen = allowOpen && open;
    el.paySideDropdown.classList.toggle('is-open', nextOpen);
    el.paySideDropdownMenu.hidden = !nextOpen;
    el.paySideDropdownBtn.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
  }

  function setPayPercentDropdownOpen(open: boolean): void {
    const nextOpen = open;
    el.payPercentDropdown.classList.toggle('is-open', nextOpen);
    el.payPercentDropdownMenu.hidden = !nextOpen;
    el.payPercentDropdownBtn.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
  }

  function syncPayPercentOptionsUi(): void {
    const selected = el.payPercentSelect.value;
    el.payPercent25Btn.classList.toggle('is-active', selected === '25');
    el.payPercent50Btn.classList.toggle('is-active', selected === '50');
    el.payPercent75Btn.classList.toggle('is-active', selected === '75');
    el.payPercent100Btn.classList.toggle('is-active', selected === '100');
  }

  function updatePayAssetOutcomeTone(): void {
    el.payAssetIcon.classList.remove('is-outcome-yes', 'is-outcome-no');
    if (!el.payAssetIcon.classList.contains('is-out-asset-icon')) return;
    const isYes = el.side.value === 'yes';
    const selectedIsTrending = isYes ? isYesTrending() : !isYesTrending();
    el.payAssetIcon.classList.add(isYes ? 'is-outcome-yes' : 'is-outcome-no');
    el.payAssetIcon.classList.toggle('is-trend-leading', selectedIsTrending);
    el.payAssetIcon.classList.toggle('is-trend-trailing', !selectedIsTrending);
  }

  function fmtCourtStakeToken(amount: bigint): string {
    const n = Number(formatUnits(amount, courtStakeAssetDecimals));
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  function fmtCourtAppealToken(amount: bigint): string {
    const n = Number(formatUnits(amount, courtAppealAssetDecimals));
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  function updateCourtStakeUiLabel(): void {
    el.jurorStakeAmountLabel.innerHTML =
      `Stake Amount (${tokenLabelHtml(courtStakeAssetSymbol)}) <span class="help-tip" title="Used for both Stake and Unstake. You must approve and stake this token to become juror-eligible.">?</span>`;
    el.jurorStakeAmount.placeholder = `Stake/Unstake amount (${courtStakeAssetSymbol})`;
    prepHelpTips(el.jurorStakeAmountLabel);
  }

  async function readTokenMeta(token: Address): Promise<{ decimals: number; symbol: string }> {
    const [decimalsRead, symbolRead] = await Promise.allSettled([
      readClient.readContract({
        address: token,
        abi: erc20Abi,
        functionName: 'decimals',
      }),
      readClient.readContract({
        address: token,
        abi: erc20Abi,
        functionName: 'symbol',
      }),
    ]);

    return {
      decimals: decimalsRead.status === 'fulfilled' ? Number(decimalsRead.value as number | bigint) : 6,
      symbol: symbolRead.status === 'fulfilled' ? sanitizeTokenSymbol(symbolRead.value, 'TOKEN') : 'TOKEN',
    };
  }

  async function refreshCourtStakeAssetMeta(oracleAddress: Address): Promise<void> {
    const [stakeAssetRead, appealAssetRead] = await Promise.all([
      readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'stakeAsset',
      }),
      readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'appealBondAsset',
      }),
    ]);

    const stakeAsset = stakeAssetRead as Address;
    const appealAsset = appealAssetRead as Address;

    const needsStakeUpdate = !courtStakeAssetAddress || courtStakeAssetAddress.toLowerCase() !== stakeAsset.toLowerCase();
    const needsAppealUpdate = !courtAppealAssetAddress || courtAppealAssetAddress.toLowerCase() !== appealAsset.toLowerCase();
    if (!needsStakeUpdate && !needsAppealUpdate) return;

    if (needsStakeUpdate) {
      const stakeMeta = await readTokenMeta(stakeAsset);
      courtStakeAssetAddress = stakeAsset;
      courtStakeAssetDecimals = stakeMeta.decimals;
      courtStakeAssetSymbol = stakeMeta.symbol;
      updateCourtStakeUiLabel();
    }

    if (needsAppealUpdate) {
      const appealMeta = await readTokenMeta(appealAsset);
      courtAppealAssetAddress = appealAsset;
      courtAppealAssetDecimals = appealMeta.decimals;
      courtAppealAssetSymbol = appealMeta.symbol;
    }
  }

  async function runTxAction(button: HTMLButtonElement, pendingLabel: string, fn: () => Promise<void>): Promise<void> {
    const previous = button.textContent || pendingLabel;
    button.disabled = true;
    button.textContent = pendingLabel;
    try {
      await fn();
    } finally {
      button.textContent = previous;
      button.disabled = false;
    }
  }

  function updateWalletPill(): void {
    if (walletState.account) {
      el.walletBtn.textContent = shortAddr(walletState.account);
      el.walletBtn.classList.add('connected');
    } else {
      el.walletBtn.textContent = 'Connect Wallet';
      el.walletBtn.classList.remove('connected');
    }
  }

  function setTradeMode(nextMode: 'buy' | 'sell'): void {
    tradeMode = nextMode;
    const isBuy = nextMode === 'buy';
    el.tradeModeToggle.setAttribute('aria-pressed', String(!isBuy));
    el.tradeModeToggle.classList.toggle('is-buy', isBuy);
    el.tradeModeToggle.classList.toggle('is-sell', !isBuy);
    updateTradeActionButtonLabel();
    el.tradeActionBtn.classList.toggle('btn-green', isBuy);
    el.tradeActionBtn.classList.toggle('btn-red', !isBuy);
    el.receivePositionLabel.textContent = isBuy ? 'TO WIN' : 'To (Est)';
    setSideDropdownOpen(false);
    setPaySideDropdownOpen(false);
    setPayAssetUi();
    if (marketContextReady) {
      void refreshTradeBalances();
    }
    applySelectedPercentToInput();
    scheduleTradeExecutionInsightsRefresh(true);
  }

  function enableAnalyticsFetching(forceRefresh = false): void {
    analyticsUserActivated = true;
    if (!isPanelActive('analyticsPanel')) {
      analyticsFetchEnabled = false;
      return;
    }

    analyticsFetchEnabled = true;
    if (!analyticsAutoRefreshStarted) {
      analyticsAutoRefreshStarted = true;
      scheduleAnalyticsAutoRefresh(ANALYTICS_REFRESH_VISIBLE_MS);
    }
    if (forceRefresh) {
      void ensureAnalyticsLoaded(true);
      return;
    }
    if (isPanelActive('analyticsPanel')) {
      void ensureAnalyticsLoaded();
    }
  }

  function disableAnalyticsFetching(): void {
    analyticsFetchEnabled = false;
    if (analyticsRefreshTimer !== null) {
      window.clearTimeout(analyticsRefreshTimer);
      analyticsRefreshTimer = null;
    }
  }

  function scheduleTradeExecutionInsightsRefresh(immediate = false): void {
    if (tradePreviewDebounceTimer) {
      clearTimeout(tradePreviewDebounceTimer);
      tradePreviewDebounceTimer = null;
    }

    if (immediate) {
      void refreshTradeExecutionInsights();
      return;
    }

    tradePreviewDebounceTimer = setTimeout(() => {
      tradePreviewDebounceTimer = null;
      void refreshTradeExecutionInsights();
    }, TRADE_PREVIEW_DEBOUNCE_MS);
  }

  // Inject lava-lamp SVG once on first activation.
  let lavaLampInjected = false;
  function ensureLavaLamp(): void {
    if (lavaLampInjected) return;
    lavaLampInjected = true;
    el.tradePriceRefreshIndicator.innerHTML = `<svg class="tuc-lava-lamp" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMax meet" style="background:none;" aria-hidden="true"><defs><filter id="lava-filter" filterUnits="userSpaceOnUse" x="0" y="0" width="100" height="100"><feGaussianBlur in="SourceGraphic" stdDeviation="3"/><feComponentTransfer result="cutoff"><feFuncA type="linear" slope="10" intercept="-5"/></feComponentTransfer></filter></defs><g filter="url(#lava-filter)"><circle cx="46" cy="143.359" r="34.9216" fill="#ff2244"><animate attributeName="cy" values="151.789;-27.745" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.964s"/><animate attributeName="r" values="38;0;0" keyTimes="0;0.58;1" dur="1s" repeatCount="indefinite" begin="-0.964s"/></circle><circle cx="42" cy="46.44" r="12.8028" fill="#ff2244"><animate attributeName="cy" values="131.056;-25.346" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.458s"/><animate attributeName="r" values="29;0;0" keyTimes="0;0.969;1" dur="1s" repeatCount="indefinite" begin="-0.458s"/></circle><circle cx="70" cy="123.801" r="16.475" fill="#ff2244"><animate attributeName="cy" values="131.021;-1.974" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.971s"/><animate attributeName="r" values="18;0;0" keyTimes="0;0.641;1" dur="1s" repeatCount="indefinite" begin="-0.971s"/></circle><circle cx="42" cy="70.462" r="13.393" fill="#ff2244"><animate attributeName="cy" values="142.87;-22.334" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.355s"/><animate attributeName="r" values="38;0;0" keyTimes="0;0.677;1" dur="1s" repeatCount="indefinite" begin="-0.355s"/></circle><circle cx="48" cy="115.146" r="30.676" fill="#ff2244"><animate attributeName="cy" values="152.091;-31.887" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.117s"/><animate attributeName="r" values="47;0;0" keyTimes="0;0.578;1" dur="1s" repeatCount="indefinite" begin="-0.117s"/></circle><circle cx="51" cy="84.2" r="16.078" fill="#ff2244"><animate attributeName="cy" values="152.398;-31.107" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.288s"/><animate attributeName="r" values="33;0;0" keyTimes="0;0.725;1" dur="1s" repeatCount="indefinite" begin="-0.288s"/></circle><circle cx="51" cy="103.348" r="35.269" fill="#ff2244"><animate attributeName="cy" values="149.603;-44.000" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.156s"/><animate attributeName="r" values="47;0;0" keyTimes="0;0.957;1" dur="1s" repeatCount="indefinite" begin="-0.156s"/></circle></g><g filter="url(#lava-filter)"><circle cx="48" cy="57.484" r="5.379" fill="#2ec989"><animate attributeName="cy" values="121.802;-9.149" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.408s"/><animate attributeName="r" values="17;0;0" keyTimes="0;0.719;1" dur="1s" repeatCount="indefinite" begin="-0.408s"/></circle><circle cx="74" cy="57.772" r="5.98" fill="#2ec989"><animate attributeName="cy" values="125.679;-22.735" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.374s"/><animate attributeName="r" values="24;0;0" keyTimes="0;0.609;1" dur="1s" repeatCount="indefinite" begin="-0.374s"/></circle><circle cx="62" cy="108.733" r="12.974" fill="#2ec989"><animate attributeName="cy" values="140.296;-4.080" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.135s"/><animate attributeName="r" values="22;0;0" keyTimes="0;0.533;1" dur="1s" repeatCount="indefinite" begin="-0.135s"/></circle><circle cx="48" cy="70.456" r="11.656" fill="#2ec989"><animate attributeName="cy" values="129.822;-5.806" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.354s"/><animate attributeName="r" values="24;0;0" keyTimes="0;0.851;1" dur="1s" repeatCount="indefinite" begin="-0.354s"/></circle><circle cx="67" cy="112.025" r="20.853" fill="#2ec989"><animate attributeName="cy" values="131.981;-15.021" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.052s"/><animate attributeName="r" values="27;0;0" keyTimes="0;0.596;1" dur="1s" repeatCount="indefinite" begin="-0.052s"/></circle><circle cx="49" cy="121.86" r="24.606" fill="#2ec989"><animate attributeName="cy" values="127.051;-9.829" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.955s"/><animate attributeName="r" values="26;0;0" keyTimes="0;0.707;1" dur="1s" repeatCount="indefinite" begin="-0.955s"/></circle></g><g filter="url(#lava-filter)"><circle cx="48" cy="50.653" r="3.584" fill="#ff7700"><animate attributeName="cy" values="133.793;-12.191" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.486s"/><animate attributeName="r" values="17;0;0" keyTimes="0;0.722;1" dur="1s" repeatCount="indefinite" begin="-0.486s"/></circle><circle cx="61" cy="69.648" r="5.629" fill="#ff7700"><animate attributeName="cy" values="122.801;3.820" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.363s"/><animate attributeName="r" values="12;0;0" keyTimes="0;0.841;1" dur="1s" repeatCount="indefinite" begin="-0.363s"/></circle><circle cx="27" cy="107.213" r="12.508" fill="#ff7700"><animate attributeName="cy" values="133.345;-3.184" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.108s"/><animate attributeName="r" values="17;0;0" keyTimes="0;0.724;1" dur="1s" repeatCount="indefinite" begin="-0.108s"/></circle><circle cx="68" cy="104.98" r="9.688" fill="#ff7700"><animate attributeName="cy" values="123.892;-7.486" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.061s"/><animate attributeName="r" values="12;0;0" keyTimes="0;0.747;1" dur="1s" repeatCount="indefinite" begin="-0.061s"/></circle></g></svg>`;
  }

  function updateTradePriceRefreshIndicator(): void {
    const active = tradePriceRefreshPending > 0;
    if (active) ensureLavaLamp();
    el.tradePriceRefreshIndicator.hidden = !active;
    el.tradePriceRefreshIndicator.classList.toggle('is-active', active);
  }

  function beginTradePriceRefresh(): () => void {
    tradePriceRefreshPending += 1;
    updateTradePriceRefreshIndicator();
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      tradePriceRefreshPending = Math.max(0, tradePriceRefreshPending - 1);
      updateTradePriceRefreshIndicator();
    };
  }

  async function getCachedFeeConfig(): Promise<{ buyFeeBps: bigint; sellFeeBps: bigint }> {
    if (cachedFeeConfig) {
      return { buyFeeBps: cachedFeeConfig.buyFeeBps, sellFeeBps: cachedFeeConfig.sellFeeBps };
    }
    // Fallback: read from factory only if getTradeState hasn't been called yet.
    const feeConfigRead = await readClient.readContract({
      address: DEPLOYMENT.factory as Address,
      abi: factoryAbi,
      functionName: 'feeConfig',
    });
    const [, buyFeeBpsRaw, sellFeeBpsRaw] = feeConfigRead as [Address, number | bigint, number | bigint];
    cachedFeeConfig = {
      buyFeeBps: BigInt(Number(buyFeeBpsRaw)),
      sellFeeBps: BigInt(Number(sellFeeBpsRaw)),
      fetchedAtMs: Date.now(),
    };
    return { buyFeeBps: cachedFeeConfig.buyFeeBps, sellFeeBps: cachedFeeConfig.sellFeeBps };
  }

  function currentTradeInputDecimals(): number {
    if (tradeMode === 'buy') return collateralMeta.decimals;
    return collateralMeta.decimals;
  }

  function outcomeDisplayDecimals(_isYes: boolean): number {
    // Share math in this market is collateral-unit based; normalize display to collateral decimals.
    return collateralMeta.decimals;
  }

  function formatInputAmount(rawAmount: bigint, decimals: number): string {
    const full = formatUnits(rawAmount, decimals);
    return full.replace(/\.?0+$/, '') || '0';
  }

  function formatNumericInputDisplay(rawNormalized: string): string {
    if (!rawNormalized) return '';
    const hasTrailingDot = rawNormalized.endsWith('.');
    const parts = rawNormalized.split('.');
    const intPartRaw = parts[0] ?? '';
    const fracPart = parts[1] ?? '';
    const intPartDigits = intPartRaw === '' ? '0' : intPartRaw;
    const groupedInt = intPartDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (hasTrailingDot) return `${groupedInt}.`;
    if (fracPart !== '') return `${groupedInt}.${fracPart}`;
    return groupedInt;
  }

  function getAmountInputRaw(): string {
    return normalizeNumericInputValue(el.amount.value.trim());
  }

  function currentPayBalanceRaw(): bigint | null {
    if (!walletState.account) return null;
    if (tradeMode === 'buy') return usdcTradeBalanceRaw;
    return el.side.value === 'yes' ? yesTradeBalanceRaw : noTradeBalanceRaw;
  }

  function currentPayAssetLabel(): string {
    if (tradeMode === 'buy') return collateralMeta.symbol;
    return el.side.value === 'yes' ? yesOutcomeLabel : noOutcomeLabel;
  }

  function warnIfTradeAmountExceedsBalance(): boolean {
    const amountRaw = getAmountInputRaw();
    if (!amountRaw) return false;

    let amount: bigint;
    try {
      amount = parseUnits(amountRaw, currentTradeInputDecimals());
    } catch {
      return false;
    }

    const balance = currentPayBalanceRaw();
    if (balance === null || amount <= balance) return false;

    const assetLabel = currentPayAssetLabel();
    const displayDecimals = currentTradeInputDecimals();
    const entered = fmtToken(amount, displayDecimals);
    const available = fmtToken(balance, displayDecimals);
    logLine(el.logs, 'warn', `Insufficient ${assetLabel} balance. Entered ${entered} ${assetLabel}, available ${available} ${assetLabel}.`);
    return true;
  }

  function applySelectedPercentToInput(): void {
    const selectedPercent = Number(el.payPercentSelect.value || 0);
    if (!selectedPercent) return;
    const balance = currentPayBalanceRaw();
    if (balance === null) return;

    const amountRaw = (balance * BigInt(selectedPercent)) / 100n;
    el.amount.value = formatNumericInputDisplay(formatInputAmount(amountRaw, currentTradeInputDecimals()));
    el.payPercentSelect.value = '';
    syncPayPercentOptionsUi();
  }

  function normalizeNumericInputValue(raw: string): string {
    if (!raw) return '';
    let value = raw.replace(/[^0-9.]/g, '');
    const firstDot = value.indexOf('.');
    if (firstDot !== -1) {
      value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, '');
    }
    return value;
  }

  function selectedTradeTokenLabel(): string {
    return el.side.value === 'yes' ? yesOutcomeLabel : noOutcomeLabel;
  }

  function updateTradeActionButtonLabel(): void {
    const verb = tradeMode === 'buy' ? 'Buy' : 'Sell';
    el.tradeActionBtn.textContent = verb;
    el.tradeActionBtn.setAttribute('aria-label', verb);
  }

  function formatSlippageBps(bps: number): string {
    return `${(bps / 100).toFixed(2)}%`;
  }

  function setActiveSlippagePresetButton(): void {
    const presetButtons = Array.from(el.tradeSlippagePresets.querySelectorAll('[data-bps]')) as HTMLButtonElement[];
    for (const button of presetButtons) {
      const bps = Number(button.dataset.bps || 0);
      button.classList.toggle('is-active', bps === tradeSlippageBps);
    }
  }

  function openTradeSettingsModal(): void {
    el.tradeSlippageCustomInput.value = (tradeSlippageBps / 100).toFixed(2);
    el.tradeExpertModeToggle.checked = tradeExpertModeEnabled;
    setActiveSlippagePresetButton();
    el.tradeSettingsModal.hidden = false;
    el.tradeSettingsModal.style.display = 'flex';
  }

  function closeTradeSettingsModal(): void {
    el.tradeSettingsModal.hidden = true;
    el.tradeSettingsModal.style.display = 'none';
  }

  function applyTradeSettingsFromModal(): void {
    const parsedPercent = Number(el.tradeSlippageCustomInput.value.trim());
    if (!Number.isFinite(parsedPercent) || parsedPercent < 0.01 || parsedPercent > 50) {
      throw new Error('Slippage must be between 0.01% and 50.00%.');
    }
    tradeSlippageBps = Math.round(parsedPercent * 100);
    tradeExpertModeEnabled = el.tradeExpertModeToggle.checked;
    scheduleTradeExecutionInsightsRefresh(true);
  }

  function applyTradeSettingsIfValid(): void {
    const parsedPercent = Number(el.tradeSlippageCustomInput.value.trim());
    if (!Number.isFinite(parsedPercent) || parsedPercent < 0.01 || parsedPercent > 50) return;
    tradeSlippageBps = Math.round(parsedPercent * 100);
    tradeExpertModeEnabled = el.tradeExpertModeToggle.checked;
    setActiveSlippagePresetButton();
    scheduleTradeExecutionInsightsRefresh(true);
  }

  function assertTradeSafetyChecks(): void {
    if (tradeImpactToleranceBreached && !tradeExpertModeEnabled) {
      const impactLabel = latestTradeImpactBps === null ? '--' : `${(Number(latestTradeImpactBps) / 100).toFixed(2)}%`;
      throw new Error(`Price impact ${impactLabel} exceeds your slippage setting (${formatSlippageBps(tradeSlippageBps)}). Enable expert mode or increase slippage.`);
    }
  }

  function outcomeSymbol(label: string): string {
    const text = (label || '').trim();
    if (!text) return 'TKN';
    const words = text.split(/\s+/).filter(Boolean);
    const numericSuffix = words.find((word) => /^\d+$/.test(word)) || '';
    const alphaWords = words.filter((word) => /[A-Za-z]/.test(word));

    let letters = '';
    if (alphaWords.length === 1) {
      const token = alphaWords[0].replace(/[^A-Za-z0-9]/g, '');
      const caps = token.match(/[A-Z]/g) || [];
      if (caps.length >= 2) {
        letters = caps.slice(0, 3).join('');
      } else if (token.length <= 4) {
        letters = token.toUpperCase();
      } else {
        letters = token.slice(0, 3).toUpperCase();
      }
    } else if (alphaWords.length > 1) {
      letters = alphaWords.map((word) => word[0]).join('').slice(0, 3).toUpperCase();
    }

    const fallback = text.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase() || 'TKN';
    return `${letters || fallback}${numericSuffix}`;
  }

  function setPayAssetUi(): void {
    setPayPercentDropdownOpen(false);
    const yesIsTrending = isYesTrending();
    const selectedTokenLabel = selectedTradeTokenLabel();
    const outcomeLabelsLoaded = yesOutcomeLabel !== '-' && noOutcomeLabel !== '-';
    const selectedTokenBalance = el.side.value === 'yes' ? yesTradeBalanceLabel : noTradeBalanceLabel;
    const selectedTokenHasBalance = selectedTokenBalance !== '';
    const yesSuffix = yesTradeBalanceLabel ? ` - ${yesTradeBalanceLabel}` : '';
    const noSuffix = noTradeBalanceLabel ? ` - ${noTradeBalanceLabel}` : '';
    const selectedSideLabel = outcomeLabelsLoaded
      ? (el.side.value === 'yes' ? `${yesOutcomeLabel}${yesSuffix}` : `${noOutcomeLabel}${noSuffix}`)
      : '-';
    const activeBalanceLabel = tradeMode === 'buy'
      ? (usdcTradeBalanceLabel !== '' ? usdcTradeBalanceLabel : '-')
      : '';
    el.payBalanceOption.textContent = activeBalanceLabel;
    el.payPercentDropdownValue.textContent = activeBalanceLabel;
    syncPayPercentOptionsUi();
    el.paySideDropdownValue.textContent = tradeMode === 'buy' ? '' : selectedSideLabel;

    const isYes = el.side.value === 'yes';
    const selectedIsTrending = isYes ? yesIsTrending : !yesIsTrending;
    const payAndReceiveDropdownTone = (dropdownButton: HTMLButtonElement): void => {
      dropdownButton.classList.toggle('is-yes', isYes);
      dropdownButton.classList.toggle('is-no', !isYes);
      dropdownButton.classList.toggle('trend-leading', selectedIsTrending);
      dropdownButton.classList.toggle('trend-trailing', !selectedIsTrending);
      dropdownButton.classList.remove('is-usdc-static');
    };

    if (tradeMode === 'buy') {
      const isDefaultCollateral = marketCollateralAddress.toLowerCase() === DEPLOYMENT.collateral.toLowerCase();
      const collateralLogo = isDefaultUsdcCollateral(marketCollateralAddress) ? defaultUsdcLogoUrl : collateralLogoUrl;
      el.payPercentSelect.title = `Use a percent of your ${collateralMeta.symbol} balance`;
      el.payPercentDropdownBtn.title = `Use a percent of your ${collateralMeta.symbol} balance`;
      el.payPercentDropdownBtn.classList.add('has-usdc-logo');
      el.payPercentDropdownBtn.style.backgroundImage = `url('${collateralLogo}')`;
      el.payPercentDropdownBtn.classList.remove('has-percent-icon');
      el.payPercentDropdown.classList.add('is-buy');
      el.payPercentDropdown.classList.remove('is-sell');
      el.paySideDropdown.hidden = true;
      el.buyUsdcActions.hidden = false;
      el.buyUsdcCopyBtn.title = `Copy ${collateralMeta.symbol} address (${marketCollateralAddress})`;
      el.buyUsdcCopyBtn.setAttribute('aria-label', `Copy ${collateralMeta.symbol} address`);
      el.buyUsdcLinkBtn.title = `Buy ${collateralMeta.symbol} on PulseX`;
      el.buyUsdcLinkBtn.setAttribute('aria-label', `Buy ${collateralMeta.symbol} on PulseX`);
      el.amountInputPrefix.hidden = false;
      el.amountInputPrefix.textContent = isDefaultCollateral ? '$' : `${collateralMeta.symbol}`;
      el.amountInputPrefix.classList.toggle('is-symbol', !isDefaultCollateral);
      el.payAssetIcon.src = collateralLogo;
      el.payAssetIcon.alt = collateralMeta.symbol;
      el.payAssetIcon.classList.remove('is-out-asset-icon', 'is-outcome-yes', 'is-outcome-no');
      el.paySideDropdownBtn.classList.remove('is-yes', 'is-no');
      el.paySideDropdownBtn.classList.add('is-usdc-static');
      el.sideDropdownValue.textContent = selectedSideLabel;
      el.sideDropdownValue.style.display = '';
      el.receiveSymbol.style.display = 'none';
      payAndReceiveDropdownTone(el.sideDropdownBtn);
      el.receiveAssetIcon.src = '/boxbet/images/win.svg';
      el.receiveAssetIcon.alt = selectedTokenLabel;
      el.receiveAssetIcon.classList.add('is-out-asset-icon');
      el.receiveAssetIcon.classList.toggle('is-outcome-yes', isYes);
      el.receiveAssetIcon.classList.toggle('is-outcome-no', !isYes);
      el.receiveAssetIcon.classList.toggle('is-trend-leading', selectedIsTrending);
      el.receiveAssetIcon.classList.toggle('is-trend-trailing', !selectedIsTrending);
      el.sideDropdownMenu.setAttribute('aria-label', 'Receive Position options');
      return;
    }

    el.payPercentDropdownBtn.classList.remove('has-usdc-logo');
    el.payPercentDropdownBtn.classList.add('has-percent-icon');
    el.payPercentDropdownBtn.style.backgroundImage = '';
    el.payPercentDropdownBtn.title = `Use a percent of your ${selectedTokenLabel} balance`;
    el.payPercentDropdown.classList.remove('is-buy');
    el.payPercentDropdown.classList.add('is-sell');
    el.paySideDropdown.hidden = false;
    el.buyUsdcActions.hidden = true;
    el.amountInputPrefix.hidden = true;
    el.amountInputPrefix.classList.remove('is-symbol');
    el.payPercentSelect.title = `Use a percent of your ${selectedTokenLabel} balance`;
    el.payAssetIcon.src = '/boxbet/images/out.svg';
    el.payAssetIcon.alt = selectedTokenLabel;
    el.payAssetIcon.classList.add('is-out-asset-icon');
    updatePayAssetOutcomeTone();
    payAndReceiveDropdownTone(el.paySideDropdownBtn);

    el.sideDropdownValue.textContent = '';
    el.sideDropdownValue.style.display = 'none';
    el.receiveSymbol.textContent = collateralMeta.symbol;
    el.receiveSymbol.style.display = '';
    el.sideDropdownBtn.classList.remove('is-yes', 'is-no');
    el.sideDropdownBtn.classList.add('is-usdc-static');
      const collateralLogo = isDefaultUsdcCollateral(marketCollateralAddress) ? defaultUsdcLogoUrl : collateralLogoUrl;
      el.receiveAssetIcon.src = collateralLogo;
      el.receiveAssetIcon.alt = collateralMeta.symbol;
      el.receiveAssetIcon.classList.remove('is-out-asset-icon', 'is-outcome-yes', 'is-outcome-no', 'is-trend-leading', 'is-trend-trailing');
    el.sideDropdownMenu.setAttribute('aria-label', 'Receive asset');
  }

  async function refreshTradeExecutionInsights(): Promise<void> {
    const requestVersion = ++tradePreviewRequestVersion;
    const isStaleRequest = (): boolean => requestVersion !== tradePreviewRequestVersion;
    const endPriceRefresh = beginTradePriceRefresh();

    try {

    const amountRaw = getAmountInputRaw();
    const isBuy = tradeMode === 'buy';
    const sideYes = el.side.value === 'yes';
    const sideLabel = sideYes ? yesOutcomeLabel : noOutcomeLabel;
    const selectedIsTrending = sideYes ? currentYesReserve >= currentNoReserve : currentNoReserve > currentYesReserve;
    const inReserve = isBuy
      ? (sideYes ? currentYesReserve : currentNoReserve)
      : (sideYes ? currentNoReserve : currentYesReserve);
    const outReserve = isBuy
      ? (sideYes ? currentNoReserve : currentYesReserve)
      : (sideYes ? currentYesReserve : currentNoReserve);
    const totalDepth = currentYesReserve + currentNoReserve;

    const sellOutputPrefix = isDefaultUsdcCollateral(marketCollateralAddress)
      ? '$'
      : escapeHtml(collateralMeta.symbol);

    const renderPreviewShell = (expectedValue: string, note?: string): string => `
      <div class="trade-preview-shell">
        <div class="trade-preview-output">
          <div class="mono trade-preview-output-label">Expected Output</div>
          <div class="trade-preview-output-value">
            ${isBuy
              ? `<span class="tuc-asset-logo"><img src="/boxbet/images/win.svg" alt="${sideLabel}" class="tuc-asset-logo-img is-out-asset-icon ${sideYes ? 'is-outcome-yes' : 'is-outcome-no'} ${selectedIsTrending ? 'is-trend-leading' : 'is-trend-trailing'}" /></span>`
              : ''}
            <span>${expectedValue}</span>
          </div>
        </div>
        ${note ? `<div class="mono trade-preview-note">${note}</div>` : ''}
      </div>
    `;

    const setImpactTrigger = (content: string): void => {
      el.tradeImpactTrigger.innerHTML = content;
    };

    const setImpactPanel = (content: string): void => {
      el.tradeImpactPanel.innerHTML = content;
    };

    if (suppressTradePreview) {
      latestTradeImpactBps = null;
      tradeImpactToleranceBreached = false;
      el.tradeExecutionInsights.innerHTML = '<div class="mono trade-preview-note">Market finalized. Trading is closed.</div>';
      tradeImpactExpanded = false;
      setImpactTrigger('');
      setImpactPanel('');
      return;
    }

    if (!amountRaw) {
      latestTradeImpactBps = null;
      tradeImpactToleranceBreached = false;
      el.tradeExecutionInsights.innerHTML = renderPreviewShell('-');
      tradeImpactExpanded = false;
      setImpactTrigger('');
      setImpactPanel('');
      return;
    }

    let amount: bigint;
    try {
      amount = parseUnits(amountRaw, currentTradeInputDecimals());
    } catch {
      latestTradeImpactBps = null;
      tradeImpactToleranceBreached = false;
      el.tradeExecutionInsights.innerHTML = renderPreviewShell(
        '-',
        'Enter a valid numeric amount.',
      );
      tradeImpactExpanded = false;
      setImpactTrigger('');
      setImpactPanel('');
      return;
    }

    if (amount <= 0n) {
      latestTradeImpactBps = null;
      tradeImpactToleranceBreached = false;
      el.tradeExecutionInsights.innerHTML = renderPreviewShell('-');
      tradeImpactExpanded = false;
      setImpactTrigger('');
      setImpactPanel('');
      return;
    }

    if (tradeBlockedReason) {
      latestTradeImpactBps = null;
      tradeImpactToleranceBreached = false;
      el.tradeExecutionInsights.innerHTML = renderPreviewShell(
        '-',
        tradeBlockedReason,
      );
      tradeImpactExpanded = false;
      setImpactTrigger('');
      setImpactPanel('');
      return;
    }

    if (inReserve <= 0n || outReserve <= 0n) {
      latestTradeImpactBps = null;
      tradeImpactToleranceBreached = false;
      el.tradeExecutionInsights.innerHTML = renderPreviewShell(
        '-',
        'Preview unavailable until reserves are active and amount is positive.',
      );
      tradeImpactExpanded = false;
      setImpactTrigger('');
      setImpactPanel('');
      return;
    }

    try {
      // Use quoteTrade for a single-call preview with fee breakdown.
      const quoteRead = await readClient.readContract({
        address: marketAddress,
        abi: marketAbi,
        functionName: 'quoteTrade',
        args: [isBuy, sideYes, amount],
      });
      const [amountOutRaw, feeRaw, netRaw] = quoteRead as [bigint, bigint, bigint, bigint, bigint];
      const previewAmount = amountOutRaw;
      const feeAmount = feeRaw;
      const netCollateralAmount = netRaw;
      const { buyFeeBps, sellFeeBps } = cachedFeeConfig ?? { buyFeeBps: 0n, sellFeeBps: 0n };
      const feeBps = isBuy ? buyFeeBps : sellFeeBps;

      if (isStaleRequest()) return;

      let impactBps = 0n;
      let effectivePriceText = 'n/a';
      let expectedText = '';

      if (isBuy) {
        const outcomeDecimals = outcomeDisplayDecimals(sideYes);
        const netIn = netCollateralAmount;
        const baselineShares = inReserve === 0n ? 0n : (netIn * outReserve) / inReserve;
        impactBps = baselineShares > 0n && baselineShares >= previewAmount
          ? ((baselineShares - previewAmount) * 10_000n) / baselineShares
          : 0n;
        expectedText = fmtToken(previewAmount, outcomeDecimals);

        const amountFloat = Number(formatUnits(amount, collateralMeta.decimals));
        const sharesFloat = Number(formatUnits(previewAmount, outcomeDecimals));
        if (amountFloat > 0 && sharesFloat > 0) {
          const priceStr = (amountFloat / sharesFloat).toFixed(4);
          effectivePriceText = collateralMeta.usdPrice !== null
            ? `$${priceStr} per ${sideLabel}`
            : `${priceStr} ${collateralMeta.symbol} per ${sideLabel}`;
        }
      } else {
        const grossOut = (amount * outReserve) / (inReserve + amount);
        const baselineGross = inReserve === 0n ? 0n : (amount * outReserve) / inReserve;
        impactBps = baselineGross > 0n && baselineGross >= grossOut
          ? ((baselineGross - grossOut) * 10_000n) / baselineGross
          : 0n;
        expectedText = fmtCollateral(previewAmount, collateralMeta);

        const outFloat = Number(formatUnits(previewAmount, collateralMeta.decimals));
        const sharesFloat = Number(formatUnits(amount, currentTradeInputDecimals()));
        if (outFloat > 0 && sharesFloat > 0) {
          const priceStr = (outFloat / sharesFloat).toFixed(4);
          effectivePriceText = collateralMeta.usdPrice !== null
            ? `$${priceStr} per ${sideLabel}`
            : `${priceStr} ${collateralMeta.symbol} per ${sideLabel}`;
        }
      }

      const impactTone = impactBps >= 300n ? 'var(--red)' : impactBps >= 100n ? 'var(--amber)' : 'var(--green)';
      const impactClass = impactBps >= 300n ? 'trade-preview-impact-pill-high' : impactBps >= 100n ? 'trade-preview-impact-pill-medium' : 'trade-preview-impact-pill-low';
      const impactText = `${(Number(impactBps) / 100).toFixed(2)}%`;
      latestTradeImpactBps = impactBps;
      tradeImpactToleranceBreached = impactBps > BigInt(tradeSlippageBps);
      const yesSplit = totalDepth > 0n ? fmtPct(currentYesReserve, totalDepth) : '50.0%';
      const noSplit = totalDepth > 0n ? fmtPct(currentNoReserve, totalDepth) : '50.0%';
      const marketSplitText = `${yesOutcomeLabel} ${yesSplit} / ${noOutcomeLabel} ${noSplit}`;
      const outcomeReservesText = `${yesOutcomeLabel}: ${fmtCollateral(currentYesReserve, collateralMeta)} | ${noOutcomeLabel}: ${fmtCollateral(currentNoReserve, collateralMeta)}`;
      const feePrefix = collateralMeta.usdPrice !== null ? '$' : '';
      const feeText = feeAmount > 0n ? `${feePrefix}${fmtCollateral(feeAmount, collateralMeta)} (${feeBps} bps)` : `${feePrefix}0 (${feeBps} bps)`;

      el.tradeExecutionInsights.innerHTML = renderPreviewShell(expectedText);
      setImpactTrigger(`
        <button type="button" data-role="impact-toggle" class="trade-preview-impact-pill ${impactClass}" style="color:${impactTone};" aria-expanded="${tradeImpactExpanded ? 'true' : 'false'}">
          <span class="trade-preview-impact-value">${impactText}</span>
          <span class="trade-preview-impact-caret ${tradeImpactExpanded ? 'is-open' : ''}" aria-hidden="true">▾</span>
        </button>
      `);

      if (tradeImpactExpanded) {
        setImpactPanel(`
          <div class="trade-preview-metrics-panel card">
            <div class="trade-preview-metric-row">
              <span class="mono trade-preview-metric-label">Average Price</span>
              <span class="trade-preview-metric-value">${effectivePriceText}</span>
            </div>
            <div class="trade-preview-metric-row">
              <span class="mono trade-preview-metric-label">Market Split</span>
              <span class="trade-preview-metric-value">${marketSplitText}</span>
            </div>
            <div class="trade-preview-metric-row">
              <span class="mono trade-preview-metric-label">Outcome Reserves</span>
              <span class="trade-preview-metric-value">${outcomeReservesText}</span>
            </div>
            <div class="trade-preview-metric-row">
              <span class="mono trade-preview-metric-label">Total Reserves</span>
              <span class="trade-preview-metric-value">${fmtCollateral(totalDepth, collateralMeta)} shares</span>
            </div>
            <div class="mono trade-preview-fee-footnote">Protocol Fee: ${feeText}</div>
          </div>
        `);
      } else {
        setImpactPanel('');
      }
    } catch {
      if (isStaleRequest()) return;

      try {
        const previewAmount = await readClient.readContract({
          address: marketAddress,
          abi: marketAbi,
          functionName: isBuy ? 'previewBuy' : 'previewSell',
          args: [isBuy ? sideYes : sideYes, amount],
        }) as bigint;

        if (isStaleRequest()) return;
        if (previewAmount <= 0n) throw new Error('zero preview');

        latestTradeImpactBps = null;
        tradeImpactToleranceBreached = false;
        el.tradeExecutionInsights.innerHTML = renderPreviewShell(
          isBuy ? fmtToken(previewAmount, outcomeDisplayDecimals(sideYes)) : fmtCollateral(previewAmount, collateralMeta),
        );
        tradeImpactExpanded = false;
        setImpactTrigger('');
        setImpactPanel('');
        return;
      } catch {
        // fall through to generic message below
      }

      latestTradeImpactBps = null;
      tradeImpactToleranceBreached = false;
      el.tradeExecutionInsights.innerHTML = renderPreviewShell(
        '-',
        'On-chain preview unavailable right now. Check RPC connectivity and retry.',
      );
      tradeImpactExpanded = false;
      setImpactTrigger('');
      setImpactPanel('');
    }
    } finally {
      endPriceRefresh();
    }
  }

  function isPanelActive(panelId: string): boolean {
    const panel = appRoot.querySelector(`#${panelId}`) as HTMLElement | null;
    return Boolean(panel && panel.classList.contains('active'));
  }

  function getMarketRefreshIntervalMs(): number {
    return document.hidden ? MARKET_REFRESH_HIDDEN_MS : MARKET_REFRESH_VISIBLE_MS;
  }

  function clearAutoRefreshTimers(): void {
    if (marketRefreshTimer !== null) {
      window.clearTimeout(marketRefreshTimer);
      marketRefreshTimer = null;
    }
    if (analyticsRefreshTimer !== null) {
      window.clearTimeout(analyticsRefreshTimer);
      analyticsRefreshTimer = null;
    }
    if (delayedMutationRefreshTimer !== null) {
      window.clearTimeout(delayedMutationRefreshTimer);
      delayedMutationRefreshTimer = null;
    }
  }

  async function refreshSafe(): Promise<void> {
    if (refreshInFlight) {
      await refreshInFlight;
      return;
    }

    const op = refresh();
    refreshInFlight = op;
    try {
      await op;
    } finally {
      if (refreshInFlight === op) {
        refreshInFlight = null;
      }
    }
  }

  function scheduleCoreAutoRefresh(nextDelayMs?: number): void {
    if (marketRefreshTimer !== null) {
      window.clearTimeout(marketRefreshTimer);
      marketRefreshTimer = null;
    }

    const waitMs = typeof nextDelayMs === 'number' ? Math.max(1_000, nextDelayMs) : getMarketRefreshIntervalMs();
    marketRefreshTimer = window.setTimeout(() => {
      void refreshSafe().finally(() => {
        scheduleCoreAutoRefresh();
      });
    }, waitMs);
  }

  function scheduleAnalyticsAutoRefresh(nextDelayMs?: number): void {
    if (!analyticsFetchEnabled || !analyticsUserActivated || !isPanelActive('analyticsPanel')) {
      if (analyticsRefreshTimer !== null) {
        window.clearTimeout(analyticsRefreshTimer);
        analyticsRefreshTimer = null;
      }
      return;
    }

    if (analyticsRefreshTimer !== null) {
      window.clearTimeout(analyticsRefreshTimer);
      analyticsRefreshTimer = null;
    }

    const waitMs = typeof nextDelayMs === 'number' ? Math.max(5_000, nextDelayMs) : ANALYTICS_REFRESH_VISIBLE_MS;
    analyticsRefreshTimer = window.setTimeout(() => {
      if (analyticsFetchEnabled && analyticsUserActivated && !document.hidden && isPanelActive('analyticsPanel')) {
        void ensureAnalyticsLoaded(true);
      }
      if (analyticsFetchEnabled && analyticsUserActivated && isPanelActive('analyticsPanel')) {
        scheduleAnalyticsAutoRefresh();
      }
    }, waitMs);
  }

  function schedulePostMutationRefreshes(): void {
    if (delayedMutationRefreshTimer !== null) {
      window.clearTimeout(delayedMutationRefreshTimer);
      delayedMutationRefreshTimer = null;
    }

    void refreshSafe();
    delayedMutationRefreshTimer = window.setTimeout(() => {
      void refreshSafe();
    }, POST_TX_DELAYED_REFRESH_MS);
  }

  async function refreshAfterMutation(): Promise<void> {
    resolutionLogsCache = null;
    await refreshSafe();
    if (delayedMutationRefreshTimer !== null) {
      window.clearTimeout(delayedMutationRefreshTimer);
      delayedMutationRefreshTimer = null;
    }
    delayedMutationRefreshTimer = window.setTimeout(() => {
      void refreshSafe();
    }, POST_TX_DELAYED_REFRESH_MS);
  }

  function handleVisibilityOrFocusChange(): void {
    scheduleCoreAutoRefresh();
    if (analyticsFetchEnabled && analyticsUserActivated && isPanelActive('analyticsPanel')) {
      scheduleAnalyticsAutoRefresh();
    }
  }

  function outcomeText(outcome: number): string {
    if (outcome === 1) return yesOutcomeLabel;
    if (outcome === 2) return noOutcomeLabel;
    return OUTCOME_LABEL[outcome] || '-';
  }


  function renderRiskPanel(summary: string, lines: string[], note: string, technical?: string): string {
    return `
      <div class="mono" style="font-size:.66rem; color:var(--muted); margin-bottom:8px;">RESOLUTION RISK / REPUTATION</div>
      <div style="display:grid; gap:8px;">
        <div class="resolution-summary">${summary}</div>
        <div class="resolution-pill-row">
          ${lines.map((line) => `<span class="resolution-pill mono">${line}</span>`).join('')}
        </div>
        <div class="mono resolution-note">${note}</div>
        ${technical ? `<div class="mono resolution-technical">${technical}</div>` : ''}
      </div>
    `;
  }

  function renderSummaryPills(items: Array<{ label: string; value: string; tone?: string }>): string {
    return `
      <div class="analytics-summary-strip">
        ${items.map((item) => `
          <div class="analytics-summary-chip" style="--tone:${item.tone || '#dff7ff'};">
            <div class="mono analytics-summary-label">${item.label}</div>
            <div class="analytics-summary-value">${item.value}</div>
          </div>
        `).join('')}
      </div>
    `;
  }

  function renderTopTrades(history: MarketHistoryPoint[], formatValue: (value: bigint) => string, limit = 5): string {
    if (!history.length) return '';
    const rowCount = Math.max(1, limit);

    const topYes = [...history]
      .filter((point) => point.yesBuyFlow > 0n || point.yesSellFlow > 0n)
      .map((point) => ({
        action: point.yesBuyFlow > 0n ? 'Buy' : 'Sell',
        volume: point.yesBuyFlow > 0n ? point.yesBuyFlow : point.yesSellFlow,
      }))
      .sort((a, b) => (a.volume > b.volume ? -1 : a.volume < b.volume ? 1 : 0))
      .slice(0, Math.max(1, limit));

    const topNo = [...history]
      .filter((point) => point.noBuyFlow > 0n || point.noSellFlow > 0n)
      .map((point) => ({
        action: point.noBuyFlow > 0n ? 'Buy' : 'Sell',
        volume: point.noBuyFlow > 0n ? point.noBuyFlow : point.noSellFlow,
      }))
      .sort((a, b) => (a.volume > b.volume ? -1 : a.volume < b.volume ? 1 : 0))
      .slice(0, rowCount);

    const maxYes = topYes.reduce((acc, item) => (item.volume > acc ? item.volume : acc), 1n);
    const maxNo = topNo.reduce((acc, item) => (item.volume > acc ? item.volume : acc), 1n);
    const yesRowsData = Array.from({ length: rowCount }, (_, index) => topYes[index] ?? { action: '', volume: 0n });
    const noRowsData = Array.from({ length: rowCount }, (_, index) => topNo[index] ?? { action: '', volume: 0n });

    const yesRows = yesRowsData.map((item, index) => {
      const widthPct = item.volume > 0n ? Math.max(8, Math.round((Number(item.volume) / Number(maxYes)) * 100)) : 0;
      const label = item.volume > 0n
        ? `<span class="analytics-top-trade-action-word ${item.action === 'Sell' ? 'is-sell' : 'is-buy'}"><span class="analytics-top-trade-action-text">${item.action}</span></span> <span class="analytics-top-trade-amount">${formatValue(item.volume)}</span>`
        : '--';
      const dotClass = item.action === 'Sell' ? 'is-sell' : 'is-buy';
      return `
        <div class="analytics-top-trade-side-row is-yes">
          <div class="analytics-top-trade-track is-yes">
            <div class="analytics-top-trade-fill is-yes" style="width:${widthPct}%"></div>
            <div class="mono analytics-top-trade-inline-label is-yes"><span class="analytics-top-trade-dot ${dotClass}" aria-hidden="true"></span>${label}</div>
          </div>
        </div>
      `;
    }).join('');

    const noRows = noRowsData.map((item, index) => {
      const widthPct = item.volume > 0n ? Math.max(8, Math.round((Number(item.volume) / Number(maxNo)) * 100)) : 0;
      const label = item.volume > 0n
        ? `<span class="analytics-top-trade-action-word ${item.action === 'Sell' ? 'is-sell' : 'is-buy'}"><span class="analytics-top-trade-action-text">${item.action}</span></span> <span class="analytics-top-trade-amount">${formatValue(item.volume)}</span>`
        : '--';
      const dotClass = item.action === 'Sell' ? 'is-sell' : 'is-buy';
      return `
        <div class="analytics-top-trade-side-row is-no">
          <div class="analytics-top-trade-track is-no">
            <div class="analytics-top-trade-fill is-no" style="width:${widthPct}%"></div>
            <div class="mono analytics-top-trade-inline-label is-no"><span class="analytics-top-trade-dot ${dotClass}" aria-hidden="true"></span>${label}</div>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="analytics-top-trades analytics-top-trades-mirror" aria-label="Top outcome trades">
        <div class="analytics-top-trade-side is-yes">${yesRows}</div>
        <div class="analytics-top-trade-side is-no">${noRows}</div>
      </div>
    `;
  }

  async function resolveBlockTimeLabel(blockNumber: bigint): Promise<string> {
    const cacheKey = `blockts:${getActiveNetworkKey()}:${blockNumber.toString()}`;
    const cached = getCache<string>(cacheKey, 31_536_000_000);
    if (cached) return cached;

    const block = await readClient.getBlock({ blockNumber });
    const label = new Date(Number(block.timestamp) * 1000).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    setCache(cacheKey, label);
    return label;
  }

  function updateAnalyticsRangeButtons(): void {
    const buttons = [
      { button: el.analyticsRangeShort, active: analyticsRange === '12h' },
      { button: el.analyticsRangeMedium, active: analyticsRange === '24h' },
      { button: el.analyticsRangeFull, active: analyticsRange === '7d' },
    ];

    for (const item of buttons) {
      item.button.classList.toggle('btn-primary', item.active);
      item.button.classList.toggle('btn', true);
      if (!item.active) item.button.classList.remove('btn-primary');
    }
  }

  async function refreshAnalyticsHistory(): Promise<void> {
    if (!analyticsFetchEnabled || !analyticsUserActivated || !isPanelActive('analyticsPanel')) return;
    try {
      updateAnalyticsRangeButtons();
      const history = await getMarketHistory(marketAddress, currentYesReserve, currentNoReserve, marketCollateralAddress);
      if (history.length === 0) {
        el.analyticsProbabilityHistory.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">No trade history yet for this market.</div>';
        el.analyticsFlowHistory.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">Window net chart appears after the first buy or sell.</div>';
        el.analyticsFlowStackedHistory.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">Buy vs sell activity chart appears after the first trade.</div>';
        return;
      }

      const now = Date.now();
      const windowMs = analyticsRange === '12h'
        ? 12 * 60 * 60 * 1000
        : analyticsRange === '24h'
          ? 24 * 60 * 60 * 1000
          : 7 * 24 * 60 * 60 * 1000;
      const windowStart = now - windowMs;
      const visibleHistory = history.filter((point) => point.timestampMs >= windowStart);

      if (visibleHistory.length === 0) {
        const label = analyticsRange === '12h' ? '12 hours' : analyticsRange === '24h' ? '24 hours' : '7 days';
        el.analyticsProbabilityHistory.innerHTML = `<div class="mono" style="font-size:.72rem; color:var(--muted);">No trades in the last ${label}.</div>`;
        el.analyticsFlowHistory.innerHTML = `<div class="mono" style="font-size:.72rem; color:var(--muted);">No window net data in the last ${label}.</div>`;
        el.analyticsFlowStackedHistory.innerHTML = `<div class="mono" style="font-size:.72rem; color:var(--muted);">No buy/sell activity in the last ${label}.</div>`;
        return;
      }

      const buckets = await analyticsBuckets.getLabeledBucketsForRange(analyticsRange, visibleHistory);
      const recentWindow = buckets.slice(-Math.min(4, buckets.length));
      const recentNetFlow = recentWindow.reduce((acc, point) => acc + point.netFlow, 0n);
      const recentTone = recentNetFlow >= 0n ? 'var(--green)' : 'var(--red)';
      const latestPoint = buckets[buckets.length - 1];
      const firstPoint = buckets[0];
      const latestYesProbability = typeof latestPoint?.yesProbability === 'number' && Number.isFinite(latestPoint.yesProbability) ? latestPoint.yesProbability : 50;
      const latestNoProbability = typeof latestPoint?.noProbability === 'number' && Number.isFinite(latestPoint.noProbability) ? latestPoint.noProbability : 50;
      const firstYesProbability = typeof firstPoint?.yesProbability === 'number' && Number.isFinite(firstPoint.yesProbability) ? firstPoint.yesProbability : latestYesProbability;
      const splitMove = latestYesProbability - firstYesProbability;
      const splitMoveLabel = `${splitMove >= 0 ? '+' : ''}${splitMove.toFixed(2)} pts`;
      const momentumLabel = recentNetFlow > 0n ? yesOutcomeLabel : recentNetFlow < 0n ? noOutcomeLabel : 'Balanced';

      const windowFlowSeries = [{
        label: analyticsRange,
        yesBuyFlow: visibleHistory.reduce((acc, point) => acc + point.yesBuyFlow, 0n),
        yesSellFlow: visibleHistory.reduce((acc, point) => acc + point.yesSellFlow, 0n),
        noBuyFlow: visibleHistory.reduce((acc, point) => acc + point.noBuyFlow, 0n),
        noSellFlow: visibleHistory.reduce((acc, point) => acc + point.noSellFlow, 0n),
      }];
      const selectedWindowFlow = windowFlowSeries[0];
      const totalBuyFlow = selectedWindowFlow.yesBuyFlow + selectedWindowFlow.noBuyFlow;
      const totalSellFlow = selectedWindowFlow.yesSellFlow + selectedWindowFlow.noSellFlow;
      const totalNetFlow = totalBuyFlow - totalSellFlow;
      const totalVolume = totalBuyFlow + totalSellFlow;
      const traderVolumeByAddress = new Map<string, bigint>();
      for (const point of visibleHistory) {
        const trader = String(point.trader || '').toLowerCase();
        if (!trader || !point.tradeVolume) continue;
        traderVolumeByAddress.set(trader, (traderVolumeByAddress.get(trader) ?? 0n) + point.tradeVolume);
      }
      const sortedTraderVolume = Array.from(traderVolumeByAddress.values()).sort((a, b) => (a > b ? -1 : a < b ? 1 : 0));
      const topTraderVolume = sortedTraderVolume[0] ?? 0n;
      const top3Volume = sortedTraderVolume.slice(0, 3).reduce((acc, value) => acc + value, 0n);
      const topTraderShare = totalVolume > 0n ? Number((topTraderVolume * 10_000n) / totalVolume) / 100 : 0;
      const top3Share = totalVolume > 0n ? Number((top3Volume * 10_000n) / totalVolume) / 100 : 0;
      const concentrationTone = topTraderShare >= 45 ? '#ff9b9b' : topTraderShare >= 30 ? '#f6c66e' : '#97e6bf';

      const tvlBalance = await readClient.readContract({
        address: marketCollateralAddress as Address,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [marketAddress as Address],
      });
      const currentTvl = (tvlBalance as bigint) || 0n;
      const flowTone = totalNetFlow >= 0n ? 'var(--green)' : 'var(--red)';
      const yesWindowNet = selectedWindowFlow.yesBuyFlow - selectedWindowFlow.yesSellFlow;
      const noWindowNet = selectedWindowFlow.noBuyFlow - selectedWindowFlow.noSellFlow;
      const formatAnalyticsValue = (value: bigint): string => {
        const usd = fmtCollateralUsd(value, collateralMeta);
        if (usd !== null) return `$${usd}`;
        return `${fmtCollateral(value, collateralMeta)} ${collateralMeta.symbol}`;
      };
      const formatAnalyticsSigned = (value: bigint): string => {
        const abs = value < 0n ? -value : value;
        return `${value >= 0n ? '+' : '-'}${formatAnalyticsValue(abs)}`;
      };

      const summaryStrip = renderSummaryPills([
        { label: 'Volume', value: formatAnalyticsValue(totalVolume), tone: '#8ec9ff' },
        { label: 'TVL', value: formatAnalyticsValue(currentTvl), tone: '#f6c66e' },
        { label: 'Net', value: `${totalNetFlow >= 0n ? '+' : '-'}${formatAnalyticsValue(totalNetFlow < 0n ? -totalNetFlow : totalNetFlow)}`, tone: flowTone },
        { label: 'Top Trader', value: `${topTraderShare.toFixed(1)}%`, tone: concentrationTone },
        { label: 'Top 3 Share', value: `${top3Share.toFixed(1)}%`, tone: concentrationTone },
        { label: 'Trend', value: momentumLabel, tone: recentTone },
        { label: 'Move', value: splitMoveLabel, tone: splitMove >= 0 ? 'var(--green)' : 'var(--red)' },
      ]);
      const topTradesMarkup = renderTopTrades(visibleHistory, formatAnalyticsValue, 5);
      const yesIsTrending = latestYesProbability >= latestNoProbability;
      const yesOutcomeTone = yesIsTrending ? '#2ec989' : '#e8637a';
      const noOutcomeTone = yesIsTrending ? '#e8637a' : '#2ec989';

      el.analyticsProbabilityHistory.innerHTML = `
        ${summaryStrip}
        <div class="mono analytics-card-copy">Latest split: <span style="color:${yesOutcomeTone}">${latestYesProbability.toFixed(2)}% ${yesOutcomeLabel}</span> / <span style="color:${noOutcomeTone}">${latestNoProbability.toFixed(2)}% ${noOutcomeLabel}</span></div>
        ${renderFlowSplitSvg(buckets.map((point) => ({ label: point.label, yesBuyFlow: point.yesBuyFlow, noBuyFlow: point.noBuyFlow, yesSellFlow: point.yesSellFlow, noSellFlow: point.noSellFlow })), fmtToken)}
      `;

      el.analyticsFlowHistory.innerHTML = `
        <div class="mono analytics-card-copy">${analyticsRange} window net: <span style="color:${yesOutcomeTone}">${yesOutcomeLabel} ${formatAnalyticsSigned(yesWindowNet)}</span> / <span style="color:${noOutcomeTone}">${noOutcomeLabel} ${formatAnalyticsSigned(noWindowNet)}</span></div>
        ${renderBarHistorySvg(windowFlowSeries, { yesOutcomeLabel, noOutcomeLabel, formatValue: formatAnalyticsValue, formatSigned: formatAnalyticsSigned, yesIsTrending })}
      `;

      el.analyticsFlowStackedHistory.innerHTML = `
        <div class="mono analytics-card-copy">${analyticsRange} buy/sell activity: <span style="color:${yesOutcomeTone}">${yesOutcomeLabel}</span> and <span style="color:${noOutcomeTone}">${noOutcomeLabel}</span>.</div>
        ${renderStackedFlowSvg(windowFlowSeries, { yesOutcomeLabel, noOutcomeLabel, formatValue: formatAnalyticsValue, formatSigned: formatAnalyticsSigned, yesIsTrending })}
        ${topTradesMarkup}
      `;
    } catch {
      el.analyticsProbabilityHistory.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">Probability history unavailable right now.</div>';
      el.analyticsFlowHistory.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">Window net history unavailable right now.</div>';
      el.analyticsFlowStackedHistory.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">Buy/sell history unavailable right now.</div>';
    }
  }

  async function ensureAnalyticsLoaded(force = false): Promise<void> {
    if (!analyticsFetchEnabled || !analyticsUserActivated || !isPanelActive('analyticsPanel')) return;
    if (!force && analyticsLoaded && !analyticsDirty) return;
    if (analyticsLoading) {
      await analyticsLoading;
      return;
    }

    analyticsLoading = (async () => {
      await refreshAnalyticsHistory();
      analyticsLoaded = true;
      analyticsDirty = false;
    })();

    try {
      await analyticsLoading;
    } finally {
      analyticsLoading = null;
    }
  }

  async function confirmResolutionAction(title: string, lines: string[], confirmLabel: string): Promise<boolean> {
    return await new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'confirm-modal-overlay';
      overlay.innerHTML = `
        <div class="confirm-modal-card card" role="dialog" aria-modal="true" aria-label="${title}">
          <div class="confirm-modal-title">${title}</div>
          <div class="confirm-modal-body">${lines.map((line) => `<div>${line}</div>`).join('')}</div>
          <div class="confirm-modal-actions">
            <button type="button" class="btn" data-action="cancel">Cancel</button>
            <button type="button" class="btn btn-red" data-action="confirm">${confirmLabel}</button>
          </div>
        </div>
      `;

      const cleanup = (value: boolean) => {
        overlay.remove();
        resolve(value);
      };

      overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', () => cleanup(false));
      overlay.querySelector('[data-action="confirm"]')?.addEventListener('click', () => cleanup(true));
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) cleanup(false);
      });

      document.body.appendChild(overlay);
    });
  }

  async function runTxConfirmationModal(config: {
    title: string;
    bodyLines: string[];
    submitLabel: string;
    submitButtonClass?: string;
    submitAction: () => Promise<void>;
    approval?: {
      label: string;
      checkNeeded: () => Promise<boolean>;
      approveAction: () => Promise<void>;
    };
  }): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const overlay = document.createElement('div');
      overlay.className = 'confirm-modal-overlay';
      overlay.innerHTML = `
        <div class="confirm-modal-card card" role="dialog" aria-modal="true" aria-label="${config.title}">
          <div class="confirm-modal-title">${config.title}</div>
          <div class="confirm-modal-body" style="display:grid; gap:10px;">
            <div style="display:grid; gap:6px;">${config.bodyLines.map((line) => `<div>${line}</div>`).join('')}</div>
            <div id="txFlowStatus" class="tx-flow-status">Checking requirements...</div>
          </div>
          <div class="confirm-modal-actions">
            <button type="button" class="btn" data-action="cancel">Cancel</button>
            ${config.approval ? `<button type="button" class="btn btn-amber" data-action="approve">${config.approval.label}</button>` : ''}
            <button type="button" class="btn ${config.submitButtonClass || 'btn-primary'}" data-action="submit">${config.submitLabel}</button>
          </div>
        </div>
      `;

      document.body.appendChild(overlay);

      const statusEl = overlay.querySelector('#txFlowStatus') as HTMLDivElement;
      const cancelBtn = overlay.querySelector('[data-action="cancel"]') as HTMLButtonElement;
      const approveBtn = overlay.querySelector('[data-action="approve"]') as HTMLButtonElement | null;
      const submitBtn = overlay.querySelector('[data-action="submit"]') as HTMLButtonElement;

      let approvalStillNeeded = false;

      const cleanup = () => {
        overlay.remove();
      };

      const setBusy = (button: HTMLButtonElement, busy: boolean, idleLabel: string) => {
        button.disabled = busy;
        button.innerHTML = busy
          ? `<span class="tx-btn-spinner" aria-hidden="true"></span>${idleLabel}`
          : idleLabel;
      };

      const fail = (error: any) => {
        cleanup();
        reject(error);
      };

      const refreshApprovalState = async () => {
        if (!config.approval) {
          submitBtn.disabled = false;
          statusEl.textContent = 'Ready to submit.';
          return;
        }

        approvalStillNeeded = await config.approval.checkNeeded();
        if (approvalStillNeeded) {
          submitBtn.disabled = true;
          statusEl.textContent = 'Approval required before submit.';
          if (approveBtn) {
            approveBtn.style.display = '';
            approveBtn.disabled = false;
          }
          return;
        }

        submitBtn.disabled = false;
        statusEl.textContent = 'Approval satisfied. Ready to submit.';
        if (approveBtn) {
          approveBtn.style.display = 'none';
        }
      };

      cancelBtn.addEventListener('click', () => {
        cleanup();
        reject(new Error('Transaction cancelled.'));
      });

      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
          cleanup();
          reject(new Error('Transaction cancelled.'));
        }
      });

      approveBtn?.addEventListener('click', async () => {
        if (!config.approval) return;
        try {
          setBusy(approveBtn, true, config.approval.label);
          submitBtn.disabled = true;
          statusEl.textContent = 'Sending approval transaction...';
          await config.approval.approveAction();
          setBusy(approveBtn, false, config.approval.label);
          await refreshApprovalState();
        } catch (error: any) {
          setBusy(approveBtn, false, config.approval.label);
          fail(error);
        }
      });

      submitBtn.addEventListener('click', async () => {
        try {
          if (config.approval && approvalStillNeeded) {
            statusEl.textContent = 'Approval must be completed first.';
            return;
          }
          setBusy(submitBtn, true, config.submitLabel);
          cancelBtn.disabled = true;
          if (approveBtn) approveBtn.disabled = true;
          statusEl.textContent = 'Submitting transaction...';
          await config.submitAction();
          schedulePostMutationRefreshes();
          cleanup();
          resolve();
        } catch (error: any) {
          fail(error);
        }
      });

      refreshApprovalState().catch(fail);
    });
  }

  function normalizeSalt(raw: string): `0x${string}` {
    const value = raw.trim();
    if (!value) throw new Error('Secret is required');
    if (/^0x[0-9a-fA-F]{64}$/.test(value)) return value as `0x${string}`;
    return keccak256(toUtf8Bytes(value)) as `0x${string}`;
  }

  function jurorSaltStorageKey(): string {
    const account = (walletState.account || 'anon').toLowerCase();
    return `boxbet:juror-secret-session:${marketAddress.toLowerCase()}:${account}`;
  }

  function generateRandomSalt(): `0x${string}` {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    return `0x${hex}` as `0x${string}`;
  }

  function saveJurorSaltInSession(): void {
    const value = el.jurorSalt.value.trim();
    if (!value) throw new Error('Secret is empty');
    sessionStorage.setItem(jurorSaltStorageKey(), value);
  }

  function loadJurorSaltFromSession(): string | null {
    return sessionStorage.getItem(jurorSaltStorageKey());
  }

  function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  function base64ToArrayBuffer(value: string): ArrayBuffer {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  async function promptBackupPassphrase(): Promise<string | null> {
    return await new Promise((resolve) => {
      const title = 'Encrypt Secret Backup';
      const overlay = document.createElement('div');
      overlay.className = 'confirm-modal-overlay';
      overlay.innerHTML = `
        <div class="confirm-modal-card card" role="dialog" aria-modal="true" aria-label="${title}">
          <div class="confirm-modal-title">${title}</div>
          <div class="confirm-modal-body" style="display:grid; gap:8px;">
            <div>Choose a passphrase to encrypt the downloaded backup file.</div>
            <input id="backupPassphrase" class="btn" type="password" style="text-align:left; background:#061320" placeholder="Passphrase (min 10 characters)" autocomplete="new-password" />
            <input id="backupPassphraseConfirm" class="btn" type="password" style="text-align:left; background:#061320" placeholder="Confirm passphrase" autocomplete="new-password" />
            <div class="mono" style="font-size:.62rem; color:var(--muted); line-height:1.45;">This passphrase is not stored. You must remember it to decrypt your backup later.</div>
          </div>
          <div class="confirm-modal-actions">
            <button type="button" class="btn" data-action="cancel">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="confirm">Encrypt + Download</button>
          </div>
        </div>
      `;

      const cleanup = (value: string | null) => {
        overlay.remove();
        resolve(value);
      };

      document.body.appendChild(overlay);
      const passInput = overlay.querySelector('#backupPassphrase') as HTMLInputElement | null;
      const confirmInput = overlay.querySelector('#backupPassphraseConfirm') as HTMLInputElement | null;
      passInput?.focus();

      overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', () => cleanup(null));
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) cleanup(null);
      });

      const submit = () => {
        const passphrase = passInput?.value?.trim() || '';
        const confirm = confirmInput?.value?.trim() || '';
        if (!passphrase) {
          cleanup(null);
          return;
        }
        if (passphrase.length < 10) {
          alert('Passphrase must be at least 10 characters.');
          return;
        }
        if (passphrase !== confirm) {
          alert('Passphrases do not match.');
          return;
        }
        cleanup(passphrase);
      };

      overlay.querySelector('[data-action="confirm"]')?.addEventListener('click', submit);
      confirmInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        }
      });
    });
  }

  async function promptBackupUnlockPassphrase(): Promise<string | null> {
    return await new Promise((resolve) => {
      const title = 'Decrypt Secret Backup';
      const overlay = document.createElement('div');
      overlay.className = 'confirm-modal-overlay';
      overlay.innerHTML = `
        <div class="confirm-modal-card card" role="dialog" aria-modal="true" aria-label="${title}">
          <div class="confirm-modal-title">${title}</div>
          <div class="confirm-modal-body" style="display:grid; gap:8px;">
            <div>Enter your backup passphrase to restore the Secret.</div>
            <input id="backupUnlockPassphrase" class="btn" type="password" style="text-align:left; background:#061320" placeholder="Passphrase" autocomplete="off" />
            <div class="mono" style="font-size:.62rem; color:var(--muted); line-height:1.45;">Your passphrase is used only in this browser session and is never stored.</div>
          </div>
          <div class="confirm-modal-actions">
            <button type="button" class="btn" data-action="cancel">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="confirm">Decrypt + Restore</button>
          </div>
        </div>
      `;

      const cleanup = (value: string | null) => {
        overlay.remove();
        resolve(value);
      };

      document.body.appendChild(overlay);
      const passInput = overlay.querySelector('#backupUnlockPassphrase') as HTMLInputElement | null;
      passInput?.focus();

      overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', () => cleanup(null));
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) cleanup(null);
      });

      const submit = () => {
        const passphrase = passInput?.value?.trim() || '';
        if (!passphrase) {
          alert('Passphrase is required.');
          return;
        }
        cleanup(passphrase);
      };

      overlay.querySelector('[data-action="confirm"]')?.addEventListener('click', submit);
      passInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        }
      });
    });
  }

  async function encryptSecretForBackup(secretValue: string, passphrase: string): Promise<Record<string, unknown>> {
    const encoder = new TextEncoder();
    const kdfSalt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: kdfSalt,
        iterations: 250000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );

    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, derivedKey, encoder.encode(secretValue));
    return {
      version: 2,
      type: 'boxbet-juror-secret-backup',
      format: 'encrypted',
      algorithm: 'AES-GCM-256',
      kdf: {
        name: 'PBKDF2-SHA256',
        iterations: 250000,
        salt: bytesToBase64(kdfSalt),
      },
      iv: bytesToBase64(iv),
      ciphertext: bytesToBase64(new Uint8Array(encrypted)),
    };
  }

  async function downloadJurorSecretBackup(secretValue: string): Promise<void> {
    const account = (walletState.account || 'anon').toLowerCase();
    const safeAccount = account.replace(/[^a-z0-9]/g, '').slice(0, 12) || 'anon';
    const safeMarket = marketAddress.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12) || 'market';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const passphrase = await promptBackupPassphrase();
    if (!passphrase) throw new Error('Backup export cancelled');
    const encryptedPayload = await encryptSecretForBackup(secretValue, passphrase);
    const payload = {
      ...encryptedPayload,
      type: 'boxbet-juror-secret-backup',
      market: marketAddress,
      account: walletState.account || null,
      createdAt: new Date().toISOString(),
      note: 'Encrypted backup. Keep this file and passphrase safe. Passphrase is not recoverable.',
    };
    const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `boxbet-secret-${safeMarket}-${safeAccount}-${timestamp}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  async function chooseBackupFileText(): Promise<string | null> {
    return await new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/json,.json';
      input.style.display = 'none';
      document.body.appendChild(input);
      let settled = false;

      const cleanup = () => {
        input.remove();
      };

      const finish = (value: string | null) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(value);
      };

      input.addEventListener('change', async () => {
        try {
          const file = input.files?.[0];
          if (!file) {
            finish(null);
            return;
          }
          const text = await file.text();
          finish(text);
        } catch {
          finish(null);
        }
      }, { once: true });

      input.addEventListener('cancel', () => {
        finish(null);
      }, { once: true });

      input.click();
    });
  }

  async function decryptSecretFromBackupPayload(payload: any, passphrase: string): Promise<string> {
    const kdf = payload?.kdf;
    if (payload?.format !== 'encrypted') throw new Error('Backup file is not encrypted format');
    if (!kdf || kdf?.name !== 'PBKDF2-SHA256') throw new Error('Unsupported backup KDF');
    if (typeof payload?.ciphertext !== 'string' || typeof payload?.iv !== 'string' || typeof kdf?.salt !== 'string') {
      throw new Error('Backup file is missing encryption fields');
    }

    const iterations = Number(kdf?.iterations || 0);
    if (!Number.isFinite(iterations) || iterations < 100000 || iterations > 1000000) {
      throw new Error('Unsupported backup iteration count');
    }

    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: base64ToArrayBuffer(kdf.salt),
        iterations,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );

    try {
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: base64ToArrayBuffer(payload.iv) },
        key,
        base64ToArrayBuffer(payload.ciphertext)
      );
      return decoder.decode(decrypted).trim();
    } catch {
      throw new Error('Invalid passphrase or corrupted backup file');
    }
  }

  function selectedJurorOutcome(): number {
    return Number(el.jurorOutcome.value || '1');
  }

  function setAppealAvailability(enabled: boolean, reason = ''): void {
    el.appealReason.disabled = !enabled;
    el.jurorAppealBtn.disabled = !enabled;
    el.appealReasonBlock.hidden = !enabled;
    el.appealActionRow.hidden = !enabled;

    if (enabled) {
      el.appealUnavailableHint.hidden = true;
      el.appealUnavailableHint.textContent = '';
      return;
    }

    el.appealUnavailableHint.hidden = false;
    el.appealUnavailableHint.textContent = reason || 'Appeals are unavailable for this deployment.';
  }

  function computeJurorCommitment(marketId: string, roundIndex: bigint, account: Address, outcome: number, salt: `0x${string}`): `0x${string}` {
    const encoded = AbiCoder.defaultAbiCoder().encode(['bytes32', 'uint256', 'address', 'uint8', 'bytes32'], [marketId, roundIndex, account, outcome, salt]);
    return keccak256(encoded) as `0x${string}`;
  }

  async function getCourtOracleAddress(): Promise<Address | null> {
    try {
      const address = (await readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'escalationOracle',
      })) as Address;
      if (!address || /^0x0{40}$/i.test(address)) return null;
      return address;
    } catch {
      const fallbackOracle = DEPLOYMENT.oracle as Address;
      if (!fallbackOracle || /^0x0{40}$/i.test(fallbackOracle)) return null;
      return fallbackOracle;
    }
  }

  function courtOracle(address: Address): Contract {
    return contractWithSigner(address, courtOracleAbiEthers);
  }

  async function refreshCourtJurorStatus(
    marketId: string,
    nowTs: number,
    context?: {
      disputed: boolean;
      disputeDeadline: number;
      escalationDeadline: number;
      emergencyDeadline: number;
    }
  ): Promise<void> {
    const oracleAddress = await getCourtOracleAddress();
    if (!oracleAddress) {
      el.courtStatus.textContent = 'Court oracle unavailable.';
      el.jurorUserStakeStats.textContent = 'Unavailable';
      el.jurorRoundStakeStats.textContent = 'Unavailable';
      el.appealCostHint.textContent = 'Appeal cost: unavailable (oracle unavailable)';
      setAppealAvailability(false, 'Appeals are unavailable because this market deployment has no court oracle.');
      return;
    }

    setAppealAvailability(true);

    await refreshCourtStakeAssetMeta(oracleAddress);

    const [roundRead, minimumStakeRead] = await Promise.allSettled([
      readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'getCurrentRound',
        args: [marketId as `0x${string}`],
      }),
      readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'minimumStake',
      }),
    ]);

    if (roundRead.status !== 'fulfilled') {
      let myStake = 0n;
      let myStakeRewards = 0n;
      let myAppealRewards = 0n;
      const minimumStake = minimumStakeRead.status === 'fulfilled' ? (minimumStakeRead.value as bigint) : 0n;

      if (walletState.account) {
        const [stakeRead, rewardRead] = await Promise.allSettled([
          readClient.readContract({
            address: oracleAddress,
            abi: courtOracleAbi,
            functionName: 'stakes',
            args: [walletState.account as Address],
          }),
          readClient.readContract({
            address: oracleAddress,
            abi: courtOracleAbi,
            functionName: 'getJurorRewards',
            args: [walletState.account as Address],
          }),
        ]);

        if (stakeRead.status === 'fulfilled') myStake = stakeRead.value as bigint;
        if (rewardRead.status === 'fulfilled') {
          const rewardRow = rewardRead.value as [bigint, bigint];
          myStakeRewards = rewardRow[0];
          myAppealRewards = rewardRow[1];
        }
      }

      const waitingForCourt = Boolean(context?.disputed && context.disputeDeadline > nowTs);
      const blockedReason = waitingForCourt
        ? `Dispute window is still open (${fmtTs(context!.disputeDeadline, nowTs)}). Court round opens when dispute window closes and Finalize is called.`
        : 'No active court case yet. If disputed, click Finalize once dispute window closes to open/progress court rounds.';

      el.courtStatus.innerHTML = [
        `Oracle ${shortAddr(oracleAddress)} <span class="help-tip" title="${oracleAddress}">i</span> · No active case`,
        `Status <span class="help-tip" title="${escapeHtml(blockedReason)}">i</span>`,
        'Commit/reveal requires being selected once a court round opens.',
      ].join('<br/>');
      prepHelpTips(el.courtStatus);
      el.jurorUserStakeStats.innerHTML = [
        `Your Stake ${walletState.account ? fmtCourtStakeToken(myStake) : 'connect'} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
        `Stake Rewards ${walletState.account ? fmtCourtStakeToken(myStakeRewards) : 'connect'} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
        `Appeal Rewards ${walletState.account ? fmtCourtAppealToken(myAppealRewards) : 'connect'} ${tokenLabelHtml(courtAppealAssetSymbol)}`,
        `Min Required ${fmtCourtStakeToken(minimumStake)} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
        `Eligibility ${walletState.account ? (myStake >= minimumStake ? 'eligible' : 'below minimum') : 'connect'}`,
      ].join('<br/>');
      el.jurorRoundStakeStats.innerHTML = [
        `Panel Jurors n/a`,
        `Total Staked n/a`,
        `Avg/Juror n/a`,
      ].join('<br/>');
      el.appealCostHint.textContent = 'Your appeal cost now: unavailable (no active court round)';
      el.jurorCommitBtn.disabled = true;
      el.jurorRevealBtn.disabled = true;
      el.jurorAppealBtn.disabled = true;
      el.appealReason.disabled = true;
      el.jurorCommitPreview.textContent = 'Commitment preview: n/a';
      return;
    }

    const roundIndex = roundRead.value as bigint;
    const minimumStake = minimumStakeRead.status === 'fulfilled' ? (minimumStakeRead.value as bigint) : 0n;

    const [roundData, selectedJurors, appealBondAmount] = await Promise.all([
      readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'getRoundData',
        args: [marketId as `0x${string}`, roundIndex],
      }),
      readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'getSelectedJurors',
        args: [marketId as `0x${string}`],
      }),
      readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'getAppealBondAmount',
        args: [roundIndex],
      }),
    ]);

    const roundRow = roundData as [boolean, boolean, number, bigint, bigint, number];
    const selected = (selectedJurors as Address[]) || [];
    const appealBond = appealBondAmount as bigint;
    const commitDeadline = Number(roundRow[3] || 0n);
    const revealDeadline = Number(roundRow[4] || 0n);
    const inPanel = walletState.account ? selected.some((address) => address.toLowerCase() === walletState.account!.toLowerCase()) : false;
    const inCommitPhase = nowTs < commitDeadline;
    const inRevealPhase = nowTs >= commitDeadline && nowTs < revealDeadline;
    el.jurorCommitBtn.disabled = !walletState.account || !inPanel || !inCommitPhase;
    el.jurorRevealBtn.disabled = !walletState.account || !inPanel || !inRevealPhase;
    el.jurorAppealBtn.disabled = false;
    el.appealReason.disabled = false;

    const panelStakeReads = await Promise.allSettled(
      selected.map((juror) => readClient.readContract({
        address: oracleAddress,
        abi: courtOracleAbi,
        functionName: 'stakes',
        args: [juror as Address],
      }))
    );
    let panelTotalStaked = 0n;
    for (const row of panelStakeReads) {
      if (row.status === 'fulfilled') panelTotalStaked += row.value as bigint;
    }

    let myStake = 0n;
    let myStakeRewards = 0n;
    let myAppealRewards = 0n;
    let myVoteSummary = 'n/a';
    if (walletState.account) {
      const [stakeRead, rewardRead, voteRead] = await Promise.allSettled([
        readClient.readContract({
          address: oracleAddress,
          abi: courtOracleAbi,
          functionName: 'stakes',
          args: [walletState.account as Address],
        }),
        readClient.readContract({
          address: oracleAddress,
          abi: courtOracleAbi,
          functionName: 'getJurorRewards',
          args: [walletState.account as Address],
        }),
        readClient.readContract({
          address: oracleAddress,
          abi: courtOracleAbi,
          functionName: 'getVote',
          args: [marketId as `0x${string}`, roundIndex, walletState.account as Address],
        }),
      ]);

      if (stakeRead.status === 'fulfilled') myStake = stakeRead.value as bigint;
      if (rewardRead.status === 'fulfilled') {
        const rewardRow = rewardRead.value as [bigint, bigint];
        myStakeRewards = rewardRow[0];
        myAppealRewards = rewardRow[1];
      }
      if (voteRead.status === 'fulfilled') {
        const voteRow = voteRead.value as [`0x${string}`, boolean, boolean, number];
        myVoteSummary = `Committed ${voteRow[1] ? yesOutcomeLabel : noOutcomeLabel} · Revealed ${voteRow[2] ? outcomeText(Number(voteRow[3])) : noOutcomeLabel}`;
      }

      try {
        const salt = normalizeSalt(el.jurorSalt.value || 'boxbet-default-salt');
        const commitment = computeJurorCommitment(marketId, roundIndex, walletState.account as Address, selectedJurorOutcome(), salt);
        el.jurorCommitPreview.textContent = `Commitment preview: ${commitment}`;
      } catch {
        el.jurorCommitPreview.textContent = 'Commitment preview: invalid salt';
      }
    } else {
      el.jurorCommitPreview.textContent = 'Commitment preview: connect wallet';
    }

    const eligible = myStake >= minimumStake;
    const avgPanelStake = selected.length > 0 ? panelTotalStaked / BigInt(selected.length) : 0n;
    const mySharePct = panelTotalStaked > 0n ? Number((myStake * 10_000n) / panelTotalStaked) / 100 : 0;

    if (walletState.account && courtAppealAssetAddress) {
      let walletAppealBalance = 0n;
      try {
        walletAppealBalance = (await readClient.readContract({
          address: courtAppealAssetAddress,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [walletState.account as Address],
        })) as bigint;
      } catch {
        walletAppealBalance = 0n;
      }

      const shortfall = appealBond > walletAppealBalance ? appealBond - walletAppealBalance : 0n;
      el.appealCostHint.innerHTML =
        `Your appeal cost now: ${fmtCourtAppealToken(appealBond)} ${tokenLabelHtml(courtAppealAssetSymbol)} · ` +
        `Wallet: ${fmtCourtAppealToken(walletAppealBalance)} ${tokenLabelHtml(courtAppealAssetSymbol)} · ` +
        `${shortfall > 0n ? `Shortfall: ${fmtCourtAppealToken(shortfall)} ${tokenLabelHtml(courtAppealAssetSymbol)}` : 'Sufficient balance'}`;
    } else {
      el.appealCostHint.textContent = `Your appeal cost now: ${fmtCourtAppealToken(appealBond)} ${courtAppealAssetSymbol} (connect wallet for balance check)`;
    }

    el.jurorUserStakeStats.innerHTML = [
      `Your Stake ${fmtCourtStakeToken(myStake)} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
      `Stake Rewards ${fmtCourtStakeToken(myStakeRewards)} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
      `Appeal Rewards ${fmtCourtAppealToken(myAppealRewards)} ${tokenLabelHtml(courtAppealAssetSymbol)}`,
      `Min Required ${fmtCourtStakeToken(minimumStake)} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
      `Eligibility ${walletState.account ? (eligible ? 'eligible' : 'below minimum') : 'connect wallet'}`,
      `Selected This Round ${inPanel ? 'yes' : 'no'}`,
    ].join('<br/>');

    el.jurorRoundStakeStats.innerHTML = [
      `Panel Jurors ${selected.length}`,
      `Total Staked ${fmtCourtStakeToken(panelTotalStaked)} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
      `Avg/Juror ${fmtCourtStakeToken(avgPanelStake)} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
      `Your Share ${walletState.account ? `${mySharePct.toFixed(2)}%` : 'connect wallet'}`,
    ].join('<br/>');

    el.courtStatus.innerHTML = [
      `Oracle ${shortAddr(oracleAddress)} (${oracleAddress})`,
      `Round ${roundIndex.toString()} · Outcome ${outcomeText(Number(roundRow[5] || 0))}`,
      `Commit ${fmtTs(commitDeadline, nowTs)} · Reveal ${fmtTs(revealDeadline, nowTs)}`,
      `Appeal Bond ${fmtCourtAppealToken(appealBondAmount as bigint)} ${tokenLabelHtml(courtAppealAssetSymbol)} · Min Stake ${fmtCourtStakeToken(minimumStake)} ${tokenLabelHtml(courtStakeAssetSymbol)} · Total Staked ${fmtCourtStakeToken(panelTotalStaked)} ${tokenLabelHtml(courtStakeAssetSymbol)}`,
      `Panel ${selected.length > 0 ? selected.map(shortAddr).join(', ') : 'n/a'}${inPanel ? ' · You are selected' : ''}`,
      `Your Stake ${fmtCourtStakeToken(myStake)} ${tokenLabelHtml(courtStakeAssetSymbol)} · Stake Rewards ${fmtCourtStakeToken(myStakeRewards)} ${tokenLabelHtml(courtStakeAssetSymbol)} · Appeal Rewards ${fmtCourtAppealToken(myAppealRewards)} ${tokenLabelHtml(courtAppealAssetSymbol)} · Vote ${myVoteSummary}`,
    ].join('<br/>');
    prepHelpTips(el.courtStatus);
  }

  async function jurorStake(isStake: boolean): Promise<void> {
    if (!walletState.account) throw new Error('Connect wallet first');
    const chainOk = await ensurePulseChainConnected();
    if (!chainOk) throw new Error(`Switch to ${getActiveChainConfig().chainName} first`);

    const oracleAddress = await getCourtOracleAddress();
    if (!oracleAddress) throw new Error('Court oracle unavailable');
    await refreshCourtStakeAssetMeta(oracleAddress);
    if (!courtStakeAssetAddress) throw new Error('Court stake asset unavailable');

    const amountRaw = el.jurorStakeAmount.value.trim();
    if (!amountRaw) throw new Error('Enter stake amount');

    const amount = parseUnits(amountRaw, courtStakeAssetDecimals);
    if (amount <= 0n) throw new Error('Amount must be greater than zero');

    const token = contractWithSigner(courtStakeAssetAddress, erc20AbiEthers);
    const oracle = courtOracle(oracleAddress);

    if (isStake) {
      const allowance = (await token.allowance(walletState.account, oracleAddress)) as bigint;
      if (allowance < amount) {
        logLine(el.logs, 'warn', `Approving ${courtStakeAssetSymbol} for court stake...`);
        const approveTx = await token.approve(oracleAddress, MaxUint256);
        logLine(el.logs, 'warn', `Court approve sent: ${approveTx.hash}`);
        await approveTx.wait();
      }

      logLine(el.logs, 'warn', `Submitting stake tx (${amountRaw} ${courtStakeAssetSymbol})...`);
      const tx = await oracle.stake(amount);
      logLine(el.logs, 'warn', `Stake sent: ${tx.hash}`);
      await tx.wait();
      logLine(el.logs, 'ok', 'Juror stake confirmed');
    } else {
      logLine(el.logs, 'warn', `Submitting unstake tx (${amountRaw} ${courtStakeAssetSymbol})...`);
      const tx = await oracle.unstake(amount);
      logLine(el.logs, 'warn', `Unstake sent: ${tx.hash}`);
      await tx.wait();
      logLine(el.logs, 'ok', 'Juror unstake confirmed');
    }

    await refreshAfterMutation();
  }

  async function jurorWithdrawRewards(): Promise<void> {
    const oracleAddress = await getCourtOracleAddress();
    if (!oracleAddress) throw new Error('Court oracle unavailable');

    const oracle = courtOracle(oracleAddress);
    let withdrewAny = false;

    try {
      const tx = await oracle.claimStakeRewards();
      logLine(el.logs, 'warn', `Claim stake rewards sent: ${tx.hash}`);
      await tx.wait();
      withdrewAny = true;
    } catch {
      // Ignore zero-reward or unavailable stake claim; try appeal rewards next.
    }

    try {
      const tx = await oracle.claimAppealRewards();
      logLine(el.logs, 'warn', `Claim appeal rewards sent: ${tx.hash}`);
      await tx.wait();
      withdrewAny = true;
    } catch {
      // Ignore zero-reward or unavailable appeal claim.
    }

    if (!withdrewAny) throw new Error('No claimable rewards');
    logLine(el.logs, 'ok', 'Juror rewards claimed');
    await refreshAfterMutation();
  }

  async function jurorCommitVote(): Promise<void> {
    if (!currentMarketId) throw new Error('Missing marketId');
    if (!walletState.account) throw new Error('Connect wallet first');
    const oracleAddress = await getCourtOracleAddress();
    if (!oracleAddress) throw new Error('Court oracle unavailable');

    const roundIndex = (await readClient.readContract({
      address: oracleAddress,
      abi: courtOracleAbi,
      functionName: 'getCurrentRound',
      args: [currentMarketId as `0x${string}`],
    })) as bigint;

    const salt = normalizeSalt(el.jurorSalt.value);
    const commitment = computeJurorCommitment(currentMarketId, roundIndex, walletState.account as Address, selectedJurorOutcome(), salt);
    const tx = await courtOracle(oracleAddress).commitVote(currentMarketId, commitment);
    logLine(el.logs, 'warn', `Commit vote sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', 'Vote commitment confirmed');
    await refreshAfterMutation();
  }

  async function jurorRevealVote(): Promise<void> {
    if (!currentMarketId) throw new Error('Missing marketId');
    const oracleAddress = await getCourtOracleAddress();
    if (!oracleAddress) throw new Error('Court oracle unavailable');
    const salt = normalizeSalt(el.jurorSalt.value);
    const tx = await courtOracle(oracleAddress).revealVote(currentMarketId, selectedJurorOutcome(), salt);
    logLine(el.logs, 'warn', `Reveal vote sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', 'Vote reveal confirmed');
    await refreshAfterMutation();
  }

  async function jurorAppeal(): Promise<void> {
    if (!currentMarketId) throw new Error('Missing marketId');
    const oracleAddress = await getCourtOracleAddress();
    if (!oracleAddress) throw new Error('Court oracle unavailable');

    await refreshCourtStakeAssetMeta(oracleAddress);
    if (!courtAppealAssetAddress) throw new Error('Court appeal asset unavailable');

    const roundIndex = (await readClient.readContract({
      address: oracleAddress,
      abi: courtOracleAbi,
      functionName: 'getCurrentRound',
      args: [currentMarketId as `0x${string}`],
    })) as bigint;

    const bondAmount = (await readClient.readContract({
      address: oracleAddress,
      abi: courtOracleAbi,
      functionName: 'getAppealBondAmount',
      args: [roundIndex],
    })) as bigint;

    const token = contractWithSigner(courtAppealAssetAddress, erc20AbiEthers);
    const approveTx = await token.approve(oracleAddress, bondAmount);
    logLine(el.logs, 'warn', `Appeal approve sent: ${approveTx.hash}`);
    await approveTx.wait();

    const reason = normalizeReason(el.appealReason.value, true, 'Appeal');
    let tx;
    try {
      tx = await courtOracle(oracleAddress).appealCaseWithReason(currentMarketId, reason);
    } catch (error) {
      const message = String((error as any)?.shortMessage || (error as any)?.message || '').toLowerCase();
      const selector = extractCustomErrorSelector(error);
      const likelyMissingReasonMethod =
        message.includes('missing revert data') ||
        message.includes('function selector was not recognized') ||
        selector === '0x08c379a0';

      if (!likelyMissingReasonMethod) {
        throw error;
      }

      logLine(el.logs, 'warn', 'Reasoned appeal method unavailable on this oracle; retrying appealCase(bytes32).');
      tx = await courtOracle(oracleAddress).appealCase(currentMarketId);
    }
    logLine(el.logs, 'warn', `Appeal sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', 'Appeal submitted');
    await refreshAfterMutation();
  }

  async function refreshTradeBalances(): Promise<void> {
    if (!marketContextReady || !marketCollateralAddress || isZeroAddress(marketCollateralAddress)) {
      usdcTradeBalanceRaw = null;
      yesTradeBalanceRaw = null;
      noTradeBalanceRaw = null;
      usdcTradeBalanceLabel = '';
      yesTradeBalanceLabel = '';
      noTradeBalanceLabel = '';
      syncSideOptionLabels();
      setPayAssetUi();
      el.yesBalanceTrade.textContent = '';
      el.noBalanceTrade.textContent = '';
      return;
    }

    if (!walletState.account) {
      usdcTradeBalanceRaw = null;
      yesTradeBalanceRaw = null;
      noTradeBalanceRaw = null;
      usdcTradeBalanceLabel = '';
      yesTradeBalanceLabel = '';
      noTradeBalanceLabel = '';
      syncSideOptionLabels();
      setPayAssetUi();
      el.yesBalanceTrade.textContent = '';
      el.noBalanceTrade.textContent = '';
      return;
    }

    try {
      const [walletUsdcRes, allowanceRes, yesTokenRes, noTokenRes] = await Promise.allSettled([
        readClient.readContract({
          address: marketCollateralAddress as Address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [walletState.account as Address],
        }),
        readClient.readContract({
          address: marketCollateralAddress as Address,
          abi: erc20Abi,
          functionName: 'allowance',
          args: [walletState.account as Address, marketAddress],
        }),
        yesTokenAddress
          ? Promise.resolve(yesTokenAddress)
          : readClient.readContract({
            address: marketAddress,
            abi: marketAbi,
            functionName: 'yesToken',
          }),
        noTokenAddress
          ? Promise.resolve(noTokenAddress)
          : readClient.readContract({
            address: marketAddress,
            abi: marketAbi,
            functionName: 'noToken',
          }),
      ]);

      const walletUsdc = walletUsdcRes.status === 'fulfilled' ? (walletUsdcRes.value as bigint) : null;
      const allowance = allowanceRes.status === 'fulfilled' ? (allowanceRes.value as bigint) : null;
      const yesTokenAddr = yesTokenRes.status === 'fulfilled' ? (yesTokenRes.value as Address) : null;
      const noTokenAddr = noTokenRes.status === 'fulfilled' ? (noTokenRes.value as Address) : null;
      const usdcLabel = walletUsdc !== null ? fmtCollateral(walletUsdc, collateralMeta) : '-';
      usdcTradeBalanceRaw = walletUsdc;
      usdcTradeBalanceLabel = usdcLabel;
      let yesLabel = '-';
      let noLabel = '-';

      if (yesTokenAddr) {
        try {
          const yesBal = (await readClient.readContract({
            address: yesTokenAddr,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [walletState.account as Address],
          })) as bigint;
          yesTradeBalanceRaw = yesBal;
          yesLabel = fmtToken(yesBal, outcomeDisplayDecimals(true));
          el.yesBalanceTrade.textContent = yesLabel;
        } catch {
          yesTradeBalanceRaw = null;
          el.yesBalanceTrade.textContent = '-';
        }
      } else {
        yesTradeBalanceRaw = null;
        el.yesBalanceTrade.textContent = '-';
      }

      if (noTokenAddr) {
        try {
          const noBal = (await readClient.readContract({
            address: noTokenAddr,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [walletState.account as Address],
          })) as bigint;
          noTradeBalanceRaw = noBal;
          noLabel = fmtToken(noBal, outcomeDisplayDecimals(false));
          el.noBalanceTrade.textContent = noLabel;
        } catch {
          noTradeBalanceRaw = null;
          el.noBalanceTrade.textContent = '-';
        }
      } else {
        noTradeBalanceRaw = null;
        el.noBalanceTrade.textContent = '-';
      }

      yesTradeBalanceLabel = yesLabel;
      noTradeBalanceLabel = noLabel;
      syncSideOptionLabels();
      setPayAssetUi();
      applySelectedPercentToInput();


    } catch {
      usdcTradeBalanceRaw = null;
      yesTradeBalanceRaw = null;
      noTradeBalanceRaw = null;
      usdcTradeBalanceLabel = '-';
      yesTradeBalanceLabel = '-';
      noTradeBalanceLabel = '-';
      syncSideOptionLabels();
      setPayAssetUi();
      el.yesBalanceTrade.textContent = '-';
      el.noBalanceTrade.textContent = '-';
    }
  }

  async function refreshClaimPreview(marketResolved: boolean): Promise<void> {
    const claimMetricCard = (label: string, value: string, accent = false, spanFull = false) => `
      <div class="card" style="padding:8px; background:${accent ? 'rgba(46, 204, 113, 0.10)' : '#061320'}; border-color:${accent ? 'rgba(46, 204, 113, 0.45)' : 'rgba(107, 196, 190, 0.16)'}; box-shadow:none; ${spanFull ? 'grid-column:1 / -1;' : ''}">
        <div class="mono" style="font-size:.62rem; color:var(--muted); margin-bottom:3px;">${label}</div>
        <div style="font-size:${accent ? '.92rem' : '.78rem'}; color:${accent ? '#8ff3c0' : '#dff7ff'}; line-height:1.35; word-break:break-word; font-weight:${accent ? 700 : 500};">${value}</div>
      </div>
    `;

    if (!walletState.account) {
      el.claimPreview.innerHTML = `
        <div style="display:grid; gap:8px;">
          <div class="mono" style="font-size:.62rem; color:var(--muted);">Claim Preview</div>
          <div class="card" style="padding:8px; background:#061320; border-color:rgba(107, 196, 190, 0.16); box-shadow:none;">
            Connect wallet to preview your claim payout.
          </div>
        </div>
      `;
      return;
    }

    if (!marketResolved) {
      el.claimPreview.innerHTML = `
        <div style="display:grid; gap:8px;">
          <div class="mono" style="font-size:.62rem; color:var(--muted);">Claim Preview</div>
          ${claimMetricCard('Status', 'Market must be settled first')}
          ${claimMetricCard('Rule', 'Winning side redeems pro-rata collateral, losing side redeems 0, invalid outcomes redeem both sides pro-rata')}
        </div>
      `;
      return;
    }

    try {
      const [canClaim, payout, yesBalance, noBalance, userWinningShares, payoutDenominator, payoutOutcome] = await readClient.readContract({
        address: marketAddress,
        abi: marketAbi,
        functionName: 'quoteClaim',
        args: [walletState.account as Address],
      }) as [boolean, bigint, bigint, bigint, bigint, bigint, number];

      const payoutLabel = `$${fmtToken(payout)}`;
      const yesBalanceLabel = fmtToken(yesBalance);
      const noBalanceLabel = fmtToken(noBalance);
      const winSide = Number(payoutOutcome) === 3 ? 'N/A' : (Number(payoutOutcome) === 1 ? yesOutcomeLabel : noOutcomeLabel);
      const loseSide = Number(payoutOutcome) === 3 ? 'N/A' : (Number(payoutOutcome) === 1 ? noOutcomeLabel : yesOutcomeLabel);
      const outcomeLabel = outcomeText(Number(payoutOutcome));
      const winningSharesLabel = fmtToken(userWinningShares);
      const denominatorLabel = fmtToken(payoutDenominator);

      if (Number(payoutOutcome) === 3) {
        el.claimPreview.innerHTML = `
          <div style="display:grid; gap:8px;">
            <div class="mono" style="font-size:.62rem; color:var(--muted);">Claim Preview</div>
            <div style="display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:8px;">
                ${claimMetricCard('Estimated Payout', canClaim ? payoutLabel : '$0', true, true)}
                ${claimMetricCard('Outcome', 'Invalid')}
                ${claimMetricCard('Claimable Shares', canClaim ? `${winningSharesLabel} / ${denominatorLabel}` : 'none')}
                ${claimMetricCard('Winning Side', 'N/A')}
                ${claimMetricCard('Losing Side', 'N/A')}
            </div>
            <div class="card" style="padding:8px; background:#061320; border-color:rgba(107, 196, 190, 0.16); box-shadow:none; display:grid; gap:6px;">
              <div class="mono" style="font-size:.62rem; color:var(--muted);">Balances</div>
              <div style="display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:8px;">
                ${claimMetricCard(yesOutcomeLabel, yesBalanceLabel)}
                ${claimMetricCard(noOutcomeLabel, noBalanceLabel)}
              </div>
            </div>
            <div class="card" style="padding:8px; background:#061320; border-color:rgba(107, 196, 190, 0.16); box-shadow:none;">
              <div class="mono" style="font-size:.62rem; color:var(--muted); margin-bottom:3px;">Payout Rule</div>
              <div style="font-size:.78rem; color:#dff7ff; line-height:1.35;">Both sides redeem pro-rata from the claim pool.</div>
            </div>
          </div>
        `;
        return;
      }

      el.claimPreview.innerHTML = `
        <div style="display:grid; gap:8px;">
          <div class="mono" style="font-size:.62rem; color:var(--muted);">Claim Preview</div>
          <div style="display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:8px;">
            ${claimMetricCard('Estimated Payout', canClaim ? payoutLabel : '$0', true, true)}
            ${claimMetricCard('Outcome', outcomeLabel)}
            ${claimMetricCard('Winning Side', winSide)}
            ${claimMetricCard('Losing Side', loseSide)}
            ${claimMetricCard('Winning Shares', canClaim ? winningSharesLabel : '0')}
            ${claimMetricCard('Claimable Shares', canClaim ? `${winningSharesLabel} / ${denominatorLabel}` : 'none')}
          </div>
          <div class="card" style="padding:8px; background:#061320; border-color:rgba(107, 196, 190, 0.16); box-shadow:none; display:grid; gap:6px;">
            <div class="mono" style="font-size:.62rem; color:var(--muted);">Balances</div>
            <div style="display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:8px;">
              ${claimMetricCard(yesOutcomeLabel, yesBalanceLabel)}
              ${claimMetricCard(noOutcomeLabel, noBalanceLabel)}
            </div>
          </div>
        </div>
      `;
    } catch {
      el.claimPreview.innerHTML = `
        <div style="display:grid; gap:8px;">
          <div class="mono" style="font-size:.62rem; color:var(--muted);">Claim Preview</div>
          <div class="card" style="padding:8px; background:#061320; border-color:rgba(107, 196, 190, 0.16); box-shadow:none;">Claim preview unavailable right now.</div>
        </div>
      `;
    }
  }

  async function refreshCreatorMarkets(): Promise<void> {
    const hadCreatorMarket = walletHasCreatedMarket;

    if (!walletState.account) {
      walletHasCreatedMarket = false;
      updateCreatorAccessVisibility();
      if (hadCreatorMarket !== walletHasCreatedMarket) void refresh();
      el.creatorMarketsHint.textContent = 'Connect wallet to load your created markets.';
      el.creatorMarketsList.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted);">Creator markets are available only while connected.</div>';
      return;
    }

    el.creatorMarketsHint.textContent = 'Loading your created markets...';
    el.creatorMarketsList.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted);">Scanning factory markets for your creator address.</div>';

    const creator = walletState.account.toLowerCase();
    const nowTs = Math.floor(Date.now() / 1000);
    creatorMarketsLastLoadedAtMs = Date.now();

    try {
      const marketCountRaw = await readClient.readContract({
        address: DEPLOYMENT.factory as Address,
        abi: factoryAbi,
        functionName: 'marketCount',
      });

      const marketCount = Number(marketCountRaw || 0n);
      if (!Number.isFinite(marketCount) || marketCount <= 0) {
        walletHasCreatedMarket = false;
        updateCreatorAccessVisibility();
        if (hadCreatorMarket !== walletHasCreatedMarket) void refresh();
        el.creatorMarketsHint.textContent = 'No markets created yet on this network.';
        el.creatorMarketsList.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted);">No created markets found.</div>';
        return;
      }

      const createdMarkets: Address[] = [];
      const chunkSize = 80;

      for (let high = marketCount - 1; high >= 0; high -= chunkSize) {
        const low = Math.max(0, high - chunkSize + 1);
        const marketCalls: Array<{ address: Address; abi: typeof factoryAbi; functionName: 'markets'; args: [bigint] }> = [];

        for (let i = high; i >= low; i -= 1) {
          marketCalls.push({
            address: DEPLOYMENT.factory as Address,
            abi: factoryAbi,
            functionName: 'markets',
            args: [BigInt(i)],
          });
        }

        const marketAddresses = (await readContractsBatch<Address>(marketCalls))
          .filter((address): address is Address => Boolean(address) && !isZeroAddress(String(address)));

        if (marketAddresses.length === 0) continue;

        const creatorCalls = marketAddresses.map((address) => ({
          address,
          abi: marketAbi,
          functionName: 'creator',
        }));

        const creators = await readContractsBatch<Address>(creatorCalls);
        for (let i = 0; i < marketAddresses.length; i += 1) {
          if (String(creators[i] || '').toLowerCase() === creator) {
            createdMarkets.push(marketAddresses[i]);
          }
        }
      }

      if (createdMarkets.length === 0) {
        walletHasCreatedMarket = false;
        updateCreatorAccessVisibility();
        if (hadCreatorMarket !== walletHasCreatedMarket) void refresh();
        el.creatorMarketsHint.textContent = 'No created markets found for this wallet on the active network.';
        el.creatorMarketsList.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted);">Create a market to organize creator appeals here.</div>';
        return;
      }

      walletHasCreatedMarket = true;
      updateCreatorAccessVisibility();
      if (hadCreatorMarket !== walletHasCreatedMarket) void refresh();

      type CreatorMarketRow = {
        address: Address;
        marketId: `0x${string}`;
        question: string;
        resolveAfter: number;
        resolved: boolean;
        disputed: boolean;
      };

      const rows: CreatorMarketRow[] = [];
      for (let start = 0; start < createdMarkets.length; start += chunkSize) {
        const addresses = createdMarkets.slice(start, start + chunkSize);
        const metadataCalls: Array<{ address: Address; abi: typeof marketAbi; functionName: string }> = [];

        for (const address of addresses) {
          metadataCalls.push({ address, abi: marketAbi, functionName: 'marketId' });
          metadataCalls.push({ address, abi: marketAbi, functionName: 'question' });
          metadataCalls.push({ address, abi: marketAbi, functionName: 'resolveAfter' });
          metadataCalls.push({ address, abi: marketAbi, functionName: 'resolved' });
        }

        const metadata = await readContractsBatch<unknown>(metadataCalls);
        for (let i = 0; i < addresses.length; i += 1) {
          const base = i * 4;
          rows.push({
            address: addresses[i],
            marketId: String(metadata[base] || '0x') as `0x${string}`,
            question: String(metadata[base + 1] || 'Untitled market'),
            resolveAfter: Number(metadata[base + 2] || 0n),
            resolved: Boolean(metadata[base + 3]),
            disputed: false,
          });
        }
      }

      for (let start = 0; start < rows.length; start += chunkSize) {
        const batch = rows.slice(start, start + chunkSize);
        const assertionCalls = batch.map((row) => ({
          address: DEPLOYMENT.adapter as Address,
          abi: adapterAbi,
          functionName: 'assertions',
          args: [row.marketId],
        }));
        const assertions = await readContractsBatch<unknown>(assertionCalls);
        for (let i = 0; i < batch.length; i += 1) {
          const assertionRow = assertions[i] as any[] | undefined;
          batch[i].disputed = Boolean(assertionRow?.[8]);
        }
      }

      rows.sort((a, b) => b.resolveAfter - a.resolveAfter);

      const marketWord = rows.length === 1 ? 'market' : 'markets';
      el.creatorMarketsHint.textContent = `Connected wallet created ${rows.length} ${marketWord} on this network.`;
      el.creatorMarketsList.innerHTML = rows
        .map((row) => {
          const statusText = row.resolved ? 'Settled' : (row.resolveAfter > nowTs ? 'Live' : 'Appeal / Resolve');
          const statusTone = row.resolved ? 'color:#7ea5c0;' : (row.resolveAfter > nowTs ? 'color:#6ee7b7;' : 'color:#fbbf24;');
          const disputeBadge = row.disputed && !row.resolved
            ? '<span class="meta-chip resolving">Dispute active</span>'
            : row.resolved
              ? '<span class="meta-chip">Finalized</span>'
              : '';
          return `
            <a href="?market=${row.address}&tab=creator" class="card" style="padding:8px; display:grid; gap:5px; text-decoration:none;">
              <div class="mono" style="font-size:.66rem; color:var(--muted);">${shortAddr(String(row.address))}</div>
              <div style="font-size:.82rem; line-height:1.4;">${escapeHtml(row.question)}</div>
              <div class="mono" style="font-size:.66rem; color:var(--muted); display:flex; gap:10px; flex-wrap:wrap;">
                <span style="${statusTone}">${statusText}</span>
                <span>Resolve ${fmtTs(row.resolveAfter, nowTs)}</span>
                ${disputeBadge}
              </div>
            </a>
          `;
        })
        .join('');
    } catch {
      walletHasCreatedMarket = false;
      updateCreatorAccessVisibility();
      if (hadCreatorMarket !== walletHasCreatedMarket) void refresh();
      el.creatorMarketsHint.textContent = 'Unable to load created markets right now.';
      el.creatorMarketsList.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted);">Try again after reconnecting or switching RPC.</div>';
    }
  }

  function canAccessCreatorControls(): boolean {
    return Boolean(walletState.account && (walletHasCreatedMarket || isCurrentWalletCreator()));
  }

  function updateCreatorAccessVisibility(): void {
    const creatorPanel = document.getElementById('creatorPanel') as HTMLElement | null;
    if (el.creatorTabBtn?.style) {
      el.creatorTabBtn.style.display = '';
    }
    if (creatorPanel) creatorPanel.style.display = '';
  }

  function renderCollateralLineWithBalance(marketCollateralBalance: bigint): void {
    const displayCollateralLogo = isDefaultUsdcCollateral(marketCollateralAddress) ? defaultUsdcLogoUrl : collateralLogoUrl;
    const collateralMetaPending = collateralMeta.symbol === 'TOKEN' || collateralMeta.name === 'Collateral Token';
    if (collateralMetaPending) {
      el.collateralLine.innerHTML = `<img src="${displayCollateralLogo}" alt="Collateral" class="token-logo"/> -`;
      return;
    }
    const balanceLabel = `${fmtCollateral(marketCollateralBalance, collateralMeta)} ${escapeHtml(collateralMeta.symbol)}`;
    el.collateralLine.innerHTML = `<img src="${displayCollateralLogo}" alt="${collateralMeta.symbol}" class="token-logo"/> ${balanceLabel}`;
  }

  async function refresh(): Promise<void> {
    const endPriceRefresh = beginTradePriceRefresh();
    try {
    // Single call for all volatile market state (reserves, resolved status, fee config, etc.)
    type TradeStateResult = {
      initialized: boolean; resolved: boolean; tradingPaused: boolean;
      marketId: `0x${string}`; collateral: Address; creator: Address;
      resolveAfter: bigint; yesReserve: bigint; noReserve: bigint;
      impliedYesBps: bigint; house: Address; buyFeeBps: number; sellFeeBps: number;
      collateralBalance: bigint; buyCollateralVolume: bigint; sellCollateralVolume: bigint;
      totalCollateralVolume: bigint; feesCollected: bigint; totalTrades: bigint; mostRecentTradeAt: bigint;
    };
    const ts = (await readClient.readContract({
      address: marketAddress,
      abi: marketAbi,
      functionName: 'getTradeState',
    })) as TradeStateResult;

    // Populate fee config cache from TradeState — no separate factory call needed.
    cachedFeeConfig = {
      buyFeeBps: BigInt(ts.buyFeeBps),
      sellFeeBps: BigInt(ts.sellFeeBps),
      fetchedAtMs: Date.now(),
    };

    // Load immutable fields only once; reuse cache on subsequent refreshes.
    if (!marketContextReady) {
      const [questionRead, yesTokenRead, noTokenRead] = await readContractsBatch<unknown>([
        { address: marketAddress, abi: marketAbi, functionName: 'question' },
        { address: marketAddress, abi: marketAbi, functionName: 'yesToken' },
        { address: marketAddress, abi: marketAbi, functionName: 'noToken' },
      ]);
      cachedQuestion = String(questionRead || 'Untitled market');
      cachedYesTokenAddress = (yesTokenRead as Address) || null;
      cachedNoTokenAddress = (noTokenRead as Address) || null;
    }

    // Map TradeState fields to local variable names used by the rest of this function.
    const collateralRead = ts.collateral;
    const marketCollateralBalance = (ts.collateralBalance as bigint) || 0n;
    const question = cachedQuestion;
    const resolveAfter = ts.resolveAfter;
    const yesReserve = ts.yesReserve;
    const noReserve = ts.noReserve;
    const marketId = ts.marketId;
    const marketResolved = ts.resolved;
    const yesToken = cachedYesTokenAddress;
    const noToken = cachedNoTokenAddress;
    const marketCreator = ts.creator;
    liquidityProviderAddress = null; // not exposed by BoxBetMarket

    // Resolve per-market collateral — update collateralMeta when the address changes.
    const rawCollateral = String((collateralRead as string) || '').trim();
    const cachedCollateral = readCachedMarketCollateral();
    const freshCollateral = /^0x[0-9a-fA-F]{40}$/.test(rawCollateral) && !isZeroAddress(rawCollateral)
      ? rawCollateral
      : marketCollateralAddress || cachedCollateral;
    if (/^0x[0-9a-fA-F]{40}$/.test(freshCollateral) && !isZeroAddress(freshCollateral)) {
      writeCachedMarketCollateral(freshCollateral);
    }

    const collateralAddressChanged = freshCollateral && freshCollateral.toLowerCase() !== marketCollateralAddress.toLowerCase();
    const collateralMetaPending = collateralMeta.symbol === 'TOKEN' || collateralMeta.name === 'Collateral Token';

    if (freshCollateral && (collateralAddressChanged || collateralMetaPending)) {
      marketCollateralAddress = freshCollateral;
      const nextMetaPromise = (async (): Promise<CollateralMeta> => {
        try {
          return applyCustomCollateralDecimalPolicy(
            await resolveCollateralMeta(marketCollateralAddress),
            marketCollateralAddress,
          );
        } catch {
          return applyCustomCollateralDecimalPolicy(
            defaultCollateralMeta(marketCollateralAddress),
            marketCollateralAddress,
          );
        }
      })();

      const nextLogoPromise = (async (): Promise<string> => {
        if (isDefaultUsdcCollateral(marketCollateralAddress)) return defaultUsdcLogoUrl;
        const logo = await resolveTokenLogoUrl(marketCollateralAddress);
        return logo ?? `${import.meta.env.BASE_URL}images/coin.svg`;
      })();

      collateralMeta = await nextMetaPromise;
      collateralLogoUrl = await nextLogoPromise;
      document.documentElement.style.setProperty('--collateral-logo-url', `url('${collateralLogoUrl}')`);
      renderCollateralLineWithBalance(marketCollateralBalance);
      }

    // Ensure collateral line updates quickly even when collateral address is unchanged.
    if (freshCollateral && freshCollateral.toLowerCase() === marketCollateralAddress.toLowerCase()) {
      renderCollateralLineWithBalance(marketCollateralBalance);
    }

    currentMarketId = String(marketId || '');
    el.marketQuestion.textContent = String(question || 'Untitled market');
    el.marketAddr.textContent = shortAddr(marketAddress);
    el.marketAddr.title = marketAddress;
    const yes = (yesReserve as bigint) || 0n;
    const no = (noReserve as bigint) || 0n;
    currentYesReserve = yes;
    currentNoReserve = no;
    const total = yes + no;

    yesTokenAddress = (yesToken as Address) || null;
    noTokenAddress = (noToken as Address) || null;
    creatorAddress = (marketCreator as Address) || null;
    liquidityProviderAddress = null;

    const isMarketSettled = Boolean(marketResolved);
    const walletConnected = Boolean(walletState.account);
    updateCreatorAccessVisibility();
    const creatorPanelActive = isPanelActive('creatorPanel');
    if (creatorPanelActive && (!creatorMarketsLastLoadedAtMs || Date.now() - creatorMarketsLastLoadedAtMs >= CREATOR_MARKETS_REFRESH_MS)) {
      void refreshCreatorMarkets();
    }

    const [yesNameRead, noNameRead, yesDecimalsRead, noDecimalsRead] = await Promise.allSettled([
      yesTokenAddress
        ? readClient.readContract({
          address: yesTokenAddress,
          abi: erc20Abi,
          functionName: 'name',
        })
        : Promise.resolve('YES'),
      noTokenAddress
        ? readClient.readContract({
          address: noTokenAddress,
          abi: erc20Abi,
          functionName: 'name',
        })
        : Promise.resolve('NO'),
      yesTokenAddress
        ? readClient.readContract({
          address: yesTokenAddress,
          abi: erc20Abi,
          functionName: 'decimals',
        })
        : Promise.resolve(collateralMeta.decimals),
      noTokenAddress
        ? readClient.readContract({
          address: noTokenAddress,
          abi: erc20Abi,
          functionName: 'decimals',
        })
        : Promise.resolve(collateralMeta.decimals),
    ]);

    yesOutcomeLabel = normalizeOutcomeLabel(yesNameRead.status === 'fulfilled' ? yesNameRead.value : 'YES', 'YES');
    noOutcomeLabel = normalizeOutcomeLabel(noNameRead.status === 'fulfilled' ? noNameRead.value : 'NO', 'NO');
    yesTokenDecimals = normalizeTokenDecimals(
      yesDecimalsRead.status === 'fulfilled' ? yesDecimalsRead.value : collateralMeta.decimals,
      collateralMeta.decimals,
    );
    noTokenDecimals = normalizeTokenDecimals(
      noDecimalsRead.status === 'fulfilled' ? noDecimalsRead.value : collateralMeta.decimals,
      collateralMeta.decimals,
    );
    applyOutcomeLabels();

    const yesPct = total > 0n ? fmtPct(yes, total) : '50.0%';
    const noPct = total > 0n ? fmtPct(no, total) : '50.0%';
    const yesIsLeader = yes >= no;

    el.yesLabelOdds.classList.remove('market-share-green', 'market-share-red');
    el.noLabelOdds.classList.remove('market-share-green', 'market-share-red');
    el.yesBalanceLabel.classList.remove('market-share-green', 'market-share-red');
    el.noBalanceLabel.classList.remove('market-share-green', 'market-share-red');
    el.yesLabelOdds.classList.add(yesIsLeader ? 'market-share-green' : 'market-share-red');
    el.noLabelOdds.classList.add(yesIsLeader ? 'market-share-red' : 'market-share-green');
    el.yesBalanceLabel.classList.add(yesIsLeader ? 'market-share-green' : 'market-share-red');
    el.noBalanceLabel.classList.add(yesIsLeader ? 'market-share-red' : 'market-share-green');

    el.yesPct.classList.remove('market-share-green', 'market-share-red');
    el.noPct.classList.remove('market-share-green', 'market-share-red');
    el.yesPct.classList.add(yesIsLeader ? 'market-share-green' : 'market-share-red');
    el.noPct.classList.add(yesIsLeader ? 'market-share-red' : 'market-share-green');

    el.yesBar.style.width = yesPct;
    el.noBar.style.width = noPct;
    el.yesBar.classList.remove('yes', 'no');
    el.noBar.classList.remove('yes', 'no');
    el.yesBar.classList.add(yesIsLeader ? 'yes' : 'no');
    el.noBar.classList.add(yesIsLeader ? 'no' : 'yes');
    el.yesPct.textContent = yesPct;
    el.noPct.textContent = noPct;
    el.reserveLine.textContent = `${yesOutcomeLabel} ${fmtCollateral(yes, collateralMeta)} / ${noOutcomeLabel} ${fmtCollateral(no, collateralMeta)}`;
    renderCollateralLineWithBalance(marketCollateralBalance);

    const nowTs = Math.floor(Date.now() / 1000);
    const sec = Number(resolveAfter || 0n) - nowTs;
    const deadlineValue = sec <= 0 ? 'Now' : fmtDuration(sec).replace(/\s+/g, '/');
    el.marketStatus.textContent = deadlineValue;

    let isResolutionFinalized = false;
    let settlementMismatch = false;
    let acceptedProposalBlocksTrading = false;
    let finalizeAvailable = false;
    let phaseBadgeText = sec <= 0 ? 'Post-resolve, pre-finalize' : 'Pre-resolve';
    let phaseBadgeTone: 'live' | 'resolving' | '' = sec <= 0 ? 'resolving' : 'live';

    if (currentMarketId && currentMarketId !== '0x') {
      try {
      const resolution = (await readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'getResolution',
        args: [currentMarketId as `0x${string}`],
      })) as [boolean, number];

      const shouldLoadResolutionDetails = isPanelActive('resolutionPanel') || isPanelActive('creatorPanel') || isPanelActive('courtPanel');
      const shouldLoadResolutionLogs = shouldLoadResolutionDetails;
      const logsCached = shouldLoadResolutionLogs
        && resolutionLogsCache !== null
        && resolutionLogsCache.marketId === currentMarketId
        && (Date.now() - resolutionLogsCache.ts) < RESOLUTION_LOGS_CACHE_MS;

      const [
        proposalLogs,
        proposalReasonLogs,
        disputeLogs,
        proposalDisputedLogs,
        disputeResolvedByCreatorLogs,
        disputeReasonLogs,
        appealReasonLogs,
        finalizedLogs,
        disputeWindowValue,
        creatorDecisionWindowValue,
        escalationTimeoutValue,
        emergencyWindowValue,
        grandJudgeValue,
        assertion,
        disputeTimingSnapshot,
        emergencyRuling,
        pendingProposal,
        hasPendingProposal,
        proposalsState,
        isProposalAccepted,
        timeToFinalization,
      ] = await Promise.all([
        shouldLoadResolutionLogs
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.proposalLogs) : readClient.getLogs({
            address: DEPLOYMENT.adapter as Address,
            event: events.proposed,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionLogs
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.proposalReasonLogs) : readClient.getLogs({
            address: DEPLOYMENT.adapter as Address,
            event: events.proposedReason,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionLogs
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.disputeLogs) : readClient.getLogs({
            address: DEPLOYMENT.adapter as Address,
            event: events.disputed,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionLogs
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.proposalDisputedLogs) : readClient.getLogs({
            address: DEPLOYMENT.adapter as Address,
            event: events.proposalDisputed,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionLogs
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.disputeResolvedByCreatorLogs) : readClient.getLogs({
            address: DEPLOYMENT.adapter as Address,
            event: events.disputeResolvedByCreator,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionLogs
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.disputeReasonLogs) : readClient.getLogs({
            address: DEPLOYMENT.adapter as Address,
            event: events.disputedReason,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionLogs && !isZeroAddress(DEPLOYMENT.oracle)
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.appealReasonLogs) : readClient.getLogs({
            address: DEPLOYMENT.oracle as Address,
            event: events.appealReason,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionLogs
          ? (logsCached ? Promise.resolve(resolutionLogsCache!.finalizedLogs) : readClient.getLogs({
            address: DEPLOYMENT.adapter as Address,
            event: events.finalized,
            args: { marketId: currentMarketId as `0x${string}` },
            fromBlock: activeChain.deploymentStartBlock,
            toBlock: 'latest',
          }))
          : Promise.resolve([]),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'disputeWindow',
          })
          : Promise.resolve(0n),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'CREATOR_DISPUTE_DECISION_WINDOW',
          })
          : Promise.resolve(86_400n),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'escalationTimeout',
          })
          : Promise.resolve(0n),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'emergencyDecisionWindow',
          })
          : Promise.resolve(0n),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'grandJudge',
          })
          : Promise.resolve('0x0000000000000000000000000000000000000000'),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'assertions',
            args: [currentMarketId as `0x${string}`],
          })
          : Promise.resolve([false, 0n, '0x0000000000000000000000000000000000000000000000000000000000000000', false, 0, '0x0000000000000000000000000000000000000000', 0, 0n, false, '0x0000000000000000000000000000000000000000']),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'disputeTimingSnapshots',
            args: [currentMarketId as `0x${string}`],
          })
          : Promise.resolve([false, 0n, 0n]),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'emergencyRulings',
            args: [currentMarketId as `0x${string}`],
          })
          : Promise.resolve([false, 0, 0n]),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getPendingProposal',
            args: [currentMarketId as `0x${string}`],
          })
          : Promise.resolve(['0x0000000000000000000000000000000000000000', 0, 0n, false]),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'hasPendingProposal',
            args: [currentMarketId as `0x${string}`],
          })
          : Promise.resolve(false),
        shouldLoadResolutionDetails
          ? readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getProposals',
            args: [currentMarketId as `0x${string}`],
          })
          : Promise.resolve(['0x0000000000000000000000000000000000000000', false, '0x0000000000000000000000000000000000000000', false, 0]),
        readClient.readContract({
          address: DEPLOYMENT.adapter as Address,
          abi: adapterAbi,
          functionName: 'isProposalAccepted',
          args: [currentMarketId as `0x${string}`],
        }),
        readClient.readContract({
          address: DEPLOYMENT.adapter as Address,
          abi: adapterAbi,
          functionName: 'getTimeToFinalization',
          args: [currentMarketId as `0x${string}`],
        }),
      ]);

      // Populate cache if we just fetched fresh logs.
      if (shouldLoadResolutionLogs && !logsCached) {
        resolutionLogsCache = {
          marketId: currentMarketId, ts: Date.now(),
          proposalLogs,
          proposalReasonLogs,
          disputeLogs,
          proposalDisputedLogs,
          disputeResolvedByCreatorLogs,
          disputeReasonLogs,
          appealReasonLogs,
          finalizedLogs,
        };
      }

      const latestProposal = proposalLogs.at(-1);
      const latestDispute = disputeLogs.at(-1);
      const latestProposalDisputed = proposalDisputedLogs.at(-1);
      const latestDisputeResolvedByCreator = disputeResolvedByCreatorLogs.at(-1);
      const latestDisputeReason = disputeReasonLogs.at(-1);
      const latestAppealReason = appealReasonLogs.at(-1);
      const latestFinalized = finalizedLogs.at(-1);
      const latestProposalArgs = (latestProposal as any)?.args as { outcome?: number | bigint; proposer?: string } | undefined;
      const latestFinalizedArgs = (latestFinalized as any)?.args as { outcome?: number | bigint } | undefined;
      const latestDisputeArgs = (latestDispute as any)?.args as { disputer?: string } | undefined;
      const latestProposalDisputedArgs = (latestProposalDisputed as any)?.args as { disputer?: string; disputedOutcome?: number | bigint; decisionDeadline?: number | bigint } | undefined;
      const latestDisputeResolvedByCreatorArgs = (latestDisputeResolvedByCreator as any)?.args as { creator?: string; finalOutcome?: number | bigint } | undefined;
      const latestDisputeReasonArgs = (latestDisputeReason as any)?.args as { reason?: string } | undefined;
      const latestAppealReasonArgs = (latestAppealReason as any)?.args as { reason?: string } | undefined;

      const proposalReasonByTx = new Map<string, string>();
      const proposalReasonByOutcomeProposer = new Map<string, string>();
      for (const rawReasonLog of proposalReasonLogs as any[]) {
        const txHash = String(rawReasonLog?.transactionHash || '').toLowerCase();
        const args = rawReasonLog?.args as { outcome?: number | bigint; proposer?: string; reason?: string } | undefined;
        const reason = escapeHtml(String(args?.reason || ''));
        if (txHash) proposalReasonByTx.set(txHash, reason);

        const outcome = Number(args?.outcome ?? 0);
        const proposer = String(args?.proposer || '').toLowerCase();
        if ((outcome === 1 || outcome === 2) && proposer) {
          proposalReasonByOutcomeProposer.set(`${outcome}:${proposer}`, reason);
        }
      }

      const latestProposalByOutcome: Record<1 | 2, any | null> = { 1: null, 2: null };
      for (const rawProposalLog of proposalLogs as any[]) {
        const args = rawProposalLog?.args as { outcome?: number | bigint } | undefined;
        const outcome = Number(args?.outcome ?? 0);
        if (outcome === 1 || outcome === 2) {
          latestProposalByOutcome[outcome] = rawProposalLog;
        }
      }

      const yesProposalLog = latestProposalByOutcome[1];
      const noProposalLog = latestProposalByOutcome[2];
      const proposalReasonTextForLog = (proposalLog: any | null): string => {
        if (!proposalLog) return '';

        const txHash = String(proposalLog?.transactionHash || '').toLowerCase();
        if (txHash && proposalReasonByTx.has(txHash)) {
          return proposalReasonByTx.get(txHash) || '';
        }

        const outcome = Number((proposalLog?.args as { outcome?: number | bigint } | undefined)?.outcome ?? 0);
        const proposer = String((proposalLog?.args as { proposer?: string } | undefined)?.proposer || '').toLowerCase();
        if ((outcome === 1 || outcome === 2) && proposer) {
          return proposalReasonByOutcomeProposer.get(`${outcome}:${proposer}`) || '';
        }

        return '';
      };

      const yesProposalReasonText = proposalReasonTextForLog(yesProposalLog);
      const noProposalReasonText = proposalReasonTextForLog(noProposalLog);
      const proposalReasonUnsupported = (proposalLogs as any[]).length > 0 && (proposalReasonLogs as any[]).length === 0;
      const proposalsRow = proposalsState as [Address, boolean, Address, boolean, number];
      const yesSlotProposer = String(proposalsRow?.[0] || '');
      const noSlotProposer = String(proposalsRow?.[2] || '');
      const hasYesSlot = hasLiveProposer(yesSlotProposer);
      const hasNoSlot = hasLiveProposer(noSlotProposer);
      const yesSlotLabel = yesSlotProposer && !/^0x0{40}$/i.test(yesSlotProposer) ? shortAddr(yesSlotProposer) : 'none';
      const noSlotLabel = noSlotProposer && !/^0x0{40}$/i.test(noSlotProposer) ? shortAddr(noSlotProposer) : 'none';
      const yesLabelEsc = escapeHtml(outcomeText(1));
      const noLabelEsc = escapeHtml(outcomeText(2));

      el.resolutionState.innerHTML = resolution[0]
        ? `<span class="resolution-pill resolution-pill-live">Finalized: ${escapeHtml(outcomeText(Number(resolution[1])))}</span>`
        : '<span class="resolution-pill resolution-pill-muted">Not finalized</span>';

      const proposalOutcome = latestProposal ? Number(latestProposalArgs?.outcome ?? 0) : null;
      const finalizedOutcome = latestFinalized ? Number(latestFinalizedArgs?.outcome ?? 0) : null;
      const disputeReasonText = latestDisputeReason ? escapeHtml(String(latestDisputeReasonArgs?.reason || '')) : '';
      const appealReasonText = latestAppealReason ? escapeHtml(String(latestAppealReasonArgs?.reason || '')) : '';
      const proposalWho = latestProposal ? shortAddr(String(latestProposalArgs?.proposer || '')) : 'n/a';
      const disputeWho = latestDispute ? shortAddr(String(latestDisputeArgs?.disputer || '')) : 'n/a';
      const creatorResolveWho = latestDisputeResolvedByCreator ? shortAddr(String(latestDisputeResolvedByCreatorArgs?.creator || '')) : 'n/a';
      const creatorResolveOutcome = latestDisputeResolvedByCreator ? Number(latestDisputeResolvedByCreatorArgs?.finalOutcome || 0) : 0;
      const yesProposalWho = yesProposalLog ? shortAddr(String((yesProposalLog as any)?.args?.proposer || '')) : 'n/a';
      const noProposalWho = noProposalLog ? shortAddr(String((noProposalLog as any)?.args?.proposer || '')) : 'n/a';

      const creatorDecisionWindowSec = Number(creatorDecisionWindowValue || 86_400n);
      const proposalDisputeDecisionDeadline = Number(latestProposalDisputedArgs?.decisionDeadline ?? 0);
      const proposalDisputeOutcome = Number(latestProposalDisputedArgs?.disputedOutcome ?? 0);
      const latestDisputeBlock = Number((latestProposalDisputed as any)?.blockNumber || (latestDispute as any)?.blockNumber || 0);
      const latestCreatorResolveBlock = Number((latestDisputeResolvedByCreator as any)?.blockNumber || 0);
      const creatorResolvedLatestDispute = latestCreatorResolveBlock > 0 && latestCreatorResolveBlock >= latestDisputeBlock;

      creatorDecisionDeadlineTs = proposalDisputeDecisionDeadline > 0
        ? proposalDisputeDecisionDeadline
        : 0;
      creatorDisputeResolved = false;
      creatorDisputeTimedOut = false;
      creatorDisputeOutcome = proposalDisputeOutcome === 1 || proposalDisputeOutcome === 2 ? proposalDisputeOutcome as 1 | 2 : null;

      if (shouldLoadResolutionLogs) {
        el.resolutionDetails.innerHTML = `
          <div class="mono" style="font-size:.67rem; color:var(--muted); margin-bottom:8px;">Resolution Timeline</div>
          <div class="resolution-timeline-grid">
            <div class="resolution-timeline-card">
              <div class="resolution-timeline-title mono">Proposal - ${yesLabelEsc}</div>
              <div class="resolution-timeline-main mono">${yesProposalLog ? `By ${yesProposalWho} · ${outcomeText(1)}` : 'None yet'}</div>
              <div class="resolution-timeline-sub mono">Reason: ${yesProposalReasonText || (proposalReasonUnsupported ? 'not emitted by this adapter' : 'none provided')}</div>
              <div class="resolution-timeline-sub mono">${yesProposalLog ? `tx ${shortAddr(String((yesProposalLog as any).transactionHash))} · #${(yesProposalLog as any).blockNumber}` : ''}</div>
            </div>
            <div class="resolution-timeline-card">
              <div class="resolution-timeline-title mono">Proposal - ${noLabelEsc}</div>
              <div class="resolution-timeline-main mono">${noProposalLog ? `By ${noProposalWho} · ${outcomeText(2)}` : 'None yet'}</div>
              <div class="resolution-timeline-sub mono">Reason: ${noProposalReasonText || (proposalReasonUnsupported ? 'not emitted by this adapter' : 'none provided')}</div>
              <div class="resolution-timeline-sub mono">${noProposalLog ? `tx ${shortAddr(String((noProposalLog as any).transactionHash))} · #${(noProposalLog as any).blockNumber}` : ''}</div>
            </div>
            <div class="resolution-timeline-card">
              <div class="resolution-timeline-title mono">Proposal Summary</div>
              <div class="resolution-timeline-main mono">${latestProposal ? `Latest by ${proposalWho} · ${escapeHtml(outcomeText(proposalOutcome || 0))}` : 'None yet'}</div>
              <div class="resolution-timeline-sub mono">Slots · ${yesLabelEsc} ${yesSlotLabel} · ${noLabelEsc} ${noSlotLabel}</div>
              <div class="resolution-timeline-sub mono">${latestProposal ? `tx ${shortAddr(String(latestProposal.transactionHash))} · #${latestProposal.blockNumber}` : ''}</div>
            </div>
            <div class="resolution-timeline-card">
              <div class="resolution-timeline-title mono">Dispute</div>
              <div class="resolution-timeline-main mono">${latestDispute ? `By ${disputeWho}` : 'None yet'}</div>
              <div class="resolution-timeline-sub mono">Reason: ${disputeReasonText || 'none provided'}</div>
              <div class="resolution-timeline-sub mono">${latestDispute ? `tx ${shortAddr(String(latestDispute.transactionHash))} · #${latestDispute.blockNumber}` : ''}</div>
            </div>
            <div class="resolution-timeline-card">
              <div class="resolution-timeline-title mono">Creator Decision</div>
              <div class="resolution-timeline-main mono">${latestDisputeResolvedByCreator ? `By ${creatorResolveWho} · ${escapeHtml(outcomeText(creatorResolveOutcome))}` : 'Pending'}</div>
              <div class="resolution-timeline-sub mono">${latestDisputeResolvedByCreator ? `tx ${shortAddr(String((latestDisputeResolvedByCreator as any).transactionHash))} · #${(latestDisputeResolvedByCreator as any).blockNumber}` : ''}</div>
            </div>
            <div class="resolution-timeline-card">
              <div class="resolution-timeline-title mono">Appeal</div>
              <div class="resolution-timeline-main mono">${appealReasonText || 'None yet'}</div>
              <div class="resolution-timeline-sub mono">${latestAppealReason ? `tx ${shortAddr(String(latestAppealReason.transactionHash))} · #${latestAppealReason.blockNumber}` : ''}</div>
            </div>
            <div class="resolution-timeline-card">
              <div class="resolution-timeline-title mono">Finalization</div>
              <div class="resolution-timeline-main mono">${latestFinalized ? `Finalized as ${escapeHtml(outcomeText(finalizedOutcome || 0))}` : 'Pending'}</div>
              <div class="resolution-timeline-sub mono">${latestFinalized ? `tx ${shortAddr(String(latestFinalized.transactionHash))} · #${latestFinalized.blockNumber}` : ''}</div>
            </div>
          </div>
        `;
      } else {
        el.resolutionDetails.innerHTML = '<div class="mono" style="font-size:.67rem; color:var(--muted);">Timeline logs load when Resolution/Creator/Court is open.</div>';
      }

      const finalized = Boolean(resolution[0]);
      isResolutionFinalized = finalized;
      settlementMismatch = finalized && !isMarketSettled;
      const resolvableTimeReached = sec <= 0;

      const assertionRow = assertion as [boolean, bigint, `0x${string}`, boolean, number, Address, number, bigint, boolean, Address];
      const timingSnapshotRow = disputeTimingSnapshot as [boolean, bigint, bigint];
      const emergencyRulingRow = emergencyRuling as [boolean, number, bigint];
      const pendingProposalRow = pendingProposal as [Address, number, bigint, boolean];

      const disputeWindowSec = Number(disputeWindowValue || 0n);
      const escalationTimeoutSec = Number(escalationTimeoutValue || 0n);
      const emergencyWindowSec = Number(emergencyWindowValue || 0n);
      const assertionExists = Boolean(assertionRow?.[0]);
      const proposer = String(assertionRow?.[5] || '');
      const hasProposal = Boolean(proposer && !/^0x0{40}$/i.test(proposer));
      const disputer = String(assertionRow?.[9] || '');
      const proposedOutcome = Number(assertionRow?.[6] || 0);
      const proposedAt = Number(assertionRow?.[7] || 0n);
      const disputed = Boolean(assertionRow?.[8]);
      creatorDisputeResolved = Boolean(disputed && !finalized && creatorResolvedLatestDispute);
      creatorDisputeTimedOut = Boolean(disputed && !finalized && creatorDecisionDeadlineTs > 0 && nowTs > creatorDecisionDeadlineTs && !creatorDisputeResolved);
      const snapshotExists = Boolean(timingSnapshotRow?.[0]);
      const caseEscalationTimeout = snapshotExists ? Number(timingSnapshotRow?.[1] || 0n) : escalationTimeoutSec;
      const caseEmergencyWindow = snapshotExists ? Number(timingSnapshotRow?.[2] || 0n) : emergencyWindowSec;
      const emergencyExists = Boolean(emergencyRulingRow?.[0]);
      const emergencyOutcome = Number(emergencyRulingRow?.[1] || 0);
      const emergencySubmittedAt = Number(emergencyRulingRow?.[2] || 0n);
      const pendingProposer = String(pendingProposalRow?.[0] || '');
      const pendingOutcome = Number(pendingProposalRow?.[1] || 0);
      const pendingSubmittedAt = Number(pendingProposalRow?.[2] || 0n);
      const adapterAccepted = Boolean(isProposalAccepted);
      const pendingState = Boolean(hasPendingProposal);
      const acceptedProposalFinalStage = adapterAccepted && !finalized;
      const secondsToFinalize = Number(timeToFinalization || 0n);
      const acceptedFinalizationOpen = acceptedProposalFinalStage && secondsToFinalize <= 0;
      const regularFinalizationOpen = !acceptedProposalFinalStage && resolvableTimeReached;
      const finalizationWindowOpen = !settlementMismatch && !finalized && (acceptedFinalizationOpen || regularFinalizationOpen);
      const walletIsCreator = Boolean(
        walletState.account && creatorAddress && walletState.account.toLowerCase() === creatorAddress.toLowerCase()
      );
      const walletIsLiquidityProvider = Boolean(
        walletState.account
          && liquidityProviderAddress
          && walletState.account.toLowerCase() === String(liquidityProviderAddress).toLowerCase()
      );
      const acceptedOutcomeForDispute = (proposedOutcome === 1 || proposedOutcome === 2) ? proposedOutcome as 1 | 2 : null;

      console.info('[BoxBet] creator controls snapshot', {
        marketId: currentMarketId,
        marketAddress,
        creatorAddress,
        liquidityProviderAddress,
        wallet: walletState.account,
        walletIsCreator,
        walletIsLiquidityProvider,
        finalized,
        disputed,
        pendingState,
        pendingProposer,
        pendingOutcome,
        pendingSubmittedAt,
        adapterAccepted,
      });

      el.creatorStatus.innerHTML = [
        `Creator ${displayAddr(String(creatorAddress || ''), shortAddr)}`,
        `Liquidity provider ${displayAddr(String(liquidityProviderAddress || ''), shortAddr)}`,
        `Wallet ${walletState.account ? shortAddr(walletState.account) : 'not connected'} · ${walletIsCreator ? 'creator' : 'not creator'}${walletIsLiquidityProvider ? ' · LP' : ''}`,
        `Collateral ${escapeHtml(collateralMeta.name)} (${escapeHtml(collateralMeta.symbol)})`,
      ].join('<br/>');

      cachedYesProposalReason = yesProposalReasonText || '';
      cachedNoProposalReason = noProposalReasonText || '';
      cachedProposalReason = cachedYesProposalReason || cachedNoProposalReason;
      cachedProposalReasonUnsupported = proposalReasonUnsupported;
      cachedProposalOutcome = isZeroAddress(pendingProposer) ? null : pendingOutcome;
      cachedProposalProposer = pendingProposer || '';
      cachedYesProposalProposer = hasYesSlot ? yesSlotProposer : '';
      cachedNoProposalProposer = hasNoSlot ? noSlotProposer : '';
      syncCreatorDecisionSelector(hasYesSlot, hasNoSlot);

      // Default hidden state; only active-dispute branches can reveal this card.
      el.creatorDisputeCard.style.display = 'none';
      el.creatorDisputeBadges.innerHTML = '';
      el.creatorDisputeCountdown.textContent = '';
      el.creatorResolveKeepBtn.disabled = true;
      el.creatorResolveDisputedBtn.disabled = true;

      if (isZeroAddress(pendingProposer)) {
        el.creatorProposalBlock.hidden = true;
        el.creatorNoPending.hidden = false;
        el.creatorPending.textContent = '';
        el.creatorPendingReason.hidden = true;
      } else {
        const acceptanceText = adapterAccepted ? 'Accepted by creator' : (pendingState ? 'Pending creator action' : 'Accepted');
        const finalizeText = adapterAccepted
          ? (secondsToFinalize > 0 ? `Finalization in ${fmtDuration(secondsToFinalize)}` : 'Finalization window open')
          : 'Finalization timer starts after creator acceptance';

        const outcomeNum = hasYesSlot && hasNoSlot
          ? selectedCreatorDecisionOutcome()
          : Number(pendingOutcome ?? 0);
        const selectedPendingProposer = outcomeNum === 1 ? yesSlotProposer : noSlotProposer;
        const selectedReasonRaw = outcomeNum === 1 ? cachedYesProposalReason : cachedNoProposalReason;

        el.creatorPendingSlotYesTitle.textContent = yesOutcomeLabel;
        el.creatorPendingSlotNoTitle.textContent = noOutcomeLabel;
        el.creatorPendingSlotYesProposer.textContent = yesSlotLabel;
        el.creatorPendingSlotNoProposer.textContent = noSlotLabel;
        el.creatorPendingSlotYesReason.textContent = `Reason: ${yesProposalReasonText || (proposalReasonUnsupported ? 'not emitted by this adapter' : 'none provided')}`;
        el.creatorPendingSlotNoReason.textContent = `Reason: ${noProposalReasonText || (proposalReasonUnsupported ? 'not emitted by this adapter' : 'none provided')}`;

        el.creatorPendingOutcomePill.className = outcomeNum === 1 ? 'meta-chip live' : 'meta-chip resolving';
        el.creatorPendingOutcomePill.textContent = outcomeText(outcomeNum || 0);
        el.creatorPendingProposer.textContent = selectedPendingProposer
          ? `Proposer ${shortAddr(String(selectedPendingProposer))}`
          : 'Proposer n/a';
        el.creatorPending.textContent = `${acceptanceText} · ${finalizeText}`;

        const reason = selectedReasonRaw.trim();
        if (reason) {
          el.creatorPendingReason.textContent = `"${reason}"`;
          el.creatorPendingReason.hidden = false;
        } else if (cachedProposalReasonUnsupported) {
          el.creatorPendingReason.textContent = 'Reason is not emitted by this adapter/event set for this proposal path.';
          el.creatorPendingReason.hidden = false;
        } else {
          el.creatorPendingReason.hidden = true;
        }

        el.creatorProposalBlock.hidden = false;
        el.creatorNoPending.hidden = true;
      }

      creatorDisputeAcceptedOutcome = acceptedOutcomeForDispute;
      const disputeOutcomeForDecision = creatorDisputeOutcome
        ?? (creatorDisputeAcceptedOutcome === 1 ? 2 : creatorDisputeAcceptedOutcome === 2 ? 1 : null);
      const isActiveDispute = Boolean(disputed && !finalized && !creatorDisputeResolved && !creatorDisputeTimedOut);
      if (isActiveDispute) {
        const acceptedLabel = creatorDisputeAcceptedOutcome ? outcomeText(creatorDisputeAcceptedOutcome) : 'accepted';
        const disputedLabel = disputeOutcomeForDecision ? outcomeText(disputeOutcomeForDecision) : 'disputed';
        el.creatorDisputeBadges.innerHTML = [
          '<span class="meta-chip resolving">Dispute active</span>',
          `<span class="meta-chip">Accepted: ${escapeHtml(acceptedLabel)}</span>`,
          `<span class="meta-chip">Disputed: ${escapeHtml(disputedLabel)}</span>`,
        ].join('');
        el.creatorDisputeCountdown.textContent = creatorDecisionDeadlineTs > 0
          ? `Creator decision deadline ${fmtTs(creatorDecisionDeadlineTs, nowTs)}.`
          : `Creator decision window is open (${fmtDuration(creatorDecisionWindowSec)} from dispute submission).`;
        const canResolveDispute = Boolean(walletIsCreator && creatorDisputeAcceptedOutcome && disputeOutcomeForDecision);
        const acceptedActionLabel = creatorDisputeAcceptedOutcome ? outcomeText(creatorDisputeAcceptedOutcome) : 'Accepted';
        const disputedActionLabel = disputeOutcomeForDecision ? outcomeText(disputeOutcomeForDecision) : 'Disputed';
        el.creatorResolveKeepBtn.textContent = `Keep Accepted (${acceptedActionLabel})`;
        el.creatorResolveDisputedBtn.textContent = `Accept Disputed (${disputedActionLabel})`;
        el.creatorResolveKeepBtn.disabled = !canResolveDispute;
        el.creatorResolveDisputedBtn.disabled = !canResolveDispute;
        el.creatorDisputeCard.style.display = 'grid';
      }

      const creatorCanAct = walletIsCreator && !finalized && !disputed && pendingState && !resolvableTimeReached;
      acceptedProposalBlocksTrading = acceptedProposalFinalStage;
      el.creatorAcceptBtn.disabled = !creatorCanAct;
      el.creatorRejectBtn.disabled = !creatorCanAct;

      if (!walletState.account) {
        el.creatorActionsHelp.textContent = 'Connect wallet to use creator controls.';
      } else if (!walletIsCreator) {
        el.creatorActionsHelp.textContent = 'Only this market creator can accept or reject a pending proposal.';
      } else if (finalized) {
        el.creatorActionsHelp.textContent = 'Market is finalized. Creator controls are closed.';
      } else if (disputed) {
        if (creatorDisputeResolved) {
          el.creatorActionsHelp.textContent = 'Dispute decision submitted by creator. Finalize to complete settlement.';
        } else if (creatorDisputeTimedOut) {
          el.creatorActionsHelp.textContent = 'Creator dispute decision window expired. Market can only finalize through timeout path.';
        } else {
          el.creatorActionsHelp.textContent = 'Dispute is active. Creator must choose final outcome before the decision deadline.';
        }
      } else if (resolvableTimeReached) {
        el.creatorActionsHelp.textContent = 'Creator acceptance is early-only and closes at resolve time. Pending proposals can be rejected by creator or withdrawn by proposer.';
      } else if (!pendingState) {
        el.creatorActionsHelp.textContent = adapterAccepted
          ? 'Proposal already accepted. Wait for dispute window or finalize when available.'
          : 'No pending proposal to accept/reject.';
      } else {
        el.creatorActionsHelp.textContent = 'Accept starts dispute window. Reject cancels the proposal and refunds proposer bond.';
      }

      el.appealTargetHint.textContent = hasProposal
        ? `Appeal target outcome: ${outcomeText(proposedOutcome)} (from current proposal/round)`
        : 'Appeal target outcome: none yet (no active proposal)';

      let bondAllowanceReady = false;
      let reputationControllerAddress: Address | null = null;
      let baseBondAmount = 0n;
      if (shouldLoadResolutionDetails) {
      try {
        const { asset: bondAssetAddress, amount: bondAmountValue } = await getResolutionBondConfig();

        const bondAllowanceValue = walletState.account
          ? await readClient.readContract({
            address: bondAssetAddress as Address,
            abi: erc20Abi,
            functionName: 'allowance',
            args: [walletState.account as Address, DEPLOYMENT.adapter as Address],
          })
          : 0n;

        try {
          const bondMeta = await resolveCollateralMeta(String(bondAssetAddress));
          const bondLogo = await resolveTokenLogoUrl(String(bondAssetAddress));
          const badgeLogo = bondLogo ?? `${import.meta.env.BASE_URL}images/coin.svg`;
          el.resolutionBondAsset.innerHTML = `<span class="token-inline"><img src="${badgeLogo}" alt="${escapeHtml(bondMeta.symbol)}" class="token-logo"/>${escapeHtml(bondMeta.name)} (${escapeHtml(bondMeta.symbol)})</span>`;
          el.resolutionBondAmount.textContent = `${fmtToken(BigInt(bondAmountValue as bigint), bondMeta.decimals)} ${bondMeta.symbol}`;
          el.resolutionBondAllowance.textContent = `${fmtToken(BigInt(bondAllowanceValue as bigint), bondMeta.decimals)} ${bondMeta.symbol}`;
        } catch {
          el.resolutionBondAsset.textContent = shortAddr(String(bondAssetAddress));
          el.resolutionBondAmount.textContent = fmtToken(BigInt(bondAmountValue as bigint));
          el.resolutionBondAllowance.textContent = fmtToken(BigInt(bondAllowanceValue as bigint));
        }
        bondAllowanceReady = BigInt(bondAllowanceValue as bigint) >= BigInt(bondAmountValue as bigint);
        baseBondAmount = BigInt(bondAmountValue as bigint);

        el.resolutionBondStatus.textContent = '';
      } catch {
        el.resolutionBondAsset.textContent = '-';
        el.resolutionBondAmount.textContent = '-';
        el.resolutionBondAllowance.textContent = '-';
        el.resolutionBondStatus.textContent = '';
      }

      const reputationControllerLookups: Array<{ address: Address; abi: typeof adapterAbi | typeof marketAbi | typeof factoryAbi; label: string }> = [
        { address: DEPLOYMENT.adapter as Address, abi: adapterAbi, label: 'adapter' },
        { address: marketAddress as Address, abi: marketAbi, label: 'market' },
        { address: DEPLOYMENT.factory as Address, abi: factoryAbi, label: 'factory' },
      ];
      const reputationLookupResults: string[] = [];

      for (const lookup of reputationControllerLookups) {
        try {
          const address = (await readClient.readContract({
            address: lookup.address,
            abi: lookup.abi,
            functionName: 'reputationController',
          })) as Address;
          reputationLookupResults.push(`${lookup.label}: ${isZeroAddress(address) ? 'none' : shortAddr(address)}`);
          if (!isZeroAddress(address)) {
            reputationControllerAddress = address;
            break;
          }
        } catch {
          reputationLookupResults.push(`${lookup.label}: unreadable`);
          continue;
        }
      }

      const reputationSourceSummary = reputationLookupResults.join(' · ');
      if (reputationControllerAddress) {
        try {
          const detailLines: string[] = [];
          let summary = 'Reputation is active for this market. Your score changes the bond required to propose or dispute.';

          if (hasProposal && !isZeroAddress(proposer)) {
            const [proposerScoreRead, proposerBondRead] = await Promise.all([
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'scores',
                args: [proposer as Address],
              }),
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'getRequiredBond',
                args: [proposer as Address, baseBondAmount, false],
              }),
            ]);

            const proposerScore = Number(proposerScoreRead || 0n) || 50;
            const proposerBond = proposerBondRead as bigint;
            detailLines.push(`Current proposer ${shortAddr(proposer)} · score ${proposerScore}/100 (${reputationLabel(proposerScore)}) · required proposer bond $${fmtToken(proposerBond)}`);
          }

          if (walletState.account) {
            const [userScoreRead, userProposerBondRead, userDisputeBondRead, proposerCooldownRead, disputerCooldownRead, proposerStreakRead, disputerStreakRead] = await Promise.all([
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'scores',
                args: [walletState.account as Address],
              }),
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'getRequiredBond',
                args: [walletState.account as Address, baseBondAmount, false],
              }),
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'getRequiredBond',
                args: [walletState.account as Address, baseBondAmount, true],
              }),
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'proposerCooldownUntil',
                args: [walletState.account as Address],
              }),
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'disputerCooldownUntil',
                args: [walletState.account as Address],
              }),
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'proposerIncorrectStreak',
                args: [walletState.account as Address],
              }),
              readClient.readContract({
                address: reputationControllerAddress,
                abi: reputationAbi,
                functionName: 'disputerIncorrectStreak',
                args: [walletState.account as Address],
              }),
            ]);

            const userScore = Number(userScoreRead || 0n) || 50;
            const userProposerBond = userProposerBondRead as bigint;
            const userDisputeBond = userDisputeBondRead as bigint;
            const proposerCooldownUntil = Number(proposerCooldownRead || 0n);
            const disputerCooldownUntil = Number(disputerCooldownRead || 0n);
            const proposerStreak = Number(proposerStreakRead || 0n);
            const disputerStreak = Number(disputerStreakRead || 0n);
            summary = `Score ${userScore}/100 · ${reputationLabel(userScore)}`;
            detailLines.push(`Propose bond $${fmtToken(userProposerBond)}`);
            detailLines.push(`Dispute bond $${fmtToken(userDisputeBond)}`);
            detailLines.push(`Proposer streak ${proposerStreak}`);
            detailLines.push(`Disputer streak ${disputerStreak}`);
            detailLines.push(`Proposer cooldown ${proposerCooldownUntil > nowTs ? fmtTs(proposerCooldownUntil, nowTs) : 'none'}`);
            detailLines.push(`Disputer cooldown ${disputerCooldownUntil > nowTs ? fmtTs(disputerCooldownUntil, nowTs) : 'none'}`);
          } else {
            summary = 'Reputation active · connect wallet';
            detailLines.push('Wallet needed for personalized bonds');
          }

          el.resolutionRisk.innerHTML = renderRiskPanel(
            summary,
            detailLines,
            'Dispute risk: if the final result matches the proposal, reputation can drop and cooldown can apply.',
            `Reputation source ${shortAddr(reputationControllerAddress)} · ${reputationSourceSummary}`,
          );
        } catch {
          el.resolutionRisk.innerHTML = renderRiskPanel(
            'Reputation controller present · details unavailable',
            ['Bond may differ from base $50'],
            'Reconnect or refresh before bonding.',
            `Reputation source ${shortAddr(reputationControllerAddress)} · ${reputationSourceSummary}`,
          );
        }
      } else {
        el.resolutionRisk.innerHTML = renderRiskPanel(
          'No reputation controller wired',
          walletState.account
            ? [
              `Base bond $${fmtToken(baseBondAmount)}`,
              'Wallet-specific score unavailable',
            ]
            : ['Connect wallet for bond comparison'],
          'Adapter falls back to the base bond when no controller is wired.',
          `Sources checked: ${reputationSourceSummary}`,
        );
      }
      } else {
        bondAllowanceReady = true;
      }

      const walletCanSubmitResolutionActions = Boolean(walletState.account);
      el.resolutionOutcome.disabled = finalized || !walletCanSubmitResolutionActions;
      el.disputeOutcome.disabled = finalized || !resolvableTimeReached || !walletCanSubmitResolutionActions;
      el.proposeBtn.disabled = finalized || !walletCanSubmitResolutionActions;
      el.disputeBtn.disabled = finalized || !resolvableTimeReached || !walletCanSubmitResolutionActions;
      el.finalizeBtn.disabled = !finalizationWindowOpen;
      el.emergencyBtn.disabled = finalized;
      finalizeAvailable = finalizationWindowOpen;

      if (settlementMismatch) {
        el.resolutionHelp.textContent = 'Adapter is finalized but this market is not settled. This legacy mismatch blocks finalize/claim for this market instance.';
      } else if (finalized) {
        el.resolutionHelp.textContent = 'Resolution finalized. Propose, dispute, and finalize are disabled.';
      } else if (acceptedProposalFinalStage && secondsToFinalize > 0) {
        el.resolutionHelp.textContent = `Accepted proposal is in final stage. Finalize unlocks in ${fmtDuration(secondsToFinalize)}.`;
      } else if (acceptedProposalFinalStage && secondsToFinalize <= 0) {
        el.resolutionHelp.textContent = 'Accepted proposal finalization window is open. Click Finalize now.';
      } else if (!resolvableTimeReached) {
        el.resolutionHelp.textContent = 'Pre-resolve: any wallet can submit a proposal. The market creator handles accept/reject in the Creator tab. Dispute/finalize unlock after resolve time.';
      } else if (latestProposal && latestDispute) {
        el.resolutionHelp.textContent = 'Proposal was disputed. Wait for dispute window to close, then click Finalize to open/progress court rounds.';
      } else if (hasProposal && !latestDispute) {
        el.resolutionHelp.textContent = 'A proposal already exists. Any wallet can dispute if needed, otherwise finalize when the dispute window closes.';
      } else if (latestProposal && !latestDispute) {
        el.resolutionHelp.textContent = 'Proposal is submitted. Dispute is open to any wallet, and finalization follows when appropriate.';
      } else if (!bondAllowanceReady) {
        el.resolutionHelp.textContent = 'Proposing needs adapter bond approval. Connect wallet, then click Propose and approve inside the confirmation modal.';
      } else {
        el.resolutionHelp.textContent = resolvableTimeReached
          ? 'Resolution is open. Any wallet can propose or dispute here, then finalize follows.'
          : 'Pre-resolve proposal is open. Any wallet can submit proposals, and the creator can accept/reject in the Creator tab.';
      }

      const resolveAt = Number(resolveAfter || 0n);
      const disputeDeadline = proposedAt > 0 ? proposedAt + disputeWindowSec : 0;
      const escalationDeadline = disputeDeadline > 0 ? disputeDeadline + caseEscalationTimeout : 0;
      const emergencyDeadline = escalationDeadline > 0 ? escalationDeadline + caseEmergencyWindow : 0;

      let phase = 'Awaiting proposal';
      if (settlementMismatch) {
        phase = 'Settlement mismatch';
      } else if (finalized) {
        phase = 'Finalized';
      } else if (acceptedProposalFinalStage) {
        phase = 'Final';
      } else if (!resolvableTimeReached) {
        phase = 'Pre-resolve';
      } else if (!assertionExists) {
        phase = 'Awaiting proposal';
      } else if (!disputed) {
        if (disputeDeadline > 0 && nowTs <= disputeDeadline) phase = 'Dispute window';
        else phase = 'Finalize window';
      } else if (escalationDeadline > 0 && nowTs <= escalationDeadline) {
        phase = 'Escalation period';
      } else if (emergencyDeadline > 0 && nowTs <= emergencyDeadline) {
        phase = 'Grand judge emergency window';
      } else {
        phase = 'Timeout/fallback window';
      }

      phaseBadgeText = phase;
      phaseBadgeTone =
        phase === 'Pre-resolve' || phase === 'Awaiting proposal'
          ? 'live'
          : 'resolving';

      el.courtDetails.innerHTML = `
        <div class="timing-grid">
          <div class="timing-row"><span class="k">Phase</span><span class="v">${phase}</span></div>
          <div class="timing-row"><span class="k">Proposer</span><span class="v">${displayAddr(proposer, shortAddr)}</span></div>
          <div class="timing-row"><span class="k">Disputer</span><span class="v">${displayAddr(disputer, shortAddr)}</span></div>
          <div class="timing-row"><span class="k">Grand Judge</span><span class="v">${displayAddr(String(grandJudgeValue || ''), shortAddr)}</span></div>

          <div class="timing-row"><span class="k">Resolve After</span><span class="v">${fmtTs(resolveAt, nowTs)}</span></div>
          <div class="timing-row"><span class="k">Proposal At</span><span class="v">${fmtTs(proposedAt, nowTs)}</span></div>
          <div class="timing-row"><span class="k">Dispute Deadline</span><span class="v">${fmtTs(disputeDeadline, nowTs)}</span></div>
          <div class="timing-row"><span class="k">Escalation Deadline</span><span class="v">${fmtTs(escalationDeadline, nowTs)}</span></div>
          <div class="timing-row"><span class="k">Emergency Deadline</span><span class="v">${fmtTs(emergencyDeadline, nowTs)}</span></div>

          <div class="timing-row"><span class="k">Assertion Exists</span><span class="v">${assertionExists ? 'Yes' : 'No'}</span></div>
          <div class="timing-row"><span class="k">Market Settled</span><span class="v">${isMarketSettled ? 'Yes' : 'No'}</span></div>
          <div class="timing-row"><span class="k">Proposed Outcome</span><span class="v">${proposedAt > 0 ? outcomeText(proposedOutcome) : 'n/a'}</span></div>
          <div class="timing-row"><span class="k">Disputed</span><span class="v">${disputed ? 'Yes' : 'No'}</span></div>
          <div class="timing-row"><span class="k">Emergency Ruling</span><span class="v">${emergencyExists ? `${outcomeText(emergencyOutcome)} at ${fmtTs(emergencySubmittedAt, nowTs)}` : 'none'}</span></div>
          <div class="timing-row"><span class="k">Timing Source</span><span class="v">${snapshotExists ? 'Snapshot at dispute time' : 'Current adapter params'}</span></div>
        </div>
      `;

      if (shouldLoadResolutionDetails) {
        await refreshCourtJurorStatus(currentMarketId, nowTs, {
          disputed,
          disputeDeadline,
          escalationDeadline,
          emergencyDeadline,
        });
      }
      } catch {
        const walletIsCreator = Boolean(
          walletState.account && creatorAddress && walletState.account.toLowerCase() === creatorAddress.toLowerCase()
        );
        const resolvableTimeReached = sec <= 0;

        let finalized = false;
        let finalizedOutcome = 0;
        let pendingOutcome = 0;
        let yesProposer = '';
        let noProposer = '';
        let isTradingPaused = false;

        try {
          const resolution = (await readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getResolution',
            args: [currentMarketId as `0x${string}`],
          })) as [boolean, number];
          finalized = Boolean(resolution?.[0]);
          finalizedOutcome = Number(resolution?.[1] || 0);
        } catch {
          finalized = false;
          finalizedOutcome = 0;
        }

        try {
          pendingOutcome = Number((await readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getPendingOutcome',
            args: [currentMarketId as `0x${string}`],
          })) || 0);
        } catch {
          pendingOutcome = 0;
        }

        try {
          const proposals = (await readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getProposals',
            args: [currentMarketId as `0x${string}`],
          })) as [Address, boolean, Address, boolean, number];
          yesProposer = String(proposals?.[0] || '');
          noProposer = String(proposals?.[2] || '');
        } catch {
          yesProposer = '';
          noProposer = '';
        }

        try {
          isTradingPaused = Boolean(await readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'isTradingPaused',
            args: [currentMarketId as `0x${string}`],
          }));
        } catch {
          isTradingPaused = false;
        }

        isResolutionFinalized = finalized;
        settlementMismatch = finalized && !isMarketSettled;
        acceptedProposalBlocksTrading = isTradingPaused && !finalized;
        finalizeAvailable = (!finalized && resolvableTimeReached) || settlementMismatch;

        const hasPendingProposal = pendingOutcome === 1 || pendingOutcome === 2;
        const hasYesSlot = hasLiveProposer(yesProposer);
        const hasNoSlot = hasLiveProposer(noProposer);
        const pendingProposer = pendingOutcome === 1 ? yesProposer : pendingOutcome === 2 ? noProposer : '';
        const yesLabelEsc = escapeHtml(outcomeText(1));
        const noLabelEsc = escapeHtml(outcomeText(2));
        cachedProposalOutcome = hasPendingProposal ? pendingOutcome : null;
        cachedProposalProposer = hasPendingProposal ? pendingProposer : '';
        cachedYesProposalProposer = hasYesSlot ? yesProposer : '';
        cachedNoProposalProposer = hasNoSlot ? noProposer : '';
        syncCreatorDecisionSelector(hasYesSlot, hasNoSlot);

        el.resolutionState.innerHTML = finalized
          ? `<span class="resolution-pill resolution-pill-live">Finalized: ${escapeHtml(outcomeText(finalizedOutcome))}</span>`
          : '<span class="resolution-pill resolution-pill-muted">Open / pending proposal state</span>';

        el.resolutionDetails.innerHTML = `
          <div class="mono" style="font-size:.67rem; color:var(--muted); margin-bottom:8px;">Resolution Timeline</div>
          <div class="mono" style="font-size:.68rem; line-height:1.5; color:var(--muted);">
            Pending proposal: ${hasPendingProposal ? outcomeText(pendingOutcome) : 'none'}<br/>
            Proposer: ${pendingProposer && !/^0x0{40}$/i.test(pendingProposer) ? shortAddr(pendingProposer) : 'n/a'}<br/>
            Slots: ${yesLabelEsc} ${yesProposer && !/^0x0{40}$/i.test(yesProposer) ? shortAddr(yesProposer) : 'none'} · ${noLabelEsc} ${noProposer && !/^0x0{40}$/i.test(noProposer) ? shortAddr(noProposer) : 'none'}
          </div>
        `;

        el.creatorStatus.innerHTML = [
          `Creator ${displayAddr(String(creatorAddress || ''), shortAddr)}`,
          `Wallet ${walletState.account ? shortAddr(walletState.account) : 'not connected'} · ${walletIsCreator ? 'creator' : 'not creator'}`,
          `Collateral ${escapeHtml(collateralMeta.name)} (${escapeHtml(collateralMeta.symbol)})`,
        ].join('<br/>');

        el.creatorDisputeCard.style.display = 'none';
        el.creatorDisputeBadges.innerHTML = '';
        el.creatorDisputeCountdown.textContent = '';
        el.creatorResolveKeepBtn.disabled = true;
        el.creatorResolveDisputedBtn.disabled = true;

        if (hasPendingProposal) {
          const outcomeNum = hasYesSlot && hasNoSlot
            ? selectedCreatorDecisionOutcome()
            : Number(pendingOutcome ?? 0);
          const yesReasonLine = cachedYesProposalReason.trim() || (cachedProposalReasonUnsupported ? 'not emitted by this adapter' : 'none provided');
          const noReasonLine = cachedNoProposalReason.trim() || (cachedProposalReasonUnsupported ? 'not emitted by this adapter' : 'none provided');
          const yesSlotLabel = yesProposer && !/^0x0{40}$/i.test(yesProposer) ? shortAddr(yesProposer) : 'none';
          const noSlotLabel = noProposer && !/^0x0{40}$/i.test(noProposer) ? shortAddr(noProposer) : 'none';
          el.creatorPendingSlotYesProposer.textContent = yesSlotLabel;
          el.creatorPendingSlotNoProposer.textContent = noSlotLabel;
          el.creatorPendingSlotYesReason.textContent = `Reason: ${yesReasonLine}`;
          el.creatorPendingSlotNoReason.textContent = `Reason: ${noReasonLine}`;
          applyProposalCardSelection(outcomeNum as 1 | 2);
          el.creatorPending.textContent = '';
          el.creatorProposalBlock.hidden = false;
          el.creatorNoPending.hidden = true;
        } else {
          el.creatorProposalBlock.hidden = true;
          el.creatorNoPending.hidden = false;
        }

        const creatorCanAct = walletIsCreator && !finalized && hasPendingProposal && !resolvableTimeReached;
        el.creatorAcceptBtn.disabled = !creatorCanAct;
        el.creatorRejectBtn.disabled = !creatorCanAct;

        if (!walletState.account) {
          el.creatorActionsHelp.textContent = 'Connect wallet to use creator controls.';
        } else if (!walletIsCreator) {
          el.creatorActionsHelp.textContent = 'Only this market creator can accept or reject a pending proposal.';
        } else if (finalized) {
          el.creatorActionsHelp.textContent = 'Market is finalized. Creator controls are closed.';
        } else if (resolvableTimeReached) {
          el.creatorActionsHelp.textContent = 'Creator acceptance is only available before resolve time.';
        } else if (!hasPendingProposal) {
          el.creatorActionsHelp.textContent = 'No pending proposal to accept or reject.';
        } else {
          el.creatorActionsHelp.textContent = 'Accept or reject the current pending proposal.';
        }

        const walletCanSubmitResolutionActions = Boolean(walletState.account);
        el.resolutionOutcome.disabled = finalized || !walletCanSubmitResolutionActions;
        el.disputeOutcome.disabled = finalized || !resolvableTimeReached || !walletCanSubmitResolutionActions;
        el.proposeBtn.disabled = finalized || !walletCanSubmitResolutionActions;
        el.disputeBtn.disabled = finalized || !resolvableTimeReached || !walletCanSubmitResolutionActions;
        el.finalizeBtn.disabled = !finalizeAvailable;
        el.emergencyBtn.disabled = true;

        el.resolutionHelp.textContent = finalized
          ? 'Resolution finalized.'
          : hasPendingProposal
            ? 'Pending proposal detected. Creator can accept/reject, and opposite-side proposal can still be submitted when open.'
            : 'Resolution is open. Proposal and dispute actions are available.';

        try {
          const { asset: bondAssetAddress, amount: bondAmountValue } = await getResolutionBondConfig();
          const bondAllowanceValue = walletState.account
            ? await readClient.readContract({
              address: bondAssetAddress as Address,
              abi: erc20Abi,
              functionName: 'allowance',
              args: [walletState.account as Address, DEPLOYMENT.adapter as Address],
            })
            : 0n;

          try {
            const bondMeta = await resolveCollateralMeta(String(bondAssetAddress));
            const bondLogo = await resolveTokenLogoUrl(String(bondAssetAddress));
            const badgeLogo = bondLogo ?? `${import.meta.env.BASE_URL}images/coin.svg`;
            el.resolutionBondAsset.innerHTML = `<span class="token-inline"><img src="${badgeLogo}" alt="${escapeHtml(bondMeta.symbol)}" class="token-logo"/>${escapeHtml(bondMeta.name)} (${escapeHtml(bondMeta.symbol)})</span>`;
            el.resolutionBondAmount.textContent = `${fmtToken(BigInt(bondAmountValue as bigint), bondMeta.decimals)} ${bondMeta.symbol}`;
            el.resolutionBondAllowance.textContent = `${fmtToken(BigInt(bondAllowanceValue as bigint), bondMeta.decimals)} ${bondMeta.symbol}`;
          } catch {
            el.resolutionBondAsset.textContent = shortAddr(String(bondAssetAddress));
            el.resolutionBondAmount.textContent = fmtToken(BigInt(bondAmountValue as bigint));
            el.resolutionBondAllowance.textContent = fmtToken(BigInt(bondAllowanceValue as bigint));
          }
          el.resolutionBondStatus.textContent = '';
        } catch {
          el.resolutionBondAsset.textContent = '-';
          el.resolutionBondAmount.textContent = '-';
          el.resolutionBondAllowance.textContent = '-';
          el.resolutionBondStatus.textContent = '';
        }

        el.courtDetails.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted)">Court/juror panel is not active for this deployment.</div>';
        el.courtStatus.textContent = 'Court actions unavailable in this deployment.';
        el.appealTargetHint.textContent = 'Appeal target outcome: unavailable';
        el.appealCostHint.textContent = 'Your appeal cost now: unavailable';
        el.jurorCommitBtn.disabled = true;
        el.jurorRevealBtn.disabled = true;
        el.jurorAppealBtn.disabled = true;
        el.appealReason.disabled = true;
        setAppealAvailability(false, 'Appeals are unavailable in this deployment because no court oracle is configured.');
        phaseBadgeText = finalized ? 'Finalized' : (resolvableTimeReached ? 'Resolve window' : 'Pre-resolve');
        phaseBadgeTone = finalized ? 'resolving' : 'live';
      }
    } else {
      el.resolutionState.innerHTML = '<span class="resolution-pill resolution-pill-muted">Market ID unavailable</span>';
      el.resolutionHelp.textContent = 'Resolution actions require a valid market id.';
      el.resolutionDetails.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted)">No resolution timeline available.</div>';
      el.courtDetails.innerHTML = '<div class="mono" style="font-size:.68rem; color:var(--muted)">No court/juror timing available without market id.</div>';
      el.courtStatus.textContent = 'Court actions are unavailable without market id.';
      el.appealTargetHint.textContent = 'Appeal target outcome: unavailable';
      el.appealCostHint.textContent = 'Your appeal cost now: unavailable';
      el.jurorCommitBtn.disabled = true;
      el.jurorRevealBtn.disabled = true;
      el.jurorAppealBtn.disabled = true;
      el.appealReason.disabled = true;
      setAppealAvailability(false, 'Appeals require a valid market id and an active court oracle.');
      el.creatorStatus.textContent = 'Creator state unavailable (market id missing).';
      el.creatorProposalBlock.hidden = true;
      el.creatorNoPending.hidden = false;
      el.creatorPending.textContent = '';
      el.creatorDisputeCard.style.display = 'none';
      el.creatorDisputeBadges.innerHTML = '';
      el.creatorDisputeCountdown.textContent = '';
      el.creatorResolveKeepBtn.disabled = true;
      el.creatorResolveDisputedBtn.disabled = true;
      el.creatorActionsHelp.textContent = 'Creator controls require a valid market id.';
      el.creatorAcceptBtn.disabled = true;
      el.creatorRejectBtn.disabled = true;
      el.proposeBtn.disabled = true;
      el.disputeBtn.disabled = true;
      el.finalizeBtn.disabled = true;
      el.emergencyBtn.disabled = true;
      el.resolutionOutcome.disabled = true;
      el.disputeOutcome.disabled = true;
      finalizeAvailable = false;

      phaseBadgeText = 'Unavailable';
      phaseBadgeTone = '';
    }

    el.marketPhase.textContent = phaseBadgeText === 'Pre-resolve'
      ? 'LIVE'
      : phaseBadgeText === 'Final'
        ? 'FINAL'
        : `Phase: ${phaseBadgeText}`;
    el.marketPhase.classList.remove('live', 'resolving');
    if (phaseBadgeTone) el.marketPhase.classList.add(phaseBadgeTone);

    el.finalizeBtn.style.display = finalizeAvailable ? '' : 'none';
    el.finalizeBtn.classList.toggle('finalize-cta-live', finalizeAvailable);

    let claimTabVisible = isMarketSettled && isResolutionFinalized;
    if (claimTabVisible && walletConnected) {
      try {
        const [canClaim] = await readClient.readContract({
          address: marketAddress,
          abi: marketAbi,
          functionName: 'quoteClaim',
          args: [walletState.account as Address],
        }) as [boolean, bigint, bigint, bigint, bigint, bigint, number];
        claimTabVisible = Boolean(canClaim);
      } catch {
        claimTabVisible = false;
      }
    } else {
      claimTabVisible = false;
    }

    el.claimsTabBtn.style.display = claimTabVisible ? '' : 'none';
    if (!claimTabVisible) {
      const activePanel = appRoot.querySelector('.panel.active') as HTMLElement | null;
      if (activePanel?.id === 'claimsPanel') {
        const tradeTab = appRoot.querySelector('[data-tab="tradePanel"]') as HTMLButtonElement | null;
        const tradePanel = appRoot.querySelector('#tradePanel') as HTMLElement | null;
        appRoot.querySelectorAll('.tab').forEach((t) => t.classList.remove('active'));
        appRoot.querySelectorAll('.panel').forEach((p) => p.classList.remove('active'));
        tradeTab?.classList.add('active');
        tradePanel?.classList.add('active');
      }
    }

    const needsSoftCloseControls = sec <= 0 && !isResolutionFinalized && !settlementMismatch;
    const softCloseToggleRow = appRoot.querySelector('.soft-close-toggle') as HTMLElement | null;
    if (softCloseToggleRow) {
      softCloseToggleRow.style.display = needsSoftCloseControls ? '' : 'none';
    }
    if (!needsSoftCloseControls) {
      el.tradeOverride.checked = false;
    }

    const softCloseActive = needsSoftCloseControls && !el.tradeOverride.checked;
    const proposalTradingBlocked = acceptedProposalBlocksTrading && !settlementMismatch;
    const finalizedTradingClosed = isResolutionFinalized || isMarketSettled;
    const tradeActionBlocked = softCloseActive || settlementMismatch || proposalTradingBlocked || finalizedTradingClosed;
    el.tradeActionBtn.disabled = tradeActionBlocked;

    let tradeGuardMessage = '';
    if (finalizedTradingClosed) {
      tradeGuardMessage = 'Market finalized: trading is permanently closed.';
    } else if (settlementMismatch) {
      tradeGuardMessage = 'Settlement mismatch detected: adapter finalized while market remains unsettled. Click Finalize to settle the market and unlock claims.';
    } else if (proposalTradingBlocked) {
      tradeGuardMessage = 'Trading is paused: an early proposal has been accepted. Wait for dispute/finalization.';
    } else if (softCloseActive) {
      tradeGuardMessage = 'Soft-close active: resolve time reached. Enable override to keep trading until finalization.';
    } else if (needsSoftCloseControls && el.tradeOverride.checked) {
      tradeGuardMessage = 'Override enabled: trading allowed post-resolve until settlement finalization.';
    }

    suppressTradePreview = finalizedTradingClosed;
    tradeBlockedReason = finalizedTradingClosed
      ? 'Market finalized. Trading is closed.'
      : proposalTradingBlocked
        ? 'Trading is currently paused for this market because an accepted early proposal is active.'
        : settlementMismatch
          ? 'Trading preview disabled while settlement mismatch is active. Finalize first.'
          : softCloseActive
            ? 'Trading is currently soft-closed at resolve time. Enable override to continue.'
            : '';

    el.tradeGuard.textContent = tradeGuardMessage;
    el.tradeGuard.style.display = tradeGuardMessage ? '' : 'none';

    if (settlementMismatch) {
      el.claimBtn.disabled = true;
      // finalizeBtn must remain enabled during mismatch - it's the fix!
    }

    marketContextReady = true;
    await refreshClaimPreview(isMarketSettled);
    await refreshTradeBalances();
    await refreshTradeExecutionInsights();
    analyticsDirty = true;
    } finally {
      endPriceRefresh();
    }
  }

  async function approveCollateral(): Promise<void> {
    const amountRaw = getAmountInputRaw() || '100';
    const amount = parseUnits(amountRaw, currentTradeInputDecimals());
    const token = contractWithSigner(marketCollateralAddress, erc20AbiEthers);
    const tx = await token.approve(marketAddress, amount);
    logLine(el.logs, 'warn', `Approve sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', 'Collateral approved for market');
  }

  async function isCollateralApprovalNeeded(amount: bigint): Promise<boolean> {
    if (!walletState.account) throw new Error('Connect wallet first');
    const allowance = (await readClient.readContract({
      address: marketCollateralAddress as Address,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [walletState.account as Address, marketAddress],
    })) as bigint;
    return allowance < amount;
  }

  async function getResolutionBondAssetAddress(): Promise<Address> {
    try {
      return (await readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'bondAsset',
      })) as Address;
    } catch {
      return (await readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'bondToken',
      })) as Address;
    }
  }

  async function getResolutionBondConfig(): Promise<{ asset: Address; amount: bigint }> {
    const [asset, amount] = await Promise.all([
      getResolutionBondAssetAddress(),
      readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'bondAmount',
      }),
    ]);
    return { asset, amount: amount as bigint };
  }

  async function approveResolutionBond(): Promise<void> {
    const { asset, amount } = await getResolutionBondConfig();
    const tx = await contractWithSigner(asset as string, erc20AbiEthers).approve(DEPLOYMENT.adapter, amount);
    logLine(el.logs, 'warn', `Resolution bond approve sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', 'Resolution bond approved for adapter');
  }

  async function isResolutionBondApprovalNeeded(): Promise<boolean> {
    if (!walletState.account) throw new Error('Connect wallet first');
    const { asset, amount } = await getResolutionBondConfig();

    const allowance = (await readClient.readContract({
      address: asset,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [walletState.account as Address, DEPLOYMENT.adapter as Address],
    })) as bigint;

    return allowance < amount;
  }

  async function approveCourtStake(oracleAddress: Address): Promise<void> {
    if (!walletState.account) throw new Error('Connect wallet first');
    await refreshCourtStakeAssetMeta(oracleAddress);
    if (!courtStakeAssetAddress) throw new Error('Court stake asset unavailable');

    const tx = await contractWithSigner(courtStakeAssetAddress, erc20AbiEthers).approve(oracleAddress, MaxUint256);
    logLine(el.logs, 'warn', `Court stake approve sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', `${courtStakeAssetSymbol} approved for court staking`);
  }

  async function isCourtStakeApprovalNeeded(oracleAddress: Address, amount: bigint): Promise<boolean> {
    if (!walletState.account) throw new Error('Connect wallet first');
    await refreshCourtStakeAssetMeta(oracleAddress);
    if (!courtStakeAssetAddress) throw new Error('Court stake asset unavailable');

    const allowance = (await readClient.readContract({
      address: courtStakeAssetAddress,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [walletState.account as Address, oracleAddress],
    })) as bigint;

    return allowance < amount;
  }

  async function openJurorStakeTxModal(isStake: boolean): Promise<void> {
    const amountRaw = el.jurorStakeAmount.value.trim();
    if (!amountRaw) throw new Error('Enter stake amount');

    const oracleAddress = await getCourtOracleAddress();
    if (!oracleAddress) throw new Error('Court oracle unavailable');
    await refreshCourtStakeAssetMeta(oracleAddress);

    const amount = parseUnits(amountRaw, courtStakeAssetDecimals);
    if (amount <= 0n) throw new Error('Amount must be greater than zero');

    await runTxConfirmationModal({
      title: isStake ? `${courtStakeAssetSymbol} Stake Confirmation` : `${courtStakeAssetSymbol} Unstake Confirmation`,
      bodyLines: [
        `Action: ${isStake ? 'Stake' : 'Unstake'} ${courtStakeAssetSymbol}`,
        `Amount: ${amountRaw} ${courtStakeAssetSymbol}`,
        isStake
          ? 'This increases your juror stake and can make you eligible for panel selection.'
          : 'This withdraws unlocked stake from the court oracle.',
      ],
      submitLabel: isStake ? 'Submit Stake' : 'Submit Unstake',
      submitButtonClass: isStake ? 'btn-primary' : 'btn',
      submitAction: async () => {
        await jurorStake(isStake);
      },
      approval: isStake
        ? {
          label: `Approve ${courtStakeAssetSymbol}`,
          checkNeeded: async () => await isCourtStakeApprovalNeeded(oracleAddress, amount),
          approveAction: async () => {
            await approveCourtStake(oracleAddress);
            await refreshSafe();
          },
        }
        : undefined,
    });
  }

  async function openJurorVoteTxModal(kind: 'commit' | 'reveal'): Promise<void> {
    const outcomeLabel = outcomeText(selectedJurorOutcome());
    const secretEntered = Boolean(el.jurorSalt.value.trim());
    if (!secretEntered) throw new Error('Secret is required');

    await runTxConfirmationModal({
      title: kind === 'commit' ? 'Confirm Vote Commit Transaction' : 'Confirm Vote Reveal Transaction',
      bodyLines: kind === 'commit'
        ? [
          `Action: Commit vote for ${outcomeLabel}`,
          'This submits only your commitment hash now (outcome stays hidden until reveal).',
          `Secret: ${secretEntered ? 'provided' : 'missing'}`,
        ]
        : [
          `Action: Reveal vote as ${outcomeLabel}`,
          'This reveals your committed vote using the same secret entered during commit.',
          `Secret: ${secretEntered ? 'provided' : 'missing'}`,
        ],
      submitLabel: kind === 'commit' ? 'Submit Commit' : 'Submit Reveal',
      submitButtonClass: kind === 'commit' ? 'btn-amber' : 'btn-primary',
      submitAction: async () => {
        if (kind === 'commit') {
          await jurorCommitVote();
          return;
        }
        await jurorRevealVote();
      },
    });
  }

  async function openTradeTxModal(isBuy: boolean): Promise<void> {
    const amountRaw = getAmountInputRaw();
    if (!amountRaw) throw new Error('Enter amount');
    const amount = parseUnits(amountRaw, currentTradeInputDecimals());
    if (amount <= 0n) throw new Error('Amount must be greater than 0');
    const amountDisplay = formatNumericInputDisplay(amountRaw);
    const sideLabel = el.side.value === 'yes' ? yesOutcomeLabel : noOutcomeLabel;
    const isDefaultCollateral = marketCollateralAddress.toLowerCase() === DEPLOYMENT.collateral.toLowerCase();
    const amountLine = isDefaultCollateral
      ? `Amount: $${amountDisplay}`
      : `Amount: ${amountDisplay} ${collateralMeta.symbol}`;
    assertTradeSafetyChecks();

    await runTxConfirmationModal({
      title: isBuy ? 'Buy Confirmation' : 'Sell Confirmation',
      bodyLines: [
        `Action: ${isBuy ? 'Buy' : 'Sell'}`,
        `Side: ${sideLabel}`,
        amountLine,
        `Slippage: ${formatSlippageBps(tradeSlippageBps)}`,
        `Expert Mode: ${tradeExpertModeEnabled ? 'Enabled' : 'Disabled'}`,
      ],
      submitLabel: isBuy ? 'Submit Buy' : 'Submit Sell',
      submitButtonClass: isBuy ? 'btn-green' : 'btn-red',
      submitAction: async () => {
        await trade(isBuy);
      },
      approval: isBuy
        ? {
          label: `Approve ${collateralMeta.symbol}`,
          checkNeeded: async () => await isCollateralApprovalNeeded(amount),
          approveAction: async () => {
            await approveCollateral();
            await refreshTradeBalances();
          },
        }
        : undefined,
    });
  }

  async function openResolutionTxModal(kind: 'propose' | 'dispute'): Promise<void> {
    const outcomeLabel = outcomeText(kind === 'propose' ? selectedProposalOutcome() : selectedDisputeOutcome());

    await runTxConfirmationModal({
      title: kind === 'propose' ? 'Confirm Proposal Transaction' : 'Confirm Dispute Transaction',
      bodyLines: kind === 'propose'
        ? [
          `Action: Propose ${outcomeLabel}`,
          'This posts the proposer bond and opens the dispute window.',
        ]
        : [
          `Action: Dispute current proposal as ${outcomeLabel}`,
          'This posts the dispute bond and escalates this market into dispute processing.',
          `Some deployments require an explicit ${yesOutcomeLabel}/${noOutcomeLabel} dispute outcome.`,
        ],
      submitLabel: kind === 'propose' ? 'Submit Proposal' : 'Submit Dispute',
      submitButtonClass: kind === 'propose' ? 'btn-primary' : 'btn-red',
      submitAction: async () => {
        if (kind === 'propose') {
          await propose(true);
          return;
        }
        await dispute(true);
      },
      approval: {
        label: 'Approve Bond',
        checkNeeded: async () => await isResolutionBondApprovalNeeded(),
        approveAction: async () => {
          await approveResolutionBond();
          await refreshSafe();
        },
      },
    });
  }

  async function trade(isBuy: boolean): Promise<void> {
    const amountRaw = getAmountInputRaw();
    if (!amountRaw) throw new Error('Enter amount');
    const amount = parseUnits(amountRaw, currentTradeInputDecimals());
    if (amount <= 0n) throw new Error('Amount must be greater than 0');
    const amountDisplay = formatNumericInputDisplay(amountRaw);
    const market = contractWithSigner(marketAddress, marketAbiEthers);
    const yes = el.side.value === 'yes';

    if (!isBuy && walletState.account) {
      const tokenAddress = (await readClient.readContract({
        address: marketAddress,
        abi: marketAbi,
        functionName: yes ? 'yesToken' : 'noToken',
      })) as Address;
      const balance = (await readClient.readContract({
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [walletState.account as Address],
      })) as bigint;

      if (balance < amount) {
        const tokenLabel = yes ? yesOutcomeLabel : noOutcomeLabel;
        throw new Error(`Not enough ${tokenLabel} shares to sell. You have ${fmtToken(balance)} ${tokenLabel}, but tried to sell ${amountDisplay}.`);
      }
    }

    const tx = isBuy
      ? yes
        ? await market.buyYes(amount)
        : await market.buyNo(amount)
      : yes
        ? await market.sellYes(amount)
        : await market.sellNo(amount);

    logLine(el.logs, 'warn', `${isBuy ? 'Buy' : 'Sell'} sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', `${isBuy ? 'Buy' : 'Sell'} confirmed`);
    await refreshAfterMutation();
  }

  async function claimAndSettle(): Promise<void> {
    const market = contractWithSigner(marketAddress, marketAbiEthers);

    const resolvedRead = await readClient.readContract({
      address: marketAddress,
      abi: marketAbi,
      functionName: 'resolved',
    });

    if (!Boolean(resolvedRead)) {
      logLine(el.logs, 'warn', 'Market not settled yet. Settling first...');
      const settleTx = await market.settleMarket();
      logLine(el.logs, 'warn', `Settle sent: ${settleTx.hash}`);
      await settleTx.wait();
      logLine(el.logs, 'ok', 'Settle confirmed');
    }

    const claimTx = await market.claim();
    logLine(el.logs, 'warn', `Claim sent: ${claimTx.hash}`);
    await claimTx.wait();
    logLine(el.logs, 'ok', 'Claim confirmed');
    await refreshAfterMutation();
  }

  function adapter(): Contract {
    return contractWithSigner(DEPLOYMENT.adapter, adapterAbiEthers);
  }

  function selectedProposalOutcome(): number {
    return Number(el.resolutionOutcome.value || '1');
  }

  function selectedDisputeOutcome(): number {
    return Number(el.disputeOutcome.value || '1');
  }

  function hasLiveProposer(addr: string): boolean {
    return Boolean(addr && !/^0x0{40}$/i.test(addr));
  }

  /** Instantly swap selected-state CSS and aria-pressed on the static card nodes — no chain read. */
  function applyProposalCardSelection(picked: 1 | 2): void {
    const isYes = picked === 1;
    el.creatorPendingSlotYes.classList.toggle('creator-pending-slot-selected', isYes);
    el.creatorPendingSlotYes.setAttribute('aria-pressed', String(isYes));
    el.creatorPendingSlotNo.classList.toggle('creator-pending-slot-selected', !isYes);
    el.creatorPendingSlotNo.setAttribute('aria-pressed', String(!isYes));

    const selectedProposer = isYes ? cachedYesProposalProposer : cachedNoProposalProposer;
    const selectedReasonRaw = isYes ? cachedYesProposalReason : cachedNoProposalReason;
    el.creatorPendingOutcomePill.className = isYes ? 'meta-chip live' : 'meta-chip resolving';
    el.creatorPendingOutcomePill.textContent = outcomeText(picked);
    el.creatorPendingProposer.textContent = selectedProposer
      ? `by ${displayAddr(selectedProposer, shortAddr)}`
      : 'by unknown';

    const reason = selectedReasonRaw.trim();
    if (reason) {
      el.creatorPendingReason.textContent = `"${reason}"`;
      el.creatorPendingReason.hidden = false;
    } else if (cachedProposalReasonUnsupported) {
      el.creatorPendingReason.textContent = 'Reason is not emitted by this adapter/event set for this proposal path.';
      el.creatorPendingReason.hidden = false;
    } else {
      el.creatorPendingReason.hidden = true;
    }
  }

  function syncCreatorDecisionSelector(hasYesSlot: boolean, hasNoSlot: boolean): void {
    if (!hasYesSlot && creatorDecisionOutcome === 1) creatorDecisionOutcome = null;
    if (!hasNoSlot && creatorDecisionOutcome === 2) creatorDecisionOutcome = null;

    if (hasYesSlot && !hasNoSlot) creatorDecisionOutcome = 1;
    if (!hasYesSlot && hasNoSlot) creatorDecisionOutcome = 2;
    if (hasYesSlot && hasNoSlot && creatorDecisionOutcome === null) creatorDecisionOutcome = 1;
  }

  function selectedCreatorDecisionOutcome(): 1 | 2 {
    const hasYesSlot = hasLiveProposer(cachedYesProposalProposer);
    const hasNoSlot = hasLiveProposer(cachedNoProposalProposer);

    if (!hasYesSlot && !hasNoSlot) {
      throw new Error('No pending proposal to act on.');
    }
    if (hasYesSlot && !hasNoSlot) return 1;
    if (!hasYesSlot && hasNoSlot) return 2;

    if (creatorDecisionOutcome !== 1 && creatorDecisionOutcome !== 2) {
      throw new Error(`Select ${yesOutcomeLabel} or ${noOutcomeLabel} pending proposal before accept/decline.`);
    }
    return creatorDecisionOutcome;
  }

  function isCurrentWalletCreator(): boolean {
    return Boolean(
      walletState.account && creatorAddress && walletState.account.toLowerCase() === creatorAddress.toLowerCase(),
    )
  }

  async function propose(skipInlineConfirmation = false): Promise<void> {
    if (!currentMarketId) throw new Error('Missing marketId');
    if (!walletState.account) throw new Error('Connect wallet first.');

    const outcome = selectedProposalOutcome();
    if (outcome !== 1 && outcome !== 2) {
      throw new Error('Only outcome 1 or outcome 2 can be proposed. Invalid is not a valid proposal outcome.');
    }

    const [resolveAfterRead, resolutionRead, proposalsRead] = await Promise.all([
      readClient.readContract({
        address: marketAddress,
        abi: marketAbi,
        functionName: 'resolveAfter',
      }),
      readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'getResolution',
        args: [currentMarketId as `0x${string}`],
      }),
      readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'getProposals',
        args: [currentMarketId as `0x${string}`],
      }),
    ]);

    const nowTs = Math.floor(Date.now() / 1000);
    const resolveAfterTs = Number(resolveAfterRead || 0n);
    const resolution = resolutionRead as [boolean, number];
    const proposals = proposalsRead as [Address, boolean, Address, boolean, number];
    const finalized = Boolean(resolution?.[0]);
    const yesProposer = String(proposals?.[0] || '');
    const noProposer = String(proposals?.[2] || '');
    const selectedSlotFilled = outcome === 1
      ? Boolean(yesProposer && !/^0x0{40}$/i.test(yesProposer))
      : Boolean(noProposer && !/^0x0{40}$/i.test(noProposer));
    const isEarlyProposal = nowTs < resolveAfterTs;

    if (finalized) throw new Error('Resolution is already finalized.');
    if (selectedSlotFilled) throw new Error('A proposal already exists for that selected outcome.');

    if (!skipInlineConfirmation) {
      const confirmed = await confirmResolutionAction('Confirm Proposal', [
        `You are proposing outcome ${outcomeText(outcome)} for this market.`,
        isEarlyProposal
          ? 'This posts the proposer bond and creates a pending proposal for creator acceptance.'
          : 'This posts the proposer bond as an accepted outcome in resolve phase.',
        isEarlyProposal
          ? 'The creator must accept it to start the dispute/finalization timeline.'
          : 'Another wallet can dispute this accepted proposal if it disagrees.',
      ], 'Submit Proposal');
      if (!confirmed) throw new Error('Proposal cancelled.');
    }

    let tx;
    try {
      if (isEarlyProposal) {
        tx = await adapter().proposeResolutionPending(currentMarketId, outcome);
      } else {
        tx = await adapter().proposeResolution(currentMarketId, outcome);
      }
    } catch (marketError) {
      const code = String((marketError as any)?.code || '').toUpperCase();
      if (code === 'ACTION_REJECTED') {
        throw new Error('Proposal cancelled in wallet.');
      }

      logLine(el.logs, 'warn', 'Primary propose path failed; retrying adapter submitOutcome(bytes32,uint8).');

      try {
        tx = await adapter().submitOutcome(currentMarketId, outcome);
        console.info('[BoxBet] propose fallback used submitOutcome path', {
          marketId: currentMarketId,
          marketAddress,
          wallet: walletState.account,
          outcome,
        });
      } catch (fallbackError) {
        const fallbackCode = String((fallbackError as any)?.code || '').toUpperCase();
        if (fallbackCode === 'ACTION_REJECTED') {
          throw new Error('Proposal cancelled in wallet.');
        }

        logTxDebug('proposeResolution primary path', marketError, {
          marketAddress,
          marketId: currentMarketId,
          creatorAddress,
          wallet: walletState.account,
          outcome,
          isEarlyProposal,
        });
        logTxDebug('submitOutcome(bytes32,uint8) fallback', fallbackError, {
          marketAddress,
          marketId: currentMarketId,
          creatorAddress,
          wallet: walletState.account,
          outcome,
          isEarlyProposal,
        });
        throw fallbackError;
      }
    }
    logLine(el.logs, 'warn', `Propose sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', 'Proposed resolution');
    await refreshAfterMutation();
  }

  async function dispute(skipInlineConfirmation = false): Promise<void> {
    if (!currentMarketId) throw new Error('Missing marketId');
    if (!walletState.account) throw new Error('Connect wallet first.');

    const [resolutionRead, bondConfig] = await Promise.all([
      readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'getResolution',
        args: [currentMarketId as `0x${string}`],
      }),
      getResolutionBondConfig(),
    ]);

    const resolution = resolutionRead as [boolean, number];
    const finalized = Boolean(resolution?.[0]);

    if (finalized) throw new Error('Resolution is already finalized.');
    if (!walletState.account) throw new Error('Connect wallet first.');

    const allowance = (await readClient.readContract({
      address: bondConfig.asset,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [walletState.account as Address, DEPLOYMENT.adapter as Address],
    })) as bigint;

    if (allowance < bondConfig.amount) {
      throw new Error('Disputing needs adapter bond approval first. Complete approval in the transaction confirmation modal.');
    }

    if (!skipInlineConfirmation) {
      const confirmed = await confirmResolutionAction('Confirm Dispute', [
        'You are disputing the currently accepted proposal.',
        'Your dispute bond will be posted. If the final result agrees with the proposer, your reputation can be penalized and cooldown may apply.',
      ], 'Submit Dispute');
      if (!confirmed) throw new Error('Dispute cancelled.');
    }

    const disputeOutcome = selectedDisputeOutcome();

    try {
      const tx = await adapter().disputeResolution(currentMarketId);
      logLine(el.logs, 'warn', `Dispute sent: ${tx.hash}`);
      await tx.wait();
    } catch (marketError) {
      const code = String((marketError as any)?.code || '').toUpperCase();
      if (code === 'ACTION_REJECTED') {
        throw new Error('Dispute cancelled in wallet.');
      }

      if (disputeOutcome !== 1 && disputeOutcome !== 2) {
        throw new Error(`This deployment requires dispute outcome ${yesOutcomeLabel} or ${noOutcomeLabel}. Select one and retry dispute.`);
      }

      try {
        logLine(el.logs, 'warn', 'Primary dispute path failed; retrying adapter disputeAcceptedProposal(bytes32,uint8).');
        const tx = await adapter().disputeAcceptedProposal(currentMarketId, disputeOutcome);
        logLine(el.logs, 'warn', `Dispute sent via adapter outcome path: ${tx.hash}`);
        await tx.wait();
        console.info('[BoxBet] dispute fallback used disputeAcceptedProposal path', {
          marketId: currentMarketId,
          marketAddress,
          wallet: walletState.account,
          selectedDisputeOutcome: disputeOutcome,
        });
      } catch (fallbackError) {
        const fallbackCode = String((fallbackError as any)?.code || '').toUpperCase();
        if (fallbackCode === 'ACTION_REJECTED') {
          throw new Error('Dispute cancelled in wallet.');
        }

        logTxDebug('disputeResolution primary path', marketError, {
          marketAddress,
          marketId: currentMarketId,
          creatorAddress,
          wallet: walletState.account,
          selectedDisputeOutcome: disputeOutcome,
        });
        logTxDebug('disputeAcceptedProposal(bytes32,uint8) fallback', fallbackError, {
          marketAddress,
          marketId: currentMarketId,
          creatorAddress,
          wallet: walletState.account,
          selectedDisputeOutcome: disputeOutcome,
        });
        throw fallbackError;
      }
    }
    logLine(el.logs, 'ok', 'Disputed resolution');
    await refreshAfterMutation();
  }

  async function finalize(): Promise<void> {
    if (!currentMarketId) throw new Error('Missing marketId');

    const confirmed = await confirmResolutionAction('Confirm Finalization', [
      'Finalization will settle this market and lock payout state.',
      'This call settled on the market',
    ], 'Finalize Market');
    if (!confirmed) throw new Error('Finalize cancelled.');

    const market = contractWithSigner(marketAddress, marketAbiEthers);
    const tx = await market.settleMarket();
    logLine(el.logs, 'warn', `Finalize/Settle sent: ${tx.hash}`);
    await tx.wait();
    logLine(el.logs, 'ok', 'Market finalized and settled');
    await refreshAfterMutation();
  }

  async function emergency(): Promise<void> {
    throw new Error('Emergency ruling is not supported in the current Yesman deployment path.');
  }

  async function creatorAcceptPending(): Promise<void> {
    if (!walletState.account) throw new Error('Connect wallet first');
    if (!creatorAddress || walletState.account.toLowerCase() !== creatorAddress.toLowerCase()) {
      throw new Error('Only this market creator can accept proposals.');
    }

    const targetOutcome = selectedCreatorDecisionOutcome();

    const resolveAfterRead = await readClient.readContract({
      address: marketAddress,
      abi: marketAbi,
      functionName: 'resolveAfter',
    });
    const nowTs = Math.floor(Date.now() / 1000);
    if (nowTs >= Number(resolveAfterRead || 0n)) {
      throw new Error('Acceptance window elapsed. Creator can only accept early proposals before resolve time.');
    }

    const confirmed = await confirmResolutionAction('Accept Pending Proposal', [
      `Target proposal: ${outcomeText(targetOutcome)}`,
      'Accepting starts the normal dispute window for this proposal.',
      'Trading pauses on acceptance until final resolution.',
    ], 'Accept Proposal');
    if (!confirmed) throw new Error('Accept cancelled.');

    console.info('[BoxBet] acceptProposedResolution snapshot', {
      marketId: currentMarketId,
      marketAddress,
      creatorAddress,
      liquidityProviderAddress,
      targetOutcome,
      wallet: walletState.account,
    });

    try {
      const tx = await contractWithSigner(marketAddress, marketAbiEthers).acceptProposedResolution();
      logLine(el.logs, 'warn', `Accept proposal sent via market: ${tx.hash}`);
      await tx.wait();
    } catch (marketError) {
      logLine(el.logs, 'warn', 'Market accept path unavailable; using adapter accept path.');
      try {
        const adapterContract = contractWithSigner(DEPLOYMENT.adapter, adapterAbiEthers);
        let tx;
        try {
          tx = await adapterContract['acceptProposal(bytes32,uint8)'](
            currentMarketId as `0x${string}`,
            targetOutcome,
          );
        } catch {
          tx = await adapterContract['acceptProposal(bytes32)'](currentMarketId as `0x${string}`);
        }
        logLine(el.logs, 'warn', `Accept proposal sent via adapter: ${tx.hash}`);
        await tx.wait();
        console.info('[BoxBet] accept fallback used adapter path after market path rejection', {
          marketId: currentMarketId,
          marketAddress,
          targetOutcome,
          wallet: walletState.account,
        });
      } catch (fallbackError) {
        logTxDebug('acceptProposedResolution via market', marketError, {
          marketAddress,
          marketId: currentMarketId,
          creatorAddress,
          liquidityProviderAddress,
          targetOutcome,
          wallet: walletState.account,
        });
        logTxDebug('acceptProposal(bytes32,uint8) via adapter', fallbackError, {
          marketAddress,
          marketId: currentMarketId,
          creatorAddress,
          liquidityProviderAddress,
          targetOutcome,
          wallet: walletState.account,
        });
        throw fallbackError;
      }
    }
    logLine(el.logs, 'ok', 'Proposal accepted by creator');
    await refreshAfterMutation();
  }

  async function creatorRejectPending(): Promise<void> {
    if (!walletState.account) throw new Error('Connect wallet first');
    if (!creatorAddress || walletState.account.toLowerCase() !== creatorAddress.toLowerCase()) {
      throw new Error('Only this market creator can reject proposals.');
    }

    const targetOutcome = selectedCreatorDecisionOutcome();

    const confirmed = await confirmResolutionAction('Reject Pending Proposal', [
      `Target proposal: ${outcomeText(targetOutcome)}`,
      'Rejecting cancels the pending proposal.',
      'Proposer bond is refunded by the adapter.',
    ], 'Reject Proposal');
    if (!confirmed) throw new Error('Reject cancelled.');

    console.info('[BoxBet] rejectPendingProposal snapshot', {
      marketId: currentMarketId,
      marketAddress,
      creatorAddress,
      liquidityProviderAddress,
      targetOutcome,
      wallet: walletState.account,
    });
    logLine(el.logs, 'warn', `Rejecting pending proposal for market ${currentMarketId}`);
    try {
      const tx = await contractWithSigner(marketAddress, marketAbiEthers).rejectPendingProposal();
      logLine(el.logs, 'warn', `Reject proposal sent via market: ${tx.hash}`);
      await tx.wait();
    } catch (marketError) {

      const adapterContract = contractWithSigner(DEPLOYMENT.adapter, adapterAbiEthers);
      let pendingOutcome: number | null = targetOutcome;

      if (pendingOutcome !== 1 && pendingOutcome !== 2) {
        const pill = String(el.creatorPendingOutcomePill.textContent || '').toLowerCase();
        if (pill.includes('yes')) pendingOutcome = 1;
        if (pill.includes('no')) pendingOutcome = 2;
      }

      if (pendingOutcome !== 1 && pendingOutcome !== 2) {
        try {
          const pendingOutcomeRead = await readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getPendingOutcome',
            args: [currentMarketId as `0x${string}`],
          });
          const normalized = Number(pendingOutcomeRead || 0);
          if (normalized === 1 || normalized === 2) pendingOutcome = normalized;
        } catch {
          // Adapter variant may not expose getPendingOutcome.
        }
      }

      if (pendingOutcome !== 1 && pendingOutcome !== 2) {
        try {
          const proposals = (await readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getProposals',
            args: [currentMarketId as `0x${string}`],
          })) as [Address, boolean, Address, boolean, number];
          const yesProposer = String(proposals?.[0] || '');
          const noProposer = String(proposals?.[2] || '');
          const hasYes = yesProposer && !/^0x0{40}$/i.test(yesProposer);
          const hasNo = noProposer && !/^0x0{40}$/i.test(noProposer);
          if (hasYes && !hasNo) pendingOutcome = 1;
          if (!hasYes && hasNo) pendingOutcome = 2;
        } catch {
          // Some deployments may not expose getProposals.
        }
      }

      if (pendingOutcome === 1 || pendingOutcome === 2) {
        cachedProposalOutcome = pendingOutcome;
      }

      if (pendingOutcome === 1 || pendingOutcome === 2) {
        try {
          const tx = await adapterContract['declineProposal(bytes32,uint8)'](
            currentMarketId as `0x${string}`,
            pendingOutcome,
          );
          logLine(el.logs, 'warn', `Reject proposal sent via adapter outcome decline: ${tx.hash}`);
          await tx.wait();
          console.info('[BoxBet] reject fallback used adapter outcome path after market path rejection', {
            marketId: currentMarketId,
            marketAddress,
            wallet: walletState.account,
            pendingOutcome,
          });
        } catch (fallbackError) {
          if (String((fallbackError as any)?.code || '').toUpperCase() === 'ACTION_REJECTED') {
            throw new Error('Reject cancelled in wallet.');
          }
          logTxDebug('rejectPendingProposal via market', marketError, {
            marketAddress,
            marketId: currentMarketId,
            creatorAddress,
            liquidityProviderAddress,
            wallet: walletState.account,
          });
          logTxDebug('declineProposal(bytes32,uint8) via adapter', fallbackError, {
            marketAddress,
            marketId: currentMarketId,
            creatorAddress,
            liquidityProviderAddress,
            wallet: walletState.account,
            pendingOutcome,
          });
          throw fallbackError;
        }
      } else {
        try {
          const tx = await adapterContract['declineProposal(bytes32)'](currentMarketId as `0x${string}`);
          logLine(el.logs, 'warn', `Reject proposal sent via adapter simple decline: ${tx.hash}`);
          await tx.wait();
          console.info('[BoxBet] reject fallback used adapter simple path after market path rejection', {
            marketId: currentMarketId,
            marketAddress,
            wallet: walletState.account,
          });
        } catch (fallbackError) {
          if (String((fallbackError as any)?.code || '').toUpperCase() === 'ACTION_REJECTED') {
            throw new Error('Reject cancelled in wallet.');
          }
          logTxDebug('rejectPendingProposal via market', marketError, {
            marketAddress,
            marketId: currentMarketId,
            creatorAddress,
            liquidityProviderAddress,
            wallet: walletState.account,
          });
          logTxDebug('declineProposal(bytes32) via adapter', fallbackError, {
            marketAddress,
            marketId: currentMarketId,
            creatorAddress,
            liquidityProviderAddress,
            wallet: walletState.account,
            pendingOutcome,
          });
          throw fallbackError;
        }
      }
    }
    logLine(el.logs, 'ok', 'Pending proposal rejected by creator');
    await refreshAfterMutation();
  }

  async function creatorResolveDisputeDecision(finalOutcome: 1 | 2): Promise<void> {
    if (!walletState.account) throw new Error('Connect wallet first');
    if (!creatorAddress || walletState.account.toLowerCase() !== creatorAddress.toLowerCase()) {
      throw new Error('Only this market creator can resolve disputes.');
    }
    if (!currentMarketId) throw new Error('Missing marketId');

    const outcomeLabel = outcomeText(finalOutcome);
    const confirmed = await confirmResolutionAction('Resolve Dispute', [
      `You are selecting final outcome: ${outcomeLabel}`,
      'This records the creator dispute decision for the current disputed proposal.',
      'After this, finalize the market to complete settlement.',
    ], 'Submit Decision');
    if (!confirmed) throw new Error('Dispute resolution cancelled.');

    try {
      const tx = await adapter().creatorResolveDispute(currentMarketId, finalOutcome);
      logLine(el.logs, 'warn', `Creator dispute decision sent: ${tx.hash}`);
      await tx.wait();
      logLine(el.logs, 'ok', `Creator dispute decision confirmed: ${outcomeLabel}`);
    } catch (error: any) {
      const code = String(error?.code || '').toUpperCase();
      if (code === 'ACTION_REJECTED') {
        throw new Error('Dispute decision cancelled in wallet.');
      }
      throw error;
    }

    await refreshAfterMutation();
  }

  async function boot(): Promise<void> {
    setupHelpTipBehavior();
    const tabs = Array.from(appRoot.querySelectorAll('.tab')) as HTMLButtonElement[];
    const panels = Array.from(appRoot.querySelectorAll('.panel')) as HTMLElement[];
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    const advancedPanelIds = new Set(['resolutionPanel', 'creatorPanel']);
    const primaryPanelIds = new Set(['tradePanel', 'claimsPanel', 'analyticsPanel']);
    let lastPrimaryPanel = 'tradePanel';
    let lastAdvancedPanel = 'resolutionPanel';

    function setControlsMode(showAdvanced: boolean) {
      el.primaryTabsContainer.style.display = showAdvanced ? 'none' : '';
      el.advancedTabsContainer.style.display = showAdvanced ? '' : 'none';
      el.extraControlsToggle.classList.toggle('is-active', showAdvanced);
      el.extraControlsToggle.setAttribute('aria-pressed', showAdvanced ? 'true' : 'false');
      el.extraControlsToggle.title = showAdvanced ? 'Show trade tabs' : 'Show extra controls';
      el.extraControlsToggle.setAttribute('aria-label', showAdvanced ? 'Show trade tabs' : 'Show extra controls');
    }

    function activateTab(panelId: string | null, fromUser = false) {
      if (!panelId) return;
      if (panelId === 'courtPanel') panelId = 'resolutionPanel';
      if (panelId === 'claimsPanel' && el.claimsTabBtn.style.display === 'none') {
        panelId = 'tradePanel';
      }

      if (advancedPanelIds.has(panelId)) {
        lastAdvancedPanel = panelId;
        setControlsMode(true);
      } else if (primaryPanelIds.has(panelId)) {
        lastPrimaryPanel = panelId;
        setControlsMode(false);
      }

      const tab = tabs.find((t) => t.dataset.tab === panelId);
      const panel = panels.find((p) => p.id === panelId);
      if (!panel) return;
      tabs.forEach((t) => t.classList.remove('active'));
      panels.forEach((p) => p.classList.remove('active'));
      if (tab) tab.classList.add('active');
      panel.classList.add('active');

      if (panelId === 'analyticsPanel' && fromUser) {
        enableAnalyticsFetching(false);
      } else {
        disableAnalyticsFetching();
      }

      if (fromUser && (panelId === 'resolutionPanel' || panelId === 'creatorPanel' || panelId === 'courtPanel')) {
        void refreshSafe();
      }
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (tab.dataset.tab === 'analyticsPanel') {
          const next = new URL(window.location.href);
          next.search = `?info=${marketAddress}`;
          window.location.assign(next.toString());
          return;
        }
        if (tab.dataset.tab === 'claimsPanel' && el.claimsTabBtn.style.display === 'none') {
          activateTab('tradePanel', true);
          return;
        }
        activateTab(tab.dataset.tab || null, true);
      });
    });

    el.extraControlsToggle.addEventListener('click', () => {
      const advancedVisible = el.advancedTabsContainer.style.display !== 'none';
      if (advancedVisible) {
        const nextPrimary = lastPrimaryPanel === 'claimsPanel' && el.claimsTabBtn.style.display === 'none'
          ? 'tradePanel'
          : lastPrimaryPanel;
        activateTab(nextPrimary, true);
        return;
      }

      activateTab(lastAdvancedPanel, true);
    });

    if (requestedTab === 'analytics' || requestedTab === 'analyticsPanel') {
      const next = new URL(window.location.href);
      next.search = `?info=${marketAddress}`;
      window.location.replace(next.toString());
      return;
    }

    if (requestedTab) {
      activateTab(requestedTab);
    } else {
      setControlsMode(false);
    }

    el.walletBtn.addEventListener('click', async () => {
      if (walletState.account) {
        await openAccountModal({
          marketAddress,
          onWalletStateChange: async () => {
            updateWalletPill();
            await refreshTradeBalances();
          },
        });
        return;
      }

      try {
        await connectWallet();
        const chainOk = await ensurePulseChainConnected();
        updateWalletPill();
        if (!chainOk) {
          logLine(el.logs, 'warn', `Switch to ${getActiveChainConfig().chainName} to continue`);
          return;
        }
        logLine(el.logs, 'ok', `Wallet connected (${shortAddr(walletState.account || '')})`);
        await refreshSafe();
      } catch (error: any) {
        logLine(el.logs, 'err', error?.message || 'Wallet connect failed');
      }
    });

    el.tradeModeToggle.addEventListener('click', () => {
      setTradeMode(tradeMode === 'buy' ? 'sell' : 'buy');
    });

    el.buyUsdcCopyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(marketCollateralAddress);
        showCopyFeedback(el.buyUsdcCopyBtn, 'Copied');
      } catch {
        showCopyFeedback(el.buyUsdcCopyBtn, 'Copy failed');
      }
    });

    el.buyUsdcLinkBtn.addEventListener('click', () => {
      window.open(getBuyUsdcPulseXUrl(), '_blank', 'noopener,noreferrer');
    });

    el.marketAddrCopyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(marketAddress);
        showCopyFeedback(el.marketAddrCopyBtn, 'Copied');
      } catch {
        showCopyFeedback(el.marketAddrCopyBtn, 'Copy failed');
      }
    });

    el.marketLinkCopyBtn.addEventListener('click', async () => {
      const marketUrl = `${window.location.origin}/boxbet/?market=${marketAddress}`;
      try {
        await navigator.clipboard.writeText(marketUrl);
        showCopyFeedback(el.marketLinkCopyBtn, 'Copied');
      } catch {
        showCopyFeedback(el.marketLinkCopyBtn, 'Copy failed');
      }
    });

    el.tradeActionBtn.addEventListener('click', async () => {
      const isBuy = tradeMode === 'buy';
      try {
        if (warnIfTradeAmountExceedsBalance()) return;
        await openTradeTxModal(isBuy);
      } catch (error: any) {
        if (!isBuy && String(error?.message || '').toLowerCase().includes('not enough no shares to sell')) {
          logLine(el.logs, 'err', error.message);
          return;
        }
        logLine(el.logs, 'err', friendlyErrorMessage(error, isBuy ? 'Buy failed' : 'Sell failed'));
      }
    });

    el.claimBtn.addEventListener('click', async () => {
      await runTxAction(el.claimBtn, 'Claiming...', async () => {
        try {
          await claimAndSettle();
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Claim failed'));
        }
      });
    });

    el.proposeBtn.addEventListener('click', async () => {
      try {
        await openResolutionTxModal('propose');
      } catch (error: any) {
        logLine(el.logs, 'err', friendlyErrorMessage(error, 'Propose failed'));
      }
    });

    el.disputeBtn.addEventListener('click', async () => {
      try {
        await openResolutionTxModal('dispute');
      } catch (error: any) {
        logLine(el.logs, 'err', friendlyErrorMessage(error, 'Dispute failed'));
      }
    });

    el.resolutionOutcome.addEventListener('change', () => {
      if (el.disputeOutcome.value !== el.resolutionOutcome.value) {
        el.disputeOutcome.value = el.resolutionOutcome.value;
      }
      void refreshSafe();
    });

    el.disputeOutcome.addEventListener('change', () => {
      if (el.resolutionOutcome.value !== el.disputeOutcome.value) {
        el.resolutionOutcome.value = el.disputeOutcome.value;
      }
      void refreshSafe();
    });

    el.finalizeBtn.addEventListener('click', async () => {
      await runTxAction(el.finalizeBtn, 'Finalizing...', async () => {
        try {
          await finalize();
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Finalize failed'));
        }
      });
    });

    el.emergencyBtn.addEventListener('click', async () => {
      await runTxAction(el.emergencyBtn, 'Submitting...', async () => {
        try {
          await emergency();
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Emergency failed'));
        }
      });
    });

    el.creatorAcceptBtn.addEventListener('click', async () => {
      await runTxAction(el.creatorAcceptBtn, 'Accepting...', async () => {
        try {
          await creatorAcceptPending();
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Accept proposal failed'));
        }
      });
    });

    el.creatorRejectBtn.addEventListener('click', async () => {
      await runTxAction(el.creatorRejectBtn, 'Rejecting...', async () => {
        try {
          await creatorRejectPending();
        } catch (error: any) {
          const code = String(error?.code || '').toUpperCase();
          const message = String(error?.shortMessage || error?.message || '').toLowerCase();
          const userRejected = code === 'ACTION_REJECTED' || message.includes('user rejected') || message.includes('cancelled in wallet');
          if (userRejected) {
            logLine(el.logs, 'warn', 'Reject cancelled in wallet.');
            return;
          }
          logTxDebug('creatorRejectPending button handler', error, {
            marketAddress,
            marketId: currentMarketId,
            creatorAddress,
            wallet: walletState.account,
          });
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Reject proposal failed'));
        }
      });
    });

    el.creatorResolveKeepBtn.addEventListener('click', async () => {
      await runTxAction(el.creatorResolveKeepBtn, 'Submitting...', async () => {
        try {
          const keepOutcome = creatorDisputeAcceptedOutcome;
          if (keepOutcome !== 1 && keepOutcome !== 2) {
            throw new Error('Accepted outcome is unavailable for this dispute.');
          }
          await creatorResolveDisputeDecision(keepOutcome);
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Creator dispute decision failed'));
        }
      });
    });

    el.creatorResolveDisputedBtn.addEventListener('click', async () => {
      await runTxAction(el.creatorResolveDisputedBtn, 'Submitting...', async () => {
        try {
          const disputedOutcome = creatorDisputeOutcome
            ?? (creatorDisputeAcceptedOutcome === 1 ? 2 : creatorDisputeAcceptedOutcome === 2 ? 1 : null);
          if (disputedOutcome !== 1 && disputedOutcome !== 2) {
            throw new Error('Disputed outcome is unavailable for this dispute.');
          }
          await creatorResolveDisputeDecision(disputedOutcome);
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Creator dispute decision failed'));
        }
      });
    });

    for (const slotEl of [el.creatorPendingSlotYes, el.creatorPendingSlotNo]) {
      slotEl.addEventListener('click', () => {
        const picked = Number(slotEl.dataset.outcome) as 1 | 2;
        if (creatorDecisionOutcome === picked) return;
        creatorDecisionOutcome = picked;
        applyProposalCardSelection(picked);
      });
      slotEl.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const picked = Number(slotEl.dataset.outcome) as 1 | 2;
        if (creatorDecisionOutcome === picked) return;
        creatorDecisionOutcome = picked;
        applyProposalCardSelection(picked);
      });
    }

    el.jurorStakeBtn.addEventListener('click', async () => {
      try {
        await openJurorStakeTxModal(true);
      } catch (error: any) {
        logLine(el.logs, 'err', friendlyErrorMessage(error, 'Stake failed'));
      }
    });

    el.jurorUnstakeBtn.addEventListener('click', async () => {
      try {
        await openJurorStakeTxModal(false);
      } catch (error: any) {
        logLine(el.logs, 'err', friendlyErrorMessage(error, 'Unstake failed'));
      }
    });

    el.jurorWithdrawBtn.addEventListener('click', async () => {
      await runTxAction(el.jurorWithdrawBtn, 'Withdrawing...', async () => {
        try {
          await jurorWithdrawRewards();
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Withdraw rewards failed'));
        }
      });
    });

    el.jurorCommitBtn.addEventListener('click', async () => {
      try {
        await openJurorVoteTxModal('commit');
      } catch (error: any) {
        logLine(el.logs, 'err', friendlyErrorMessage(error, 'Commit vote failed'));
      }
    });

    el.jurorRevealBtn.addEventListener('click', async () => {
      try {
        await openJurorVoteTxModal('reveal');
      } catch (error: any) {
        logLine(el.logs, 'err', friendlyErrorMessage(error, 'Reveal vote failed'));
      }
    });

    el.jurorAppealBtn.addEventListener('click', async () => {
      await runTxAction(el.jurorAppealBtn, 'Appealing...', async () => {
        try {
          await jurorAppeal();
        } catch (error: any) {
          logLine(el.logs, 'err', friendlyErrorMessage(error, 'Appeal failed'));
        }
      });
    });

    const updateCommitPreview = async () => {
      if (!walletState.account || !currentMarketId) {
        el.jurorCommitPreview.textContent = 'Commitment preview: connect wallet';
        return;
      }
      try {
        const oracleAddress = await getCourtOracleAddress();
        if (!oracleAddress) {
          el.jurorCommitPreview.textContent = 'Commitment preview: oracle unavailable';
          return;
        }
        const roundIndex = (await readClient.readContract({
          address: oracleAddress,
          abi: courtOracleAbi,
          functionName: 'getCurrentRound',
          args: [currentMarketId as `0x${string}`],
        })) as bigint;
        const salt = normalizeSalt(el.jurorSalt.value || 'boxbet-default-salt');
        const commitment = computeJurorCommitment(currentMarketId, roundIndex, walletState.account as Address, selectedJurorOutcome(), salt);
        el.jurorCommitPreview.textContent = `Commitment preview: ${commitment}`;
      } catch (error: any) {
        const selector = extractCustomErrorSelector(error);
        if (selector === '0x0d34f119') {
          el.jurorCommitPreview.textContent = 'Commitment preview: waiting for court case to open';
          return;
        }
        el.jurorCommitPreview.textContent = 'Commitment preview: n/a';
      }
    };

    const ensureUsableSalt = () => {
      const stored = loadJurorSaltFromSession();
      if (stored) {
        el.jurorSalt.value = stored;
        return;
      }

      if (!el.jurorSalt.value.trim()) {
        el.jurorSalt.value = generateRandomSalt();
      }

      try {
        saveJurorSaltInSession();
      } catch {
        // no-op
      }
    };

    el.jurorSaltGenerateBtn.addEventListener('click', () => {
      el.jurorSalt.value = generateRandomSalt();
      try {
        saveJurorSaltInSession();
        logLine(el.logs, 'ok', 'Generated and saved new Secret in session storage');
      } catch {
        logLine(el.logs, 'warn', 'Generated Secret (session storage unavailable)');
      }
      void updateCommitPreview();
    });

    el.jurorSaltSaveBtn.addEventListener('click', async () => {
      try {
        const value = el.jurorSalt.value.trim();
        if (!value) throw new Error('Secret is empty');
        saveJurorSaltInSession();
        await downloadJurorSecretBackup(value);
        logLine(el.logs, 'ok', 'Secret saved in session storage and encrypted backup downloaded');
      } catch (error: any) {
        if ((error?.message || '').toLowerCase().includes('cancelled')) {
          logLine(el.logs, 'warn', 'Secret saved in session storage. Encrypted backup download cancelled');
          return;
        }
        logLine(el.logs, 'err', error?.message || 'Failed to save/download encrypted Secret backup');
      }
    });

    el.jurorSaltRestoreBtn.addEventListener('click', async () => {
      try {
        const text = await chooseBackupFileText();
        if (!text) {
          logLine(el.logs, 'warn', 'Backup restore cancelled');
          return;
        }
        let payload: any;
        try {
          payload = JSON.parse(text);
        } catch {
          throw new Error('Backup file is not valid JSON');
        }
        if (payload?.type !== 'boxbet-juror-secret-backup') {
          throw new Error('Selected file is not a BoxBet secret backup');
        }
        if (payload?.format !== 'encrypted') {
          throw new Error('Backup file must be encrypted. Plaintext backups are not supported for restore');
        }

        const passphrase = await promptBackupUnlockPassphrase();
        if (!passphrase) {
          logLine(el.logs, 'warn', 'Backup decrypt cancelled');
          return;
        }

        const restored = await decryptSecretFromBackupPayload(payload, passphrase);
        if (!restored) throw new Error('Decrypted Secret is empty');
        el.jurorSalt.value = restored;
        saveJurorSaltInSession();
        await updateCommitPreview();
        logLine(el.logs, 'ok', 'Secret restored from encrypted backup and saved in session storage');
      } catch (error: any) {
        logLine(el.logs, 'err', error?.message || 'Failed to restore Secret from backup');
      }
    });

    el.jurorSaltCopyBtn.addEventListener('click', async () => {
      const value = el.jurorSalt.value.trim();
      if (!value) {
        logLine(el.logs, 'warn', 'Secret is empty, nothing copied');
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        logLine(el.logs, 'ok', 'Secret copied to clipboard');
      } catch {
        logLine(el.logs, 'err', 'Clipboard copy failed');
      }
    });

    el.jurorSalt.addEventListener('input', () => {
      void updateCommitPreview();
    });
    el.jurorOutcome.addEventListener('change', () => {
      void updateCommitPreview();
    });

    el.amount.value = '';
    el.tradeOverride.checked = false;
    closeTradeSettingsModal();
    setTradeMode('buy');
    el.jurorStakeAmount.value = '1000';
    ensureUsableSalt();
    el.tradeOverride.addEventListener('change', async () => {
      await refreshSafe();
    });
    el.amount.addEventListener('input', () => {
      const normalized = normalizeNumericInputValue(el.amount.value);
      const display = formatNumericInputDisplay(normalized);
      if (display !== el.amount.value) {
        el.amount.value = display;
      }
      scheduleTradeExecutionInsightsRefresh();
    });
    el.tradeSettingsBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      openTradeSettingsModal();
    });
    el.tradeRefreshBtn.addEventListener('click', async () => {
      el.tradeRefreshBtn.classList.remove('is-spinning');
      void el.tradeRefreshBtn.offsetWidth;
      el.tradeRefreshBtn.classList.add('is-spinning');
      try {
        await refreshSafe();
      } catch (error: any) {
        logLine(el.logs, 'err', error?.message || 'Refresh failed');
      }
    });
    el.tradeRefreshBtn.addEventListener('animationend', (event) => {
      if (event.animationName !== 'refresh-spin-once') return;
      el.tradeRefreshBtn.classList.remove('is-spinning');
    });
    el.tradeSettingsCloseBtn.addEventListener('click', () => {
      closeTradeSettingsModal();
    });
    el.tradeSlippageCustomInput.addEventListener('input', () => {
      applyTradeSettingsIfValid();
    });
    el.tradeSlippageCustomInput.addEventListener('change', () => {
      try {
        applyTradeSettingsFromModal();
      } catch (error: any) {
        logLine(el.logs, 'err', error?.message || 'Unable to apply trade settings');
      }
    });
    el.tradeExpertModeToggle.addEventListener('change', () => {
      applyTradeSettingsIfValid();
    });
    el.tradeSlippagePresets.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest('[data-bps]') as HTMLButtonElement | null;
      if (!target) return;
      const bps = Number(target.dataset.bps || 0);
      if (!Number.isFinite(bps) || bps <= 0) return;
      tradeSlippageBps = bps;
      el.tradeSlippageCustomInput.value = (bps / 100).toFixed(2);
      tradeExpertModeEnabled = el.tradeExpertModeToggle.checked;
      scheduleTradeExecutionInsightsRefresh(true);
      setActiveSlippagePresetButton();
    });
    el.sideDropdownBtn.addEventListener('click', () => {
      if (tradeMode === 'sell') return;
      setSideDropdownOpen(!el.sideDropdown.classList.contains('is-open'));
    });
    el.payPercentDropdownBtn.addEventListener('click', () => {
      setPayPercentDropdownOpen(!el.payPercentDropdown.classList.contains('is-open'));
    });
    el.paySideDropdownBtn.addEventListener('click', () => {
      if (tradeMode === 'buy') return;
      setPaySideDropdownOpen(!el.paySideDropdown.classList.contains('is-open'));
    });
    el.sideOptionYesBtn.addEventListener('click', () => {
      el.side.value = 'yes';
      setSideDropdownOpen(false);
      el.side.dispatchEvent(new Event('change'));
    });
    el.sideOptionNoBtn.addEventListener('click', () => {
      el.side.value = 'no';
      setSideDropdownOpen(false);
      el.side.dispatchEvent(new Event('change'));
    });
    el.paySideOptionYesBtn.addEventListener('click', () => {
      el.side.value = 'yes';
      setPaySideDropdownOpen(false);
      el.side.dispatchEvent(new Event('change'));
    });
    el.paySideOptionNoBtn.addEventListener('click', () => {
      el.side.value = 'no';
      setPaySideDropdownOpen(false);
      el.side.dispatchEvent(new Event('change'));
    });
    const onPayPercentPick = (value: string): void => {
      el.payPercentSelect.value = value;
      syncPayPercentOptionsUi();
      setPayPercentDropdownOpen(false);
      applySelectedPercentToInput();
      scheduleTradeExecutionInsightsRefresh(true);
    };
    el.payPercent25Btn.addEventListener('click', () => onPayPercentPick('25'));
    el.payPercent50Btn.addEventListener('click', () => onPayPercentPick('50'));
    el.payPercent75Btn.addEventListener('click', () => onPayPercentPick('75'));
    el.payPercent100Btn.addEventListener('click', () => onPayPercentPick('100'));
    document.addEventListener('click', (event) => {
      const target = event.target as Node;
      if (!el.sideDropdown.contains(target)) {
        setSideDropdownOpen(false);
      }
      if (!el.paySideDropdown.contains(target)) {
        setPaySideDropdownOpen(false);
      }
      if (!el.payPercentDropdown.contains(target)) {
        setPayPercentDropdownOpen(false);
      }
      if (!el.tradeSettingsModal.hidden) {
        const clickedSettingsButton = (event.target as HTMLElement).closest('#tradeSettingsBtn');
        if (clickedSettingsButton) {
          return;
        }
        const clickedInsideSheet = (event.target as HTMLElement).closest('.trade-settings-sheet');
        if (!clickedInsideSheet) {
          closeTradeSettingsModal();
        }
      }
    });
    el.tradeImpactTrigger.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const toggleBtn = target.closest('[data-role="impact-toggle"]') as HTMLButtonElement | null;
      if (!toggleBtn) return;
      tradeImpactExpanded = !tradeImpactExpanded;
      scheduleTradeExecutionInsightsRefresh(true);
    });
    el.side.addEventListener('change', () => {
      syncSideOptionLabels();
      if (marketContextReady) {
        void refreshTradeBalances();
      }
      scheduleTradeExecutionInsightsRefresh(true);
    });
    el.payPercentSelect.addEventListener('change', () => {
      applySelectedPercentToInput();
      syncPayPercentOptionsUi();
      scheduleTradeExecutionInsightsRefresh(true);
    });
    el.analyticsRangeShort.addEventListener('click', () => {
      if (!isPanelActive('analyticsPanel')) {
        analyticsDirty = true;
        return;
      }
      enableAnalyticsFetching(false);
      analyticsRange = '12h';
      if (isPanelActive('analyticsPanel')) {
        void ensureAnalyticsLoaded(true);
      } else {
        analyticsDirty = true;
      }
    });
    el.analyticsRangeMedium.addEventListener('click', () => {
      if (!isPanelActive('analyticsPanel')) {
        analyticsDirty = true;
        return;
      }
      enableAnalyticsFetching(false);
      analyticsRange = '24h';
      if (isPanelActive('analyticsPanel')) {
        void ensureAnalyticsLoaded(true);
      } else {
        analyticsDirty = true;
      }
    });
    el.analyticsRangeFull.addEventListener('click', () => {
      if (!isPanelActive('analyticsPanel')) {
        analyticsDirty = true;
        return;
      }
      enableAnalyticsFetching(false);
      analyticsRange = '7d';
      if (isPanelActive('analyticsPanel')) {
        void ensureAnalyticsLoaded(true);
      } else {
        analyticsDirty = true;
      }
    });
    el.analyticsRefreshBtn.addEventListener('click', () => {
      if (!isPanelActive('analyticsPanel')) {
        analyticsDirty = true;
        return;
      }
      enableAnalyticsFetching(false);
      el.analyticsRefreshBtn.classList.remove('is-spinning');
      void el.analyticsRefreshBtn.offsetWidth;
      el.analyticsRefreshBtn.classList.add('is-spinning');
      void ensureAnalyticsLoaded(true);
    });
    el.analyticsRefreshBtn.addEventListener('animationend', (event) => {
      if (event.animationName !== 'refresh-spin-once') return;
      el.analyticsRefreshBtn.classList.remove('is-spinning');
    });

    bindWalletEvents(async () => {
      ensureUsableSalt();
      updateWalletPill();
      if (walletState.account) {
        await ensurePulseChainConnected();
      }
      await refreshSafe();
      scheduleCoreAutoRefresh();
    });

    try {
      await rehydrateWalletSession();
    } catch {
      // no-op
    }

    updateWalletPill();
    if (walletState.account) {
      void ensurePulseChainConnected();
    }
    // Start from neutral icon so custom markets do not flash USDC before market collateral loads.
    collateralLogoUrl = `${import.meta.env.BASE_URL}images/coin.svg`;
    document.documentElement.style.setProperty('--collateral-logo-url', `url('${collateralLogoUrl}')`);
    // Seed from cached per-market collateral if available; refresh() will reconcile with chain.
    const cachedCollateral = readCachedMarketCollateral();
    if (cachedCollateral) {
      marketCollateralAddress = cachedCollateral;
      try {
        collateralMeta = applyCustomCollateralDecimalPolicy(await resolveCollateralMeta(cachedCollateral), cachedCollateral);
      } catch {
        collateralMeta = applyCustomCollateralDecimalPolicy(defaultCollateralMeta(cachedCollateral), cachedCollateral);
      }
    }
    await refreshSafe();

    document.addEventListener('visibilitychange', handleVisibilityOrFocusChange);
    window.addEventListener('focus', handleVisibilityOrFocusChange);
    window.addEventListener('blur', handleVisibilityOrFocusChange);
    window.addEventListener('beforeunload', clearAutoRefreshTimers);

    scheduleCoreAutoRefresh(getMarketRefreshIntervalMs());
  }

  await boot();
}
